import { v4 } from 'uuid';
/**
 * Entities are stored under their id in the database.
 * Dependants only exist as references from fields or arrays,
 * the ids exist mostly for UI convenience.
 */
export default {
    $schema: 'https://json-schema.org/draft/2020-12/schema',
    $id: 'https://www.microcasting.com/schemas/microcasting.schema.json',
    title: 'Microcasting',
    description: 'The Microcasting Domain Schema for E-Learning Everywhere!',
    type: 'schema',
    properties: {
        accessGroupDefinition: {
            name: 'Access Group Definition',
            label: 'Access Group Definition',
            description: 'Mechanism for defining groups of resourses and access to those resources.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                code: {
                    label: 'Code',
                    type: 'string',
                    minLength: 3,
                    maxLength: 12,
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                },
                active: {
                    label: 'Active',
                    type: 'boolean',
                    default: true,
                },
                canDelete: {
                    label: 'Can Delete',
                    type: 'boolean',
                    default: true,
                },
                description: {
                    label: 'Description',
                    type: 'string',
                    maxLength: 500,
                },
            },
            required: ['id', 'code'],
        },
        aiConfiguration: {
            name: 'AI Configuration',
            label: 'AI Configuration',
            description: 'Client AI settings with basic properties for flexible configuration. Customize name, type, and value as needed.',
            type: 'dependant',
            properties: {
                languageModel: {
                    label: 'Language Model Override',
                    description: 'ID of the language model to use in generating responses.',
                    type: 'string',
                    default: 'default',
                    enum: ['default', 'us.anthropic.claude-3-5-haiku-20241022-v1:0', 'us.anthropic.claude-3-5-sonnet-20241022-v2:0', 'us.anthropic.claude-3-7-sonnet-20250219-v1:0', 'global.anthropic.claude-haiku-4-5-20251001-v1:0', 'anthropic/claude-3-5-sonnet-20241022', 'anthropic/claude-3-5-haiku-20241022', 'anthropic/claude-3-opus-20240229', 'anthropic/claude-3-7-sonnet-20250219', 'anthropic/claude-haiku-4-5-20251001', 'openai.gpt-4o-mini', 'openai.gpt-4.1-2025-04-14', 'openai.gpt-5-mini', 'openai.gpt-5-nano', 'google.gemini-2.5-flash', 'google.gemini-2.5-flash-lite', 'google.gemini-2.5-pro', 'google.gemini-3-flash', 'google.gemini-3-pro', 'moonshotai.kimi-k2.5', 'mistral.voxtral-small-24b-2507'],
                    enumLabels: ['Haiku 4.5 Direct (System Default)', 'Anthropic Claude Haiku 3.5 (Bedrock)', 'Anthropic Claude Sonnet 3.5 (Bedrock)', 'Anthropic Claude Sonnet 3.7 (Bedrock)', 'Anthropic Claude Haiku 4.5 (Bedrock)', 'Anthropic Claude Sonnet 3.5 (Direct)', 'Anthropic Claude Haiku 3.5 (Direct)', 'Anthropic Claude Opus 3 (Direct)', 'Anthropic Claude Sonnet 3.7 (Direct)', 'Anthropic Claude Haiku 4.5 (Direct)', 'OpenAI GPT-4o Mini', 'OpenAI GPT-4.1', 'OpenAI GPT-5 Mini', 'OpenAI GPT-5 Nano', 'Google Gemini 2.5 Flash', 'Google Gemini 2.5 Flash Lite', 'Google Gemini 2.5 Pro', 'Google Gemini 3 Flash', 'Google Gemini 3 Pro', 'Moonshot Kimi 2.5 (Bedrock)', 'Mistral Voxtral Small 1.0 (Bedrock)'],
                },
                temperature: {
                    label: 'Temperature',
                    description: 'Sampling temperature for model responses (0 = deterministic, higher = more varied).',
                    type: 'number',
                    default: 0.1,
                    minValue: 0,
                    maxValue: 2,
                },
                filterMode: {
                    label: 'Knowledge Base Filter Mode',
                    description: 'Determines if documents are filtered by organization or by libraries.',
                    type: 'string',
                    default: 'organization',
                    enum: ['organization', 'library', 'none'],
                    enumLabels: ['By Organization', 'By Libraries', 'None'],
                },
                knowledgeBaseToolEnabled: {
                    label: 'Knowledge Base Tool Enabled',
                    description: 'Search knowledge base for information regarding users questions.',
                    type: 'boolean',
                    default: true,
                },
                maxResults: {
                    label: 'Max Results',
                    description: 'Maximum number of results to return from the knowledge base prior to filtering.',
                    type: 'number',
                    default: 8,
                    minValue: 1,
                    maxValue: 100,
                    portalApi: false,
                },
                useProductType: {
                    label: 'Use Product Type',
                    description: 'Use the product type to filter the knowledge base results.',
                    type: 'boolean',
                    default: false,
                },
                useFullProductSheet: {
                    label: 'Use Full Product Sheet',
                    description: 'Fetch full product sheet content from S3 instead of using the default content from the knowledge base.',
                    type: 'boolean',
                    default: false,
                },
                useHybridSearch: {
                    label: 'Use Hybrid Search',
                    description: 'Combine vector similarity with full-text search (ts_rank) using Reciprocal Rank Fusion. Improves retrieval for keyword-heavy queries. Default true.',
                    type: 'boolean',
                    default: true,
                },
                postFilter: {
                    label: 'PG vector: HNSW (approximate) search',
                    description: 'When true, pgvector queries use ORDER BY embedding with the HNSW index (filter during index scan / iterative scan) instead of pre-filtering all rows and exact brute-force ranking. Faster for very large catalogs; ranking may differ from exact kNN. Default false.',
                    type: 'boolean',
                    default: false,
                },
                openSupportToolEnabled: {
                    label: 'Open Support Tool Enabled',
                    description: 'Enable the Open Support Tool.',
                    type: 'boolean',
                    default: false,
                },
                orderStatusToolEnabled: {
                    label: 'Order Status Tool Enabled',
                    description: 'Enable the Order Status Tool for Shopify order queries.',
                    type: 'boolean',
                    default: false,
                },
                orderStatusToolPrompt: {
                    label: 'Order Status Tool System Prompt Instructions',
                    description: 'Instructions to guide the AI when handling order-related queries. The supportMessage variable is available for conditional support chat references.',
                    type: 'string',
                    multiline: true,
                    rows: 10,
                    default: '',
                    defaultForEmptyMatch: true,
                    defaultKey: 'orderStatus',
                },
                orderStatusToolInstructions: {
                    label: 'Order Status Tool Additional Instructions',
                    description: 'Instructions appended after the base. Leave empty to append nothing.',
                    type: 'string',
                    multiline: true,
                    rows: 6,
                    default: '',
                    promptKey: 'orderStatus',
                },
                exitToolEnabled: {
                    label: 'Exit Tool Enabled',
                    description: 'Enable the Exit Conversation Tool to allow users to end the conversation.',
                    type: 'boolean',
                    default: true,
                },
                exitToolPrompt: {
                    label: 'Exit Tool System Prompt Instructions',
                    description: 'Instructions to guide the AI when handling conversation exit requests.',
                    type: 'string',
                    multiline: true,
                    rows: 4,
                    default: '',
                    defaultForEmptyMatch: true,
                    promptKey: 'exitTool',
                },
                exitToolInstructions: {
                    label: 'Exit Tool Additional Instructions',
                    description: 'Additional instructions to include with Exit Tool.',
                    type: 'string',
                    multiline: true,
                    rows: 4,
                    default: '',
                    promptKey: 'exitTool',
                },
                featureCaptureToolEnabled: {
                    label: 'Feature Capture Tool Enabled',
                    description: 'Enable the Feature Capture Tool to collect user-provided information (e.g. email, product interest) for analytics during conversation.',
                    type: 'boolean',
                    default: false,
                },
                featureCaptureConcepts: {
                    label: 'Feature Capture Concepts',
                    description: 'Comma-separated concepts to capture from users (e.g. email, company, productInterest, intent). Leave empty for default.',
                    type: 'string',
                    default: 'email, company, productInterest, intent',
                },
                featureCaptureToolInstructions: {
                    label: 'Feature Capture Tool Additional Instructions',
                    description: 'Additional instructions for the Feature Capture Tool. Appended after the base prompt.',
                    type: 'string',
                    multiline: true,
                    rows: 6,
                    default: '',
                    promptKey: 'featureCapture',
                },
                followUpRequestToolEnabled: {
                    label: 'Follow-up Request Tool Enabled',
                    description: 'When enabled, the assistant may call requestFollowUp after the customer asks to be contacted. Requires a valid CS notification email.',
                    type: 'boolean',
                    default: false,
                },
                followUpNotificationEmail: {
                    label: 'Follow-up CS Notification Email',
                    description: 'Inbox that receives plain-text follow-up alerts when a customer uses the follow-up tool. Required when the tool is enabled.',
                    type: 'string',
                    default: '',
                },
                followUpRequestToolInstructions: {
                    label: 'Follow-up Request Tool Additional Instructions',
                    description: 'Optional merchant-specific instructions appended after the built-in follow-up behavior (strategy A: no separate base prompt in admin).',
                    type: 'string',
                    multiline: true,
                    rows: 6,
                    default: '',
                },
                openSupportToolDelay: {
                    label: 'Open Support Tool Delay',
                    description: 'Delay (in milliseconds) before the conversation window is closed and the chat window openned.',
                    type: 'number',
                    minValue: 0,
                    maxValue: 10000,
                    default: 6000,
                },
                openSupportToolMessage: {
                    label: 'Open Support Tool Message',
                    description: 'Message to display in the conversation window before the chat window is openned.',
                    type: 'string',
                    default: null,
                },
                openSupportToolStatus: {
                    label: 'Open Support Status',
                    description: 'Status of the open support tool.',
                    type: 'string',
                    default: 'closed',
                    enum: ['closed', 'open', 'calendar'],
                    enumLabels: ['Closed', 'Open', 'Use Calendar'],
                },
                openSupportToolOpenHours: {
                    label: 'Open Support Open Hours',
                    description: 'Hours that the support team is available to chat.',
                    type: 'string',
                    default: 'Mo-Fr 08:00-17:00',
                },
                openSupportToolTimezone: {
                    label: 'Open Support Timezone',
                    description: 'Timezone for the open support tool.',
                    type: 'string',
                    default: 'America/San_Francisco',
                    enum: [
                        'America/New_York',
                        'America/Chicago',
                        'America/Denver',
                        'America/Los_Angeles',
                    ],
                    enumLabels: [
                        'America/New_York',
                        'America/Chicago',
                        'America/Denver',
                        'America/Los_Angeles',
                    ],
                },
                openSupportToolOffHoursMessage: {
                    label: 'Open Support Off Hours Message',
                    description: 'Message to display in the conversation window when the support team is not available to chat.',
                    type: 'string',
                    default: null,
                },
                openSupportToolOpenHoursPrompt: {
                    label: 'Open Support Chat Open Hours System Prompt',
                    description: 'Text added to the system prompt when the support team is available to chat.',
                    type: 'string',
                    multiline: true,
                    rows: 4,
                    default: '',
                },
                openSupportToolOffHoursPrompt: {
                    label: 'Open Support Off Hours System Prompt',
                    description: 'Text added to the system prompt when the support team is not available to chat.',
                    type: 'string',
                    multiline: true,
                    rows: 4,
                    default: '',
                },
                customerContextToolEnabled: {
                    label: 'Customer Context Tool Enabled',
                    description: 'Enable the customer context tool that provides information about the page the customer is currently on.',
                    type: 'boolean',
                    default: true,
                },
                productFeedID: {
                    label: 'Product Feed ID',
                    description: 'Feed to use when to lookup product information for customer context tool.',
                    type: 'string',
                    default: false,
                },
                productFeedOrganizationID: {
                    label: 'Product Feed Organization ID',
                    description: 'Organization that owns the product feed (ShopifyConfig). Use when the feed is in a different org than the portal. If unset, uses the portal organization.',
                    type: 'string',
                    default: null,
                },
                productPageSuggestedPromptsEnabled: {
                    label: 'Product Page Suggested Prompts Enabled',
                    description: 'Enable AI-suggested prompts for product pages.',
                    type: 'boolean',
                    default: false,
                },
                categoryPageSuggestedPromptsEnabled: {
                    label: 'Category Page Suggested Prompts Enabled',
                    description: 'Enable AI-suggested prompts for category/collection pages.',
                    type: 'boolean',
                    default: false,
                },
                pageSuggestedPromptsMaxCount: {
                    label: 'Max Suggested Prompts Count',
                    description: 'Maximum number of page-specific prompts to generate.',
                    type: 'number',
                    default: 3,
                    minValue: 1,
                    maxValue: 10,
                },
                pageSuggestedPromptsRefreshDays: {
                    label: 'Suggested Prompts Refresh Interval (days)',
                    description: 'Number of days before refreshing the suggested prompts cache.',
                    type: 'number',
                    default: 7,
                    minValue: 1,
                    maxValue: 30,
                },
                pageGeneratedContentHeader: {
                    label: 'Page-generated content header',
                    description: 'Shared header styling (tag, className, fontSize, fontWeight, color, alignment, margin) for Suggested Prompts and Smart Summary blocks.',
                    type: 'json',
                    default: {},
                },
                pageSuggestedPromptsStyle: {
                    label: 'Suggested Prompts Styles',
                    description: 'JSON: layout, button, container, input; optional promptPrefixIconAssetID (JPEG, PNG, SVG, or WebP before each prompt), optional promptPrefixIconSize (square icon size in px — same width and height; defaults to 16).',
                    type: 'json',
                    default: {},
                },
                pageSuggestedPromptsScrollStyle: {
                    label: 'Scroll Block Style',
                    description: 'JSON for scroll blocks. layout (horizontal|vertical), scrollHideHeader, button.maxWidth, optional promptPrefixIconAssetID, optional promptPrefixIconSize (square icon size in px, default 16), autoScrollSpeed (px/s, 0=off). Falls back to pageSuggestedPromptsStyle.',
                    type: 'json',
                    default: {},
                },
                clientAIUseDualSearch: {
                    label: 'Use Dual Search Mode',
                    description: 'Enable dual search mode with inline carousel and conversational links.',
                    type: 'boolean',
                    default: false,
                },
                shoppingAssistantInstructions: {
                    label: 'Shopping Assistant Instructions',
                    description: 'Product-specific instructions for Shopping Assistant. These instructions are appended to the system prompt template. Use this to customize behavior specific to the Shopping Assistant product.',
                    type: 'string',
                    multiline: true,
                    rows: 10,
                    default: '',
                    promptKey: 'shoppingAssistant',
                },
                shoppingAssistantPrompt: {
                    label: 'Shopping Assistant Prompt Template',
                    description: 'System prompt template for Shopping Assistant. Supports template variables like $\\{brandVoice\\}, $\\{shoppingAssistantInstructions\\}, $\\{additionalInstructions\\}, etc. Uses default template if empty.',
                    type: 'string',
                    multiline: true,
                    rows: 25,
                    default: '',
                    defaultForEmptyMatch: true,
                    promptKey: 'shoppingAssistant',
                },
                aiModeSearchInstructions: {
                    label: 'AI Mode Search Instructions',
                    description: 'Product-specific instructions for AI Mode Search. These instructions are appended to the system prompt template. Use this to customize behavior specific to the AI Mode Search product.',
                    type: 'string',
                    multiline: true,
                    rows: 10,
                    default: '',
                    promptKey: 'aiSearch',
                },
                aiModeSearchPrompt: {
                    label: 'AI Mode Search Prompt Template',
                    description: 'System prompt template for AI Mode Search. Supports template variables like $\\{brandVoice\\}, $\\{aiModeSearchInstructions\\}, $\\{additionalInstructions\\}, etc. Uses default template if empty.',
                    type: 'string',
                    multiline: true,
                    rows: 25,
                    default: '',
                    defaultForEmptyMatch: true,
                    promptKey: 'aiSearch',
                },
                dualSearchInstructions: {
                    label: 'Dual Search Instructions',
                    description: 'Additional instructions specific to Dual Search. These instructions are included in the system prompt after the Additional Instructions.',
                    type: 'string',
                    multiline: true,
                    rows: 10,
                    default: '',
                    promptKey: 'dualSearch',
                },
                dualSearchPrompt: {
                    label: 'Dual Search Prompt Template',
                    description: 'System prompt template for Dual Search. This prompt includes instructions for Carousel and ProductLink components. Supports template variables like $\\{brandVoice\\}, $\\{dualSearchInstructions\\}, $\\{additionalInstructions\\}, etc. Uses default dual search template if empty.',
                    type: 'string',
                    multiline: true,
                    rows: 25,
                    default: '',
                    defaultForEmptyMatch: true,
                    promptKey: 'dualSearch',
                },
                productAnswersPrompt: {
                    label: 'Product Answers Prompt',
                    description: 'Instructions to guide the AI when generating prompts. Appended after a fixed system prompt. Can define tone, focus, etc.',
                    type: 'string',
                    default: '',
                    defaultForEmptyMatch: true,
                    promptKey: 'productAnswers',
                    multiline: true,
                    rows: 6,
                },
                productContentInstructions: {
                    label: 'Product Content Additional Instructions',
                    description: 'Additional instructions to include with Product Answers and Smart Summary.',
                    type: 'string',
                    multiline: true,
                    rows: 6,
                    default: '',
                    promptKey: 'productAnswers',
                },
                categoryAnswersPrompt: {
                    label: 'Category Answers Prompt',
                    description: 'Instructions to guide the AI when generating prompts. Appended after a fixed system prompt. Can define tone, focus, etc.',
                    type: 'string',
                    default: '',
                    defaultForEmptyMatch: true,
                    promptKey: 'categoryAnswers',
                    multiline: true,
                    rows: 6,
                },
                categoryContentInstructions: {
                    label: 'Category Content Additional Instructions',
                    description: 'Additional instructions to include with Category Answers and Smart Summary.',
                    type: 'string',
                    multiline: true,
                    rows: 6,
                    default: '',
                    promptKey: 'categoryAnswers',
                },
                askAboutProductWidgetPrompt: {
                    label: 'Ask About Product Widget System Prompt',
                    description: 'System prompt override for the Ask About this Product widget. Edit from System → System Prompts. Leave empty to use the default.',
                    type: 'string',
                    default: '',
                    defaultForEmptyMatch: true,
                    defaultKey: 'askAboutProductWidget',
                    promptKey: 'askAboutProductWidget',
                    multiline: true,
                    rows: 6,
                },
                pageSuggestedPromptsCustomCss: {
                    label: 'Suggested Prompts Custom CSS',
                    description: 'Optional: Raw CSS to inject for advanced customization.',
                    type: 'string',
                    default: '',
                },
                pageSuggestedPromptsHeader: {
                    label: 'Suggested Prompts Header',
                    description: 'Header to display above the suggested prompts.',
                    type: 'string',
                    default: null,
                },
                categoryPageSuggestedPromptsHeader: {
                    label: 'Category Suggested Prompts Header',
                    description: 'Header to display above the suggested prompts for category/collection pages.',
                    type: 'string',
                    default: null,
                },
                productAnswersHeaderImage: {
                    label: 'Product Answers Header Image',
                    description: 'Optional image shown above the Product Answers header.',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    defaultPublicAccess: true,
                    default: null,
                },
                productAnswersHeaderImageAlignment: {
                    label: 'Product Answers Header Image Alignment',
                    description: 'Alignment for the Product Answers header image.',
                    type: 'string',
                    enum: ['left', 'center', 'right'],
                    enumLabels: ['Left', 'Center', 'Right'],
                    default: 'left',
                },
                categoryAnswersHeaderImage: {
                    label: 'Category Answers Header Image',
                    description: 'Optional image shown above the Category Answers header.',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    defaultPublicAccess: true,
                    default: null,
                },
                categoryAnswersHeaderImageAlignment: {
                    label: 'Category Answers Header Image Alignment',
                    description: 'Alignment for the Category Answers header image.',
                    type: 'string',
                    enum: ['left', 'center', 'right'],
                    enumLabels: ['Left', 'Center', 'Right'],
                    default: 'left',
                },
                askAboutProductWidgetEnabled: {
                    label: 'Ask About Product Widget',
                    description: 'Enable the Ask About this Product widget.',
                    type: 'boolean',
                    default: false,
                },
                askAboutProductWidgetHeaderImage: {
                    label: 'Ask About Product Widget Header Image',
                    description: 'Optional image shown above the widget title.',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    defaultPublicAccess: true,
                    default: null,
                },
                askAboutProductWidgetHeaderImageAlignment: {
                    label: 'Ask About Product Widget Header Image Alignment',
                    description: 'Alignment for the widget header image.',
                    type: 'string',
                    enum: ['left', 'center', 'right'],
                    enumLabels: ['Left', 'Center', 'Right'],
                    default: 'left',
                },
                askAboutProductWidgetHeaderTitle: {
                    label: 'Ask About Product Widget Header Title',
                    description: 'Main title shown at the top of the widget.',
                    type: 'string',
                    default: 'Ask About this Product',
                },
                askAboutProductWidgetHeaderSubtitle: {
                    label: 'Ask About Product Widget Header Subtitle',
                    description: 'Optional supporting line below the title.',
                    type: 'string',
                    default: '',
                },
                askAboutProductWidgetFooterText: {
                    label: 'Ask About Product Widget Footer Text',
                    description: 'Optional footer text displayed below the answer.',
                    type: 'string',
                    default: '',
                },
                askAboutProductWidgetSubmitButtonText: {
                    label: 'Ask About Product Widget Submit Button Text',
                    description: 'Text shown on the submit button.',
                    type: 'string',
                    default: 'Ask',
                },
                askAboutProductWidgetButtonColor: {
                    label: 'Ask About Product Widget Button Color',
                    description: 'Optional custom background color for the submit button.',
                    type: 'string',
                    default: '',
                },
                askAboutProductWidgetButtonTextColor: {
                    label: 'Ask About Product Widget Button Text Color',
                    description: 'Optional custom text color for the submit button.',
                    type: 'string',
                    default: '',
                },
                askAboutProductWidgetMaxWidth: {
                    label: 'Ask About Product Widget Max Width',
                    description: 'Maximum width of the widget in pixels.',
                    type: 'number',
                    default: 900,
                    minValue: 280,
                    maxValue: 1600,
                },
                askAboutProductWidgetThumbsFeedbackEnabled: {
                    label: 'Ask About Product Widget Thumbs Feedback',
                    description: 'Show thumbs up/down feedback controls after an answer.',
                    type: 'boolean',
                    default: true,
                },
                askAboutProductWidgetProductQuestionsEnabled: {
                    label: 'Ask About Product Widget Product Questions',
                    description: 'Show starter product questions above the input.',
                    type: 'boolean',
                    default: false,
                },
                askAboutProductWidgetRefinementsEnabled: {
                    label: 'Allow Refinements',
                    description: 'Allow the widget to show refinement choices after an answer.',
                    type: 'boolean',
                    default: false,
                },
                askAboutProductWidgetProductCardsEnabled: {
                    label: 'Allow Product Cards',
                    description: 'Allow the widget to show product cards in answers.',
                    type: 'boolean',
                    default: false,
                },
                askAboutProductWidgetInputPlaceholder: {
                    label: 'Ask About Product Widget Input Placeholder',
                    description: 'Placeholder text for the widget input.',
                    type: 'string',
                    default: 'Ask me anything...',
                },
                askAboutProductWidgetContainerCss: {
                    label: 'Ask About Product Widget Container CSS',
                    description: 'Optional inline CSS declarations for the widget root container (for example: margin-top: 16px;).',
                    type: 'string',
                    default: '',
                },
                askAboutProductWidgetQuestionAlignment: {
                    label: 'Ask Widget Question Alignment',
                    description: 'Alignment of the submitted question bubble above the answer.',
                    type: 'string',
                    enum: ['left', 'right', 'right-60'],
                    enumLabels: ['Left', 'Right', '60% Right'],
                    default: 'left',
                },
                askAboutProductWidgetProductQuestionIcon: {
                    label: 'Ask Widget Product Question Icon',
                    description: 'Optional image shown before each Product Question chip in the Ask widget.',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    defaultPublicAccess: true,
                    default: null,
                },
                askAboutProductWidgetProductQuestionIconSize: {
                    label: 'Ask Widget Product Question Icon Size',
                    description: 'Square icon size in pixels. Defaults to 32px.',
                    type: 'number',
                    default: 32,
                    minValue: 8,
                    maxValue: 64,
                },
                categoryPageSuggestedPromptsMaxCount: {
                    label: 'Category Max Suggested Prompts Count',
                    description: 'Maximum number of page-specific prompts to generate for category/collection pages. Falls back to pageSuggestedPromptsMaxCount if not set.',
                    type: 'number',
                    default: null,
                    minValue: 1,
                    maxValue: 10,
                },
                categoryPageSuggestedPromptsRefreshDays: {
                    label: 'Category Suggested Prompts Refresh Interval (days)',
                    description: 'Number of days before refreshing the suggested prompts cache for category pages. Falls back to pageSuggestedPromptsRefreshDays if not set.',
                    type: 'number',
                    default: null,
                    minValue: 1,
                    maxValue: 30,
                },
                categoryPageSuggestedPromptsStyle: {
                    label: 'Category Suggested Prompts Styles',
                    description: 'JSON (incl. optional promptPrefixIconAssetID, optional promptPrefixIconSize in px for a square icon, default 16). Falls back to pageSuggestedPromptsStyle.',
                    type: 'json',
                    default: {},
                },
                categoryPageSuggestedPromptsScrollStyle: {
                    label: 'Category Scroll Block Style',
                    description: 'JSON for category scroll blocks (optional promptPrefixIconAssetID, optional promptPrefixIconSize in px for a square icon, default 16). Falls back to pageSuggestedPromptsScrollStyle.',
                    type: 'json',
                    default: {},
                },
                categoryPageSuggestedPromptsCustomCss: {
                    label: 'Category Suggested Prompts Custom CSS',
                    description: 'Optional: Raw CSS for category suggested prompts. Falls back to pageSuggestedPromptsCustomCss if not set.',
                    type: 'string',
                    default: '',
                },
                systemPromptsBase: {
                    label: 'System Prompts Base',
                    description: 'Per-flow base system prompt template (protected area). Keys: shoppingAssistant, aiSearch, dualSearch, orderStatus, exitTool, productAnswers, categoryAnswers, smartSummary, smartSummaryCategory. Empty string = use system default at runtime. AI Config fields append to this base.',
                    type: 'json',
                    default: {},
                },
                productPagePath: {
                    label: 'Product Page Path',
                    description: 'URL path pattern to detect product pages. Default: /product',
                    type: 'string',
                    default: '/product',
                },
                categoryCollectionPath: {
                    label: 'Category Collection Path',
                    description: 'URL path pattern to detect collection pages. Default: /collections',
                    type: 'string',
                    default: '/collections',
                },
                showProductPromptsInShoppingAssistant: {
                    label: 'Show Product Prompts in Shopping Assistant',
                    description: 'Display AI-generated product prompts in the chat starting screen. If disabled, prompts only appear on page.',
                    type: 'boolean',
                    default: true,
                },
                displayProductQuestionsAtConversationEnd: {
                    label: 'Display Product Questions at Conversation End',
                    description: 'Show AI-generated product questions at the end of the conversation so shoppers can keep asking follow-ups.',
                    type: 'boolean',
                    default: true,
                },
                welcomeScreenHtml: {
                    label: 'Welcome Screen HTML',
                    description: 'Custom HTML for the shopping assistant welcome screen.',
                    type: 'string',
                    multiline: true,
                    default: '',
                },
                suppressSuggestedPromptMessage: {
                    label: 'Suppress Starting Message',
                    description: 'Hide the inline starting message and welcome chips.',
                    type: 'boolean',
                    default: false,
                },
                hideProductAnswerTitleInMessage: {
                    label: 'Hide Product Answer Title in Message',
                    description: 'Hide the product or category title above prompt text in chat messages while keeping full context sent to AI.',
                    type: 'boolean',
                    default: false,
                },
                resetConversationMode: {
                    label: 'Reset Conversation Mode',
                    description: 'When enabled for inline, starts a fresh conversation on each interaction and disables persistence.',
                    type: 'boolean',
                    default: false,
                },
                productAnswersInputEnabled: {
                    label: 'Product Answers Input',
                    description: 'Show text input below prompts on product pages. Sends free-text to Shopping Assistant.',
                    type: 'boolean',
                    default: false,
                },
                categoryAnswersInputEnabled: {
                    label: 'Category Answers Input',
                    description: 'Show text input below prompts on collection pages. Sends free-text to Shopping Assistant.',
                    type: 'boolean',
                    default: false,
                },
                productAnswersInputPlaceholder: {
                    label: 'Product Answers Input Placeholder',
                    description: 'Placeholder text for the input on product pages.',
                    type: 'string',
                    default: 'Ask me anything...',
                },
                categoryAnswersInputPlaceholder: {
                    label: 'Category Answers Input Placeholder',
                    description: 'Placeholder text for the input on collection pages.',
                    type: 'string',
                    default: 'Ask me anything...',
                },
                productPageSmartSummaryEnabled: {
                    label: 'Product Page Smart Summary Enabled',
                    description: 'Enable AI-generated smart summary (key features bullets) for product pages.',
                    type: 'boolean',
                    default: false,
                },
                categoryPageSmartSummaryEnabled: {
                    label: 'Category Page Smart Summary Enabled',
                    description: 'Enable AI-generated smart summary for category/collection pages.',
                    type: 'boolean',
                    default: false,
                },
                productInsertMetadataEnabled: {
                    label: 'Product Insert Metadata Enabled',
                    description: 'Persist AI-generated SEO meta description and GEO summary to Shopify product metafields (microcommerce.seo_description, microcommerce.geo_summary). Add the SEO/GEO Content block to your theme to render them.',
                    type: 'boolean',
                    default: false,
                },
                categoryInsertMetadataEnabled: {
                    label: 'Category Insert Metadata Enabled',
                    description: 'Persist AI-generated SEO/GEO content to Shopify collection metafields. Add the SEO/GEO Content block to your theme to render them.',
                    type: 'boolean',
                    default: false,
                },
                pageSmartSummaryMaxBullets: {
                    label: 'Smart Summary Max Bullets',
                    description: 'Maximum number of bullet points in the smart summary.',
                    type: 'number',
                    default: 5,
                    minValue: 1,
                    maxValue: 15,
                },
                pageSmartSummaryGeneratedByAiLabel: {
                    label: 'Show generated by AI label',
                    description: 'When enabled, shows a label below the summary. The label text comes from the token "generatedByAiLabel" (e.g. "Generated by AI").',
                    type: 'boolean',
                    default: false,
                },
                pageSmartSummaryGeneratedByAiLabelAlignment: {
                    label: 'Generated by AI label alignment',
                    description: 'Text alignment of the "Generated by AI" label below the summary.',
                    type: 'string',
                    enum: ['left', 'center', 'right'],
                    enumLabels: ['Left', 'Center', 'Right'],
                    default: 'right',
                },
                pageSmartSummaryAlignBulletsLeft: {
                    label: 'Align smart summary bullets left',
                    description: 'When enabled, removes list indent so bullets align under the header.',
                    type: 'boolean',
                    default: false,
                },
                pageSmartSummaryStyle: {
                    label: 'Smart Summary Styles',
                    description: 'JSON for container, header, and summary font styles (product and category).',
                    type: 'json',
                    default: {},
                },
                pageSmartSummaryCustomCss: {
                    label: 'Smart Summary Custom CSS',
                    description: 'Optional: Raw CSS for the smart summary block (product and category).',
                    type: 'string',
                    default: '',
                },
                pageGeoSummaryDisplayMode: {
                    label: 'On Page Display',
                    description: 'Choose how GEO Summary content is displayed on product and category pages.',
                    type: 'string',
                    enum: ['full', 'accordion'],
                    enumLabels: ['Full Display', 'Accordion'],
                    default: 'full',
                },
                pageGeoSummaryMaxWidth: {
                    label: 'GEO Summary Max Width',
                    description: 'Optional max width for GEO Summary block.',
                    type: 'string',
                    default: '',
                },
                guidesBlockTitle: {
                    label: 'Guides block title (PDP)',
                    description: 'Heading shown above the related guides list on the product page (Shopify theme block). Leave empty to use the default "Guides".',
                    type: 'string',
                    default: '',
                },
                guidesGeoEnabled: {
                    label: 'GEO guides (Shopify PDP)',
                    description: 'When enabled, product and guide changes sync the related-guides metafield (microcommerce.related_guides) to Shopify for this portal. Requires product mutation scopes on the Shopify session. Turn off unless GEO guides are live to avoid sync noise and permission errors on product feeds.',
                    type: 'boolean',
                    default: false,
                },
                guidesTopicsPrompt: {
                    label: 'GEO Guides Topics Prompt',
                    description: 'Optional prompt override for generating guide topics from a source URL. Leave empty to use the default.',
                    type: 'string',
                    multiline: true,
                    rows: 10,
                    default: '',
                },
                guidesContentPrompt: {
                    label: 'GEO Guides Content Prompt',
                    description: 'Optional prompt override for generating topic guide content. Leave empty to use the default.',
                    type: 'string',
                    multiline: true,
                    rows: 16,
                    default: '',
                },
                coreGuideContentPrompt: {
                    label: 'Core Guide Content Prompt',
                    description: 'Optional prompt override for generating Core Guide content. Leave empty to use the default.',
                    type: 'string',
                    multiline: true,
                    rows: 16,
                    default: '',
                },
                guideScoreSystemPrompt: {
                    label: 'Guide Score System Prompt',
                    description: 'Optional system prompt override for guide scoring (SEO/GEO/E-E-A-T). Leave empty to use the default.',
                    type: 'string',
                    multiline: true,
                    rows: 16,
                    default: '',
                },
                llmsTxtPrompt: {
                    label: 'llms.txt Prompt',
                    description: 'Optional prompt override for generating merchant storefront llms.txt content. Leave empty to use the default.',
                    type: 'string',
                    multiline: true,
                    rows: 16,
                    default: '',
                },
                guidesHybridInstructions: {
                    label: 'GEO Guides Hybrid Append',
                    description: 'Additional instructions appended when guide generation uses Hybrid optimization mode.',
                    type: 'string',
                    multiline: true,
                    rows: 8,
                    default: '',
                },
                guidesSeoHeavyInstructions: {
                    label: 'GEO Guides SEO Append',
                    description: 'Additional instructions appended when guide generation uses SEO optimization mode.',
                    type: 'string',
                    multiline: true,
                    rows: 8,
                    default: '',
                },
                guidesGeoHeavyInstructions: {
                    label: 'GEO Guides GEO Append',
                    description: 'Additional instructions appended when guide generation uses GEO optimization mode.',
                    type: 'string',
                    multiline: true,
                    rows: 8,
                    default: '',
                },
                citationInstructionsOverride: {
                    label: 'Guide Citation Instructions',
                    description: 'Optional override for guide source-citation instructions. Leave empty to use the default.',
                    type: 'string',
                    multiline: true,
                    rows: 16,
                    default: '',
                },
                citationSourcesSuggestPrompt: {
                    label: 'Guide Citation Source Suggestions Prompt',
                    description: 'Optional override for the prompt that suggests authoritative citation source domains. Leave empty to use the default.',
                    type: 'string',
                    multiline: true,
                    rows: 16,
                    default: '',
                },
                citationDomains: {
                    label: 'Guide Trusted Source Domains',
                    description: 'Merchant-approved external domains that may be cited during guide generation.',
                    type: 'array',
                    references: 'string',
                    default: [],
                },
                editorialInstructionsOverride: {
                    label: 'Guide Editorial Neutrality Instructions',
                    description: 'Optional override for guide editorial-neutrality instructions. Leave empty to use the default.',
                    type: 'string',
                    multiline: true,
                    rows: 16,
                    default: '',
                },
                humanVoiceInstructionsOverride: {
                    label: 'Guide Human Voice Instructions',
                    description: 'Optional override for guide human-voice instructions. Leave empty to use the default.',
                    type: 'string',
                    multiline: true,
                    rows: 16,
                    default: '',
                },
                decisionSupportInstructionsOverride: {
                    label: 'Guide Decision Support Instructions',
                    description: 'Optional override for guide decision-support instructions. Leave empty to use the default.',
                    type: 'string',
                    multiline: true,
                    rows: 16,
                    default: '',
                },
                shopifyWidgetsCustomCss: {
                    label: 'Shopify Widgets Custom CSS',
                    description: 'CSS applied to all offline Shopify widgets (GEO Summary, Related Products Carousel, etc.). Injected server-side — no JS bundle required.',
                    type: 'string',
                    multiline: true,
                    rows: 16,
                    placeholder: '.microcommerce-geo-summary {\n  /* GEO Summary styles */\n}\n\n.mc-related-carousel {\n  /* Carousel styles */\n}',
                    default: '',
                },
                productStructuredDataConfig: {
                    label: 'Product Structured Data Config',
                    description: 'Managed via the Product Structured Data UI in Portal AI Configuration. Raw JSON contract: { emit_core, emit_images, emit_additional_property, emit_aggregate_rating, emit_policy, emit_shipping }; missing flags default to true, all-false emits nothing. Legacy "mode" (full/complement/disabled) is read only for old configs and is migrated to flags, never written back.',
                    type: 'json',
                    default: {},
                },
                pageSmartSummarySystemPromptInstructions: {
                    label: 'Smart Summary System Prompt Instructions (Product)',
                    description: 'Instructions to guide the AI when generating product smart summary. Appended after a fixed system prompt.',
                    type: 'string',
                    default: '',
                    defaultForEmptyMatch: true,
                    defaultKey: 'smartSummary',
                    multiline: true,
                    rows: 6,
                },
                categoryPageSmartSummarySystemPromptInstructions: {
                    label: 'Smart Summary System Prompt Instructions (Category)',
                    description: 'Instructions to guide the AI when generating category smart summary.',
                    type: 'string',
                    default: '',
                    defaultForEmptyMatch: true,
                    defaultKey: 'smartSummaryCategory',
                    multiline: true,
                    rows: 6,
                },
                addToCartEnabled: {
                    label: 'Add to Cart Enabled',
                    description: 'Enable the "Add to Cart" functionality in product cards within the conversation widget.',
                    type: 'boolean',
                    default: false,
                },
                mergeProductIntegrationData: {
                    label: 'Merge Product Integration Data',
                    description: 'When enabled, knowledge base / search item references include a full Product-table payload (`productPayload`: variants, inventory, options) for product cards so the widget does not need a separate storefront fetch for those results.',
                    type: 'boolean',
                    default: false,
                },
                enhancedProductCardEnabled: {
                    label: 'Enhanced Product Card Enabled',
                    description: 'Enable the enhanced product card layout and formatting even without Add to Cart.',
                    type: 'boolean',
                    default: false,
                },
                guideProductPopupConfig: {
                    label: 'Related Products Popup',
                    description: 'Dedicated guide-linked product pop-up sourced from article.metafields.microcommerce.related_products. This is separate from Shopping Assistant chat.',
                    type: 'json',
                    default: {
                        enabled: false,
                        size: {
                            widthPx: null,
                            heightPx: null,
                        },
                        autoOpen: {
                            delayMs: 0,
                        },
                        content: {
                            heading: 'Featured products from this guide',
                            addToCartLabel: 'Add to cart',
                            addingLabel: 'Adding...',
                            addedLabel: 'Added',
                            unavailableLabel: 'Unavailable',
                            retryLabel: 'Try again',
                            errorLabel: 'Could not add this item. Please try again.',
                            variantLabel: 'Options',
                        },
                        products: {
                            source: 'guide_linked_products',
                            maxItems: 12,
                        },
                    },
                },
                enableGeoLocation: {
                    label: 'Enable Geolocation',
                    description: 'Prompt the browser for the shopper\'s location (via window.buyblvd.getUserLocation) and include it in the customer context so the product card can display distance to the nearest store.',
                    type: 'boolean',
                    default: false,
                },
                hamburgerMenu: {
                    label: 'Hamburger Menu',
                    description: 'Links and options for the header hamburger menu. Items are ordered (drag to reorder); New Conversation can be a row with checkbox.',
                    type: 'json',
                    default: { items: [{ type: 'newConversation', enabled: true }] },
                },
                productCardConfig: {
                    label: 'Product Card Configuration',
                    description: 'Display and behavior for the product card (ordering, visibility, styles).',
                    type: 'json',
                    default: {
                        orientation: 'vertical',
                        showTitle: true,
                        showDescription: true,
                        showRatings: false,
                        showPrice: true,
                        showVariantSelection: true,
                        elementOrder: [
                            'productName',
                            'ratings',
                            'description',
                            'pricing',
                            'variantSelection',
                            'addToCartButton',
                        ],
                        buttonText: 'ADD TO BAG',
                        buttonStyle: {
                            backgroundColor: 'color_primary',
                            color: 'color_font_inverse',
                            hoverBackgroundColor: 'color_primary_hover',
                            hoverColor: 'color_font_inverse',
                            disabledBackgroundColor: '',
                            disabledColor: 'color_font',
                            borderRadius: 8,
                        },
                        borderStyle: { borderWidth: 1, borderColor: '#ccc', borderRadius: 4 },
                        backgroundColor: '#ffffff',
                        imageClickable: true,
                        imageSwatches: {
                            enabled: true,
                            size: 50,
                            gap: 8,
                            hideOnOutOfStock: true,
                        },
                        // When false, hide the post-click success bubble + Checkout button.
                        // Default true for the Shopify cart flow; set false for Save-to-List
                        // style flows (e.g. Buy Blvd) where "added" is terminal. (MCN-2268)
                        showCheckoutFlow: true,
                        // When false, hide quantity stepper on product card; omit = show (MCN-2331).
                        showQuantitySelector: true,
                    },
                },
                aiSearchConfig: {
                    label: 'AI Search Configuration',
                    description: 'Sizing and layout configuration for AI Search ProductDetailPanel.',
                    type: 'json',
                    default: {
                        mainImage: {
                            height: '50vh',
                            minHeight: '200px',
                            aspectRatio: 0.775,
                            borderRadius: 0,
                        },
                        thumbnails: {
                            width: 76,
                            height: 76,
                            borderRadius: 8,
                            gap: 8,
                            selectedBorderWidth: 2,
                            hoverBorderWidth: 2,
                        },
                        variantPhotoSwatches: {
                            enabled: true,
                            size: 50,
                            gap: 8,
                        },
                        detailsButton: {
                            padding: '12px 16px',
                            borderRadius: 8,
                            fontSize: '0.875rem',
                        },
                        typography: {
                            titleFontSize: '1.1rem',
                            priceFontSize: '1rem',
                            errorFontSize: '0.75rem',
                            cardTitleVariant: 'h5',
                            panelTitleVariant: 'h6',
                            cardTitleMaxLines: 2,
                            panelTitleMaxLines: undefined,
                        },
                        description: {
                            cardMaxLength: 75,
                            panelMaxLength: 200,
                            showSeeMore: true,
                        },
                        container: {
                            padding: 16,
                            gap: 16,
                        },
                        layout: {
                            headerHeightValue: '88px',
                            headerCssVariable: '--header-height',
                            useCssVariable: true,
                        },
                    },
                },
                testPrompts: {
                    label: 'Prompt Testing',
                    description: 'Saved prompts and run counts for the Prompt Testing tab.',
                    type: 'json',
                    default: { prompts: [] },
                },
                wizards: {
                    label: 'Selection Wizards',
                    description: 'Preference wizards (e.g. shoes, runner\'s watch). Each has id, name, and steps. The AI picks the wizard from the topic.',
                    type: 'json',
                    format: 'FormattedText',
                    default: [],
                },
                brandVoicePrompt: {
                    label: 'Brand Voice Prompt',
                    description: 'Enter instructions for the voice style for the assistant.',
                    type: 'string',
                    multiline: true,
                    rows: 4,
                    default: '',
                },
                useGoogleAnalytics: {
                    label: 'Use Google Analytics',
                    description: 'Enable Google Analytics tracking for the client AI.',
                    type: 'boolean',
                    default: false,
                },
                googleAnalyticsPropertyID: {
                    label: 'Google Analytics Property ID',
                    description: 'The Google Analytics property ID (e.g., G-XXXXXXXXXX) to use for tracking.',
                    type: 'string',
                    default: '',
                },
                clientAISuggestedPromptsLocalized: {
                    label: 'Localize Suggested Prompts',
                    type: 'boolean',
                    default: false,
                    description: 'Enable localized suggested prompts per language.',
                },
                clientAISuggestedPromptsL10N: {
                    label: 'Suggested Prompts (Localized)',
                    type: 'json',
                    default: null,
                    description: 'Localized suggested prompts by locale.',
                },
                clientAIPersistentPromptsLocalized: {
                    label: 'Localize Persistent Prompts',
                    type: 'boolean',
                    default: false,
                    description: 'Enable localized persistent prompts per language.',
                },
                clientAIPersistentPromptsL10N: {
                    label: 'Persistent Prompts (Localized)',
                    type: 'json',
                    default: null,
                    description: 'Localized persistent prompts by locale.',
                },
            },
        },
        learningBoardConfiguration: {
            name: 'Learning Board Configuration',
            label: 'Learning Board Configuration',
            description: 'Configuration for Learning Board display settings.',
            type: 'dependant',
            properties: {
                noDataMessage: {
                    label: 'No Data Message',
                    type: 'richtext',
                    description: 'Message to display when no learning board is available for the customer.',
                    default: {
                        html: null,
                        slate: [
                            {
                                children: [{ text: 'A Learning Board has not yet been created for this account. Check back later.' }],
                            },
                        ],
                    },
                },
            },
        },
        asset: {
            name: 'Asset',
            label: 'Asset',
            description: 'A file that has been uploaded to a repository.  Examples include videos, images, PDFs, spreadsheets, etc.',
            type: 'entity',
            formConfig: [
                {
                    name: 'tabs',
                    type: 'tabs',
                    elements: [
                        {
                            name: 'general',
                            type: 'group',
                            label: 'General',
                            elements: [
                                'name',
                                'type',
                                'description',
                                'access',
                                'tags',
                                'status',
                                'repository',
                                'useHls',
                            ],
                        },
                        {
                            name: 'transcript',
                            type: 'group',
                            label: 'Transcript',
                            elements: ['transcript'],
                        },
                        {
                            name: 'integration',
                            type: 'group',
                            label: 'Integration',
                            elements: ['path', 'externalID'],
                        },
                        {
                            name: 'knowledgeBase',
                            label: 'Knowledge Base',
                            type: 'component',
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                externalID: {
                    label: 'External ID',
                    type: 'string',
                    description: 'A reference to this assets ID in an external system such as a CMS.  Typically used in import processes.',
                    default: '',
                },
                name: {
                    label: 'Name',
                    description: 'The customer facing name for the asset. Defaults to the file name at upload.',
                    type: 'string',
                    searchable: true,
                },
                path: {
                    label: 'Bucket Path',
                    description: 'The S3 path to the asset.',
                    type: 'string',
                    readOnly: true,
                    fieldName: 'key',
                },
                description: {
                    label: 'Description',
                    type: 'string',
                    aiTarget: 'assetFile',
                    promptList: [
                        'Summarize',
                    ],
                    searchable: true,
                },
                repository: {
                    label: 'Repository',
                    type: 'reference',
                    references: 'repository',
                    fieldName: 'repositoryID',
                    readOnly: true,
                },
                type: {
                    label: 'Type',
                    type: 'string',
                    default: undefined,
                    enum: ['video', 'audio', 'image', 'pdf', 'article', 'quiz', 'text', 'zip', 'application', 'scorm', 'knowledgeCheck', 'typeform'],
                    enumLabels: ['Video', 'Audio', 'Image', 'PDF', 'Article', 'Quiz', 'Text', 'ZIP', 'Application', 'SCORM', 'Knowledge Check', 'Typeform'],
                    readOnly: true,
                },
                tags: {
                    label: 'Tags',
                    type: 'string',
                    placeholder: 'Add tags...',
                    subtype: 'chips',
                    enum: [],
                    enumLabels: [],
                    maxLength: 500,
                    group: 'general',
                    searchable: true,
                    portalApi: false,
                },
                mimeType: {
                    label: 'Mime Type',
                    type: 'string',
                    readOnly: true,
                },
                derivativeStatus: {
                    label: 'Derivative Status',
                    description: 'Status of processing of thumbnails for images, key frames for videos, etc.',
                    type: 'string',
                    default: 'pending',
                    enum: ['pending', 'processing', 'processed', 'error'],
                    enumLabels: ['Pending', 'Processing', 'Processed', 'Error'],
                    readOnly: true,
                },
                transcribeStatus: {
                    label: 'Transcribe Status',
                    description: 'Status of transcription generation for videos.',
                    type: 'string',
                    default: 'pending',
                    enum: ['pending', 'processing', 'processed', 'error'],
                    enumLabels: ['Pending', 'Processing', 'Processed', 'Error'],
                    readOnly: true,
                },
                status: {
                    label: 'Status',
                    type: 'string',
                    default: 'defined',
                    enum: ['defined', 'uploaded', 'processing', 'processed', 'published', 'locked', 'removed', 'deleted'],
                    enumLabels: ['Defined', 'Uploaded', 'Processing', 'Processed', 'Published', 'Locked', 'Removed', 'Deleted'],
                    readOnly: true,
                },
                access: {
                    label: 'Access',
                    type: 'string',
                    default: 'normal',
                    enum: ['normal', 'public'],
                    enumLabels: ['Normal', 'Public'],
                    readOnly: true,
                },
                useHls: {
                    label: 'Use HLS',
                    description: 'Use HLS streaming the video.',
                    type: 'boolean',
                    default: false,
                },
                hlsGenerated: {
                    label: 'HLS Generated',
                    description: 'HLS output has been generated for this asset.',
                    type: 'boolean',
                    default: false,
                    portalApi: false,
                },
                dominantColor: {
                    label: 'Dominant Color',
                    type: 'string',
                    subtype: 'hexColor',
                },
                length: {
                    label: 'Length',
                    type: 'string',
                    default: '0',
                    group: null,
                    subGroup: null,
                },
                transcript: {
                    label: 'Transcript',
                    type: 'string',
                    default: '',
                    multiline: true,
                    rows: 10,
                    group: null,
                    subGroup: null,
                    searchable: true,
                },
                scormManifest: {
                    label: 'Scorm Manifest',
                    type: 'string',
                    default: '',
                    multiline: true,
                    portalApi: false,
                    rows: 10,
                    group: null,
                    subGroup: null,
                },
                // dont want it in DB but want to serialize between server and client code
                /*
                 * Its a json object
                 * {
                 * size: { url: signedUrl, expiry: timestamp }
                 * }
                 */
                signedUrlMap: {
                    label: 'Signed URL',
                    type: 'json',
                    default: {},
                    group: null,
                    subGroup: null,
                    excludeField: true, // isnt in database
                },
            },
        },
        category: {
            name: 'Category',
            label: 'Category',
            pluralTypeName: 'Categories',
            description: 'A list of Categories to organize courses.',
            type: 'entity',
            formConfig: [
                {
                    name: 'tabs',
                    type: 'tabs',
                    elements: [
                        {
                            name: 'details',
                            type: 'group',
                            label: 'Details',
                            elements: [
                                'name',
                                'description',
                                'categoryImage',
                                {
                                    name: 'slug',
                                    label: 'Slug',
                                    type: 'subGroup',
                                    elements: [
                                        {
                                            name: 'manualSlug',
                                            type: 'guardedOption',
                                            controlledBy: 'manualSlug',
                                            controlledField: 'slug',
                                            label: 'Override Slug.',
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            name: 'items',
                            type: 'group',
                            label: 'Items',
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                    resolver: 'PortalCategoryResolver',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                    group: 'details',
                    // default: '',
                },
                slug: {
                    label: 'Slug',
                    type: 'string',
                    validation: 'slug',
                    group: null,
                    searchable: true,
                },
                manualSlug: {
                    type: 'boolean',
                    group: null,
                    portalApi: false,
                },
                // active: {
                //   label: 'Active',
                //   type: 'boolean',
                //   default: true,
                //   group: 'details',
                // },
                // displayInNavigation: {
                //   label: 'Display In Navigation',
                //   type: 'boolean',
                //   default: true,
                //   group: 'details',
                // },
                description: {
                    label: 'Description',
                    type: 'string',
                    maxLength: 500,
                    group: 'details',
                    // default: '',
                },
                categoryImage: {
                    label: 'Category Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    group: 'details',
                },
                // image: {
                //   label: 'Image',
                //   type: 'reference',
                //   references: 'asset',
                //   assetType: 'image',
                //   fieldName: 'image',
                //   group: 'details',
                // },
                // startDate: {
                //   label: 'Active Dates',
                //   type: 'date',
                //   enum: ['startDate', 'endDate'],
                //   enumLabels: ['Start Date', 'End Date'],
                //   group: 'details',
                // },
                endDate: {
                    label: 'End Date',
                    type: 'date',
                    group: null,
                },
                subCategories: {
                    label: 'Sub Categories',
                    type: 'array',
                    references: 'category',
                    fieldName: 'categories', // overrides the name in the backend datasource
                    group: null, // should hide it
                },
                items: {
                    label: 'Items',
                    type: 'array',
                    references: 'item',
                    group: 'items',
                    sortable: true,
                    scope: 'portal',
                },
            },
            required: ['id', 'name'],
        },
        certificate: {
            name: 'Certificate',
            label: 'Course Certificate',
            description: "Certificate for course. It's subtype of Certification.",
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                awardedAt: {
                    label: 'Awarded Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                certification: {
                    label: 'Certification',
                    type: 'reference',
                    references: 'certification',
                    fieldName: 'certificationID', // overrides the name in the backend datasource
                    readOnly: true, // defaults to true
                },
                customer: {
                    label: 'Customer',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'customerID',
                    shortKey: true,
                    readOnly: true,
                },
                progressPercentage: {
                    label: 'Course Progress',
                    type: 'number',
                    default: 0,
                },
                score: {
                    label: 'Score',
                    type: 'number',
                    default: 0,
                },
                course: {
                    label: 'Course',
                    type: 'reference',
                    references: 'course',
                    fieldName: 'courseKey',
                    group: 'general',
                },
                program: {
                    label: 'Program',
                    type: 'reference',
                    references: 'program',
                    fieldName: 'programKey',
                    group: 'general',
                },
            },
        },
        certification: {
            name: 'certification',
            label: 'Certification',
            description: 'Certification definition used for courses and programs. A certification is awared to a customer as a certificate.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                    primaryKey: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                },
                description: {
                    label: 'Description',
                    type: 'string',
                },
                template: {
                    label: 'Certificate Template',
                    description: 'The template for the certificate.',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'pdf',
                },
                poster: {
                    label: 'Certificate Poster',
                    description: 'Poster image for the certificate.',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                },
                fields: {
                    label: 'Field Specifications',
                    type: 'json',
                    defaultValue: [],
                    portalApi: false,
                },
                emailTemplate: {
                    label: 'Certificate Email',
                    type: 'reference',
                    references: 'emailTemplate',
                    assetType: 'emailTemplate',
                    fieldName: 'emailTemplateID',
                    group: 'details',
                    portalApi: false,
                },
            },
        },
        clientAi: {
            name: 'ClientAI',
            label: 'Client AI Configuration',
            description: 'A configuration for an embeddable client AI instance.',
            type: 'entity',
            formConfig: [
                {
                    name: 'tabs',
                    type: 'tabs',
                    elements: [
                        {
                            name: 'general',
                            type: 'group',
                            label: 'Details',
                            elements: [
                                'name',
                                'active',
                                'platform',
                                'referrers',
                                {
                                    name: 'embedGroup',
                                    type: 'subGroup',
                                    label: 'Embed Code',
                                    elements: [
                                        {
                                            type: 'component',
                                            name: 'embedCode',
                                        },
                                        'customInitCode',
                                        'customCartUpdateCode',
                                        'customProductDescriptionSanitizerCode',
                                    ],
                                },
                            ],
                        },
                        {
                            name: 'shopper',
                            type: 'group',
                            label: 'Shopping Assistant',
                            elements: [
                                'initialState',
                                {
                                    name: 'popupConfigGroup',
                                    type: 'subGroup',
                                    label: 'Popup Configuration',
                                    elements: [
                                        'popupConfig',
                                    ],
                                },
                                {
                                    name: 'mobilePopupConfigGroup',
                                    type: 'subGroup',
                                    label: 'Mobile Popup Configuration',
                                    elements: [
                                        'mobilePopupConfig',
                                    ],
                                },
                                {
                                    name: 'minimizedConfigGroup',
                                    type: 'subGroup',
                                    label: 'Minimized Configuration',
                                    elements: [
                                        'minimizedConfig',
                                    ],
                                },
                                {
                                    name: 'autoOpenConfigGroup',
                                    type: 'subGroup',
                                    label: 'Auto Open Configuration',
                                    elements: [
                                        'autoOpenConfig',
                                    ],
                                },
                                {
                                    name: 'autoMinimizeConfigGroup',
                                    type: 'subGroup',
                                    label: 'Auto Minimize Configuration',
                                    elements: [
                                        'autoMinimizeOnBlur',
                                    ],
                                },
                                {
                                    name: 'conversationPersistenceConfigGroup',
                                    type: 'subGroup',
                                    label: 'Conversation Persistence',
                                    elements: [
                                        'disableConversationPersistence',
                                    ],
                                },
                                {
                                    name: 'shoppingAssistantPageSelector',
                                    type: 'subGroup',
                                    label: 'Shopping Assistant Page Selector',
                                    elements: [
                                        'portal',
                                        'page',
                                    ],
                                },
                            ],
                        },
                        {
                            name: 'inlineAssistant',
                            type: 'group',
                            label: 'Inline Assistant',
                            elements: [
                                {
                                    name: 'inlineAssistantWidgetConfig',
                                    type: 'subGroup',
                                    label: 'Page & layout',
                                    elements: [
                                        'inlinePortal',
                                        'inlinePage',
                                        'inlineWidgetWidth',
                                        'inlineMinHeight',
                                        'inlineMaxHeight',
                                    ],
                                },
                            ],
                        },
                        {
                            name: 'search',
                            type: 'group',
                            label: 'Search',
                            elements: [
                                {
                                    name: 'searchPageSelector',
                                    type: 'subGroup',
                                    label: 'Search Page Selector',
                                    elements: [
                                        'searchPortal',
                                        'searchPage',
                                        'aiSearchButtonConfig',
                                    ],
                                },
                                {
                                    name: 'searchLayoutGroup',
                                    type: 'subGroup',
                                    label: 'Search Layout Configuration',
                                    elements: [
                                        'initialHeight',
                                    ],
                                },
                                {
                                    name: 'searchPromptGroup',
                                    type: 'subGroup',
                                    label: 'Search Prompt Configuration',
                                    elements: [
                                        'searchOverrides',
                                    ],
                                },
                            ],
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                    group: null,
                    subGroup: null,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                },
                libraries: {
                    label: 'Libraries',
                    type: 'referenceArray',
                    subtype: 'chips',
                    references: 'library',
                    fieldName: 'libraries',
                    group: 'advancedOptions',
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                externalID: {
                    label: 'External ID',
                    type: 'string',
                    group: 'advanced',
                    description: 'A reference to this assets ID in an external system such as a CMS.  Typically used in import processes.',
                    default: '',
                },
                name: {
                    label: 'Client AI Name',
                    placeholder: 'Add name',
                    type: 'string',
                    minLength: 8,
                    maxLength: 80,
                    group: 'general',
                    searchable: true,
                },
                // headline: {
                //   label: 'Headline',
                //   placeholder: 'Add headline',
                //   type: 'string',
                //   maxLength: 500,
                //   wordRequired: true,
                //   group: 'general',
                //   searchable: true,
                // },
                active: {
                    label: 'Active',
                    type: 'boolean',
                    default: true,
                    group: 'publishDetails',
                    placeholder: 'Service is enabled and available for access by customers.',
                },
                referrers: {
                    label: 'Referrers',
                    type: 'string',
                    default: '',
                    subtype: 'bulletPoints',
                },
                initialState: {
                    label: 'Initial State',
                    type: 'string',
                    default: 'CLOSED', // closed, minimized, open
                    enum: ['CLOSED', 'MINIMIZED', 'OPEN'],
                    enumLabels: ['Closed', 'Minimized', 'Open'],
                    portalApi: false,
                },
                platform: {
                    label: 'Host Platform',
                    description: 'Host platform this embed is installed on. Selects the PlatformAdapter used for Add-to-Cart, product data fetch, order lookup, locale and currency. Buy Blvd delegates Add-to-Cart to the host\'s window.buyblvd.addToCart hook (Save to List).',
                    type: 'string',
                    default: 'Shopify',
                    enum: ['Shopify', 'BuyBlvd', 'Custom'],
                    enumLabels: ['Shopify', 'Buy Blvd', 'Custom'],
                },
                portal: {
                    label: 'Portal',
                    type: 'reference',
                    references: 'portal',
                    fieldName: 'portalKey',
                },
                page: {
                    label: 'Shopping Assistant Page',
                    type: 'string',
                    default: '',
                },
                searchPortal: {
                    label: 'Search Portal',
                    type: 'reference',
                    references: 'portal',
                    fieldName: 'searchPortalKey',
                },
                searchPage: {
                    label: 'Search Page',
                    type: 'string',
                    default: '',
                },
                inlinePortal: {
                    label: 'Inline Assistant Portal',
                    type: 'reference',
                    references: 'portal',
                    fieldName: 'inlinePortalKey',
                },
                inlinePage: {
                    label: 'Inline Assistant Page',
                    type: 'string',
                    default: '',
                },
                aiSearchButtonConfig: {
                    label: 'AI Search Button Configuration',
                    description: 'Controls the AI Mode button inside the Shopify search bar.',
                    type: 'json',
                    default: {
                        enabled: false,
                        targetUrl: '/search',
                        buttonText: 'AI Mode',
                    },
                    portalApi: false,
                },
                popupConfig: {
                    label: 'Popup Configuration',
                    type: 'json',
                    default: {
                        borderRadius: 8,
                        heightPercent: 60,
                        maxHeight: 900,
                        maxWidth: 900,
                        minHeight: 400,
                        minWidth: 375,
                        paddingX: 10,
                        paddingY: 10,
                        position: 'bottom-right',
                        shadow: {
                            color: '#9b9b9b',
                            position: 'full',
                            thickness: 2,
                        },
                        widthPercent: 50,
                    },
                    portalApi: false,
                },
                mobilePopupConfig: {
                    label: 'Popup Configuration',
                    type: 'json',
                    default: {
                        borderRadius: 0,
                        heightPercent: 80,
                        maxHeight: 900,
                        maxWidth: 900,
                        minHeight: 400,
                        minWidth: 350,
                        paddingX: 10,
                        paddingY: 20,
                        position: 'bottom-center',
                        shadow: {
                            color: '#9b9b9b',
                            position: 'full',
                            thickness: 0,
                        },
                        widthPercent: 96,
                    },
                    portalApi: false,
                },
                minimizedConfig: {
                    label: 'Minimized Configuration',
                    type: 'json',
                    default: {
                        height: 60,
                        paddingX: 10,
                        paddingY: 10,
                        position: 'bottom-right',
                        width: 160,
                    },
                    portalApi: false,
                },
                customInitCode: {
                    label: 'Custom Initialization Code',
                    type: 'string',
                    default: '',
                    multiline: true,
                    rows: 12,
                    portalApi: false,
                },
                customCartUpdateCode: {
                    label: 'Custom Cart Update Code',
                    type: 'string',
                    default: '',
                    multiline: true,
                    rows: 12,
                    portalApi: false,
                },
                customProductDescriptionSanitizerCode: {
                    label: 'Custom Product Description Sanitizer Code',
                    description: 'Runs before product data is sent to the widget. Provide a single expression using `description` and `productData`.',
                    type: 'string',
                    default: '',
                    multiline: true,
                    rows: 12,
                    portalApi: false,
                },
                autoOpenConfig: {
                    label: 'Auto Open Configuration',
                    type: 'json',
                    default: {
                        enabled: false,
                        openDelay: 30,
                        newCustomerOnly: false,
                    },
                    portalApi: false,
                },
                inlineWidgetWidth: {
                    label: 'Inline Assistant Width',
                    description: 'Inline width for the Inline Assistant (for example: 100%, 75%, 1200px, 1200). Leave empty to use full available width.',
                    type: 'string',
                    default: null,
                    portalApi: false,
                },
                inlineMinHeight: {
                    label: 'Inline Assistant Min Height',
                    description: 'Initial inline height for the Inline Assistant (for example: 440px, 30vh, 440).',
                    type: 'string',
                    default: '440px',
                    portalApi: false,
                },
                inlineMaxHeight: {
                    label: 'Inline Assistant Max Height',
                    description: 'Maximum inline height for the Inline Assistant (for example: 700px, 75vh, 700). Use none, unbounded, or unlimited for no cap so the iframe grows with content and the store page scrolls.',
                    type: 'string',
                    default: '700px',
                    portalApi: false,
                },
                initialHeight: {
                    label: 'Initial Height',
                    description: 'Initial height of the search container in pixels. Used for dual mode containers.',
                    type: 'number',
                    default: 500,
                    min: 100,
                    max: 2000,
                    group: 'search',
                },
                autoMinimizeOnBlur: {
                    label: 'Auto Minimize on Blur',
                    type: 'boolean',
                    default: false,
                    portalApi: false,
                },
                disableConversationPersistence: {
                    label: 'Disable Conversation Persistence',
                    description: 'When enabled, conversation snapshots are not stored in browser localStorage. Each refresh or page change starts a new conversation.',
                    type: 'boolean',
                    default: false,
                    portalApi: false,
                },
                searchOverrides: {
                    label: 'Search Overrides',
                    description: 'Search mode specific prompt overrides. These take highest priority in search mode.',
                    type: 'json',
                    default: {},
                    portalApi: false,
                },
            },
            required: ['id', 'name'],
        },
        course: {
            name: 'Course',
            label: 'Course',
            description: 'A course is a structured collection of content on a specific topic.',
            type: 'entity',
            formConfig: [
                {
                    name: 'tabs',
                    type: 'tabs',
                    elements: [
                        {
                            name: 'general',
                            type: 'group',
                            label: 'Details',
                            elements: [
                                'name',
                                'headline',
                                'description',
                                'objectives',
                                'courseImage',
                                'tags',
                                'keywords',
                                'level',
                                'instructors',
                                'length',
                                'companyName',
                                'format',
                                'enableAutoplay',
                                {
                                    name: 'slug',
                                    label: 'Slug',
                                    type: 'subGroup',
                                    elements: [
                                        {
                                            name: 'manualSlug',
                                            type: 'guardedOption',
                                            controlledBy: 'manualSlug',
                                            controlledField: 'slug',
                                            label: 'Override Slug.',
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            name: 'advancedOptions',
                            type: 'group',
                            label: 'Advanced Options',
                            elements: [
                                { type: 'component', name: 'CourseAlertConfig', label: 'Alerts Email' },
                                'libraries',
                                'searchable',
                                'ogImage',
                                {
                                    name: 'aiResults',
                                    label: 'AI Results',
                                    type: 'subGroup',
                                    elements: ['doNotIndex', 'doNotReference', { type: 'component', name: 'IndexedTextPreview' }],
                                },
                            ],
                        },
                        {
                            name: 'commerce',
                            type: 'group',
                            label: 'Commerce',
                            elements: [
                                {
                                    name: 'commerceDefault',
                                    type: 'subGroup',
                                },
                                {
                                    name: 'cta',
                                    label: 'Call to Action (CTA)',
                                    type: 'subGroup',
                                },
                                'kicker',
                                {
                                    name: 'access',
                                    label: 'Access Rules For Course',
                                    type: 'subGroup',
                                },
                                'confirmationMessage',
                                {
                                    name: 'promoVideo',
                                    label: 'Promo Video',
                                    type: 'subGroup',
                                },
                            ],
                        },
                        {
                            name: 'publishDetails',
                            type: 'group',
                            label: 'Publish Details',
                        },
                        {
                            name: 'attributes',
                            type: 'group',
                            label: 'Attributes',
                        },
                        {
                            name: 'discussion',
                            type: 'group',
                            label: 'Discussion',
                        },
                        {
                            name: 'prerequisites',
                            label: 'Prerequisites',
                            type: 'group',
                            elements: [
                                'prerequisiteCertificates',
                                'prerequisiteMessage',
                            ],
                        },
                        {
                            name: 'KnowledgeBase',
                            label: 'Knowledge Base',
                            type: 'component',
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                    group: null,
                    subGroup: null,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                slug: {
                    label: 'Slug',
                    type: 'string',
                    validation: 'slug',
                    group: null,
                    searchable: true,
                },
                manualSlug: {
                    type: 'boolean',
                    group: null,
                    portalApi: false,
                },
                libraries: {
                    label: 'Libraries',
                    type: 'referenceArray',
                    subtype: 'chips',
                    references: 'library',
                    fieldName: 'libraries',
                    group: 'advancedOptions',
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                externalID: {
                    label: 'External ID',
                    type: 'string',
                    group: 'advanced',
                    description: 'A reference to this assets ID in an external system such as a CMS.  Typically used in import processes.',
                    default: '',
                },
                name: {
                    label: 'Course Name',
                    placeholder: 'Add course name',
                    type: 'string',
                    minLength: 8,
                    maxLength: 80,
                    group: 'general',
                    searchable: true,
                },
                headline: {
                    label: 'Headline',
                    placeholder: 'Add headline',
                    type: 'string',
                    maxLength: 500,
                    wordRequired: true,
                    group: 'general',
                    searchable: true,
                },
                description: {
                    label: 'Description',
                    placeholder: 'Add description...',
                    type: 'string',
                    multiline: true,
                    aiTarget: 'course',
                    rows: 6,
                    maxLength: 4000,
                    group: 'general',
                    resize: true,
                    searchable: true,
                },
                objectives: {
                    label: 'Learning Objectives',
                    placeholder: 'Add Learning Objective...',
                    aiTarget: 'course',
                    type: 'string',
                    subtype: 'bulletPoints',
                    group: 'general',
                },
                courseImage: {
                    label: 'Course Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    fieldName: 'courseImage', // Change this out to be courseImageId?
                    group: 'general',
                },
                tags: {
                    label: 'Course Tags',
                    type: 'string',
                    placeholder: 'Add course tags...',
                    subtype: 'chips',
                    enum: ['courseTag1', 'courseTag2', 'courseTag3'],
                    enumLabels: ['Course Tag 1', 'Course Tag 2', 'Course Tag 3'],
                    maxLength: 500,
                    group: 'general',
                    searchable: true,
                },
                keywords: {
                    label: 'Keywords',
                    type: 'string',
                    placeholder: 'Add keywords...',
                    subtype: 'chips',
                    maxLength: 500,
                    group: 'general',
                    searchable: true,
                },
                level: {
                    label: 'Level',
                    type: 'string',
                    default: 'beginner',
                    enum: ['beginner', 'intermediate', 'advanced', 'all'],
                    enumLabels: ['Beginner', 'Intermediate', 'Advanced', 'All Levels'],
                    group: 'general',
                    searchable: true,
                },
                instructors: {
                    label: 'Instructors',
                    type: 'referenceArray',
                    subtype: 'chips',
                    references: 'instructor',
                    fieldName: 'instructors',
                    group: 'general',
                },
                searchInstructors: {
                    label: 'Search Instructors',
                    type: 'string',
                    searchable: true,
                    group: null,
                    portalApi: false,
                },
                // sharePassword: {
                //   label: 'Share Password',
                //   type: 'string',
                //   maxLength: 500,
                //   group: 'general',
                // },
                searchable: {
                    label: 'Searchable',
                    type: 'boolean',
                    default: true,
                    group: 'advancedOptions',
                },
                // autoplay: {
                //   label: 'Autoplay',
                //   type: 'boolean',
                //   default: true,
                //   group: 'advancedOptions',
                // },
                // "forSale": {
                //   "label": "Avaliable for sale",
                //   "type": "boolean",
                //   "default": true,
                //   "enum": [
                //     false,
                //     true,
                //   ],
                //   "enumLabels": [
                //     'Not avaliable for sale',
                //     'Not avaliable as a stand-alone course',
                //   ],
                //   "group": "advanced",
                //   "subGroup": "options",
                // },
                gated: {
                    label: 'Gated',
                    type: 'boolean',
                    default: false,
                    description: 'Gated courses require lessons be completed in order.',
                    enum: [
                        true,
                        false,
                    ],
                    enumLabels: [
                        'Gated (Learning content must be completed in sequence',
                        'Ungated (User may complete learning content in any order',
                    ],
                    // Hide for now
                    // group: 'advancedOptions',
                    group: null,
                },
                startDate: {
                    label: 'Active Dates',
                    type: 'date',
                    enum: ['startDate', 'expiryDate'],
                    enumLabels: ['Start Date', 'Expiry Date'],
                    // Hide for now
                    // group: 'advancedOptions',
                    group: null,
                },
                expiryDate: {
                    label: 'Expiry Date',
                    type: 'date',
                    group: null,
                    subGroup: null,
                },
                // requiredSkills: {
                //   label: 'Skills needed',
                //   type: 'string',
                //   placeholder: 'Add required skills...',
                //   subtype: 'chips',
                //   enum: ['researcher', 'technicalSEO', 'contentWriting'],
                //   enumLabels: ['Researcher', 'Technical SEO', 'Content Writing'],
                //   maxLength: 500,
                //   group: 'advancedOptions',
                //   searchable: true,
                // },
                // audience: {
                //   label: 'Who Is This Course For',
                //   type: 'string',
                //   maxLength: 500,
                //   group: 'advancedOptions',
                // },
                confirmationMessage: {
                    label: 'Confirmation Page Message',
                    type: 'richtext',
                    maxLength: 2000,
                    group: 'commerce',
                    portalApi: false,
                },
                // welcomeMessage: {
                //   label: 'Welcome Message',
                //   type: 'richtext',
                //   maxLength: 2000,
                //   group: 'advancedOptions',
                //   portalApi: false,
                // },
                // congratulatoryMessage: {
                //   label: 'Congratulatory Message',
                //   type: 'richtext',
                //   maxLength: 2000,
                //   group: 'advancedOptions',
                //   portalApi: false,
                // },
                length: {
                    label: 'Course Length',
                    type: 'string',
                    subtype: 'duration',
                    group: 'general',
                },
                enrollmentCount: {
                    label: 'Students Enrolled',
                    type: 'number',
                    default: 0,
                    group: null,
                    subGroup: null,
                },
                sections: {
                    label: 'Sections',
                    type: 'array', // these is a contained array - items are stored directly
                    references: 'courseSection',
                    fieldName: 'sections',
                    group: null,
                    subGroup: null,
                },
                // i think this should be removed?
                // courseImageThumbnail: {
                //   label: 'Course Image Thumbnail',
                //   type: 'reference',
                //   references: 'asset',
                //   assetType: 'image',
                //   group: null,
                //   subGroup: null,
                //   portalApi: false,
                // },
                currency: {
                    label: 'Currency',
                    type: 'string',
                    default: 'USD',
                    enum: ['USD', 'EUR', 'JPY', 'GBP', 'CAD'],
                    enumLabels: ['USD', 'EUR', 'JPY', 'GBP', 'CAD'],
                    group: null, // hide until implemented 'commerce',
                    subGroup: null,
                },
                price: {
                    label: 'Price',
                    type: 'currency',
                    default: '0',
                    group: 'commerceDefault',
                },
                sellPrice: {
                    label: 'Sell Price',
                    type: 'currency',
                    default: '0',
                    group: 'commerceDefault',
                },
                posterImage: {
                    label: 'Poster Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    fieldName: 'posterImage',
                    group: 'promoVideo',
                },
                promoVideo: {
                    label: 'Promo Video',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'video',
                    group: 'promoVideo',
                },
                discountPercent: {
                    label: 'Discount',
                    type: 'number',
                    default: 0,
                    group: null, // hide until implemented 'commerce',
                    subGroup: null,
                },
                avgRating: {
                    label: 'Ratings',
                    type: 'number',
                    default: 0,
                    group: null,
                    subGroup: null,
                },
                // "badges": {
                //   "label": "Badges",
                //   "type": "array",
                //   "group": null,
                // },
                lessonCount: {
                    label: 'Course Lesson Count',
                    type: 'number',
                    default: 0,
                },
                companyName: {
                    label: 'Company Name',
                    placeholder: 'Add company name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                    group: 'general',
                    searchable: true,
                },
                reviews: {
                    label: 'Reviews',
                    type: 'array', // these is a contained array - items are stored directly
                    references: 'review',
                    fieldName: 'reviews',
                    group: null, // hide until implemented 'commerce',
                },
                faqs: {
                    label: 'FAQs',
                    type: 'array', // these is a contained array - items are stored directly
                    references: 'faq',
                    fieldName: 'faqs',
                    group: null, // hide until implemented 'commerce',
                },
                /// ///////////////////////////////////////////////////////////////
                // "structure": {
                //   "label": "Structure",
                //   "type": "json",
                // },
                // "sections": {
                //   "label": "Sections",
                //   "type": "dependant",
                // },
                // "certificate": {
                //   "label": "Course Certificate",
                //   "type": "reference",
                //   "references": "certificate",
                // },
                // certification: {
                //   label: 'Certification',
                //   type: 'reference',
                //   references: 'certification',
                //   fieldName: 'certification', // Change this out to be courseImageId?
                //   // group: 'commerce',
                //   // subGroup: null,
                // },
                active: {
                    label: 'Active',
                    type: 'boolean',
                    default: true,
                    group: 'publishDetails',
                    placeholder: 'Course is enabled and available for access by customers.',
                },
                ctaUrl: {
                    label: 'CTA URL',
                    type: 'string',
                    group: 'cta',
                    placeholder: 'Enter full url...',
                    description: 'Include the protocol (http:// or https://) as part of the url entered.',
                },
                ctaLabel: {
                    label: 'Label',
                    type: 'string',
                    group: 'cta',
                    placeholder: 'Call To Action label...',
                },
                ctaNewWindow: {
                    label: 'New window',
                    type: 'boolean',
                    default: true,
                    group: 'cta',
                    placeholder: 'Open Call To Action in a new window?',
                },
                accessConfiguration: {
                    label: 'Access Configuration',
                    type: 'string',
                    enum: ['anonymous', 'loggedIn', 'accessGroups'],
                    enumLabels: ['Anonymous', 'Enroll', 'Access Groups'],
                    default: 'accessGroups',
                    group: 'access',
                },
                excludeFromAllCourses: {
                    label: 'Exclude from All Courses memberships',
                    type: 'boolean',
                    default: false,
                    group: 'access',
                    description: 'When checked access to this course must be granted via a specific access group.  The All Courses group does not grant access.',
                },
                accessGroups: {
                    label: 'Access Groups',
                    type: 'accessGroups',
                    group: 'access',
                },
                kicker: {
                    label: 'Kicker',
                    type: 'reference',
                    references: 'kicker',
                    fieldName: 'portalKicker',
                    group: 'commerce',
                },
                prerequisiteMessage: {
                    label: 'Prerequisite Error Message',
                    placeholder: 'Enter a message to display for unmet prerequisites.',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    description: 'Rich Text message shown to users who do not meet the course prerequisites.',
                    group: 'prerequisites',
                },
                prerequisiteCertificates: {
                    label: 'Prerequisites',
                    type: 'array',
                    references: 'prerequisite',
                    subtype: 'prerequisite',
                    group: 'prerequisites',
                },
                richText1: {
                    label: 'Rich Text 1',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText1Label',
                },
                richText2: {
                    label: 'Rich Text 2',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText2Label',
                },
                richText3: {
                    label: 'Rich Text 3',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText3Label',
                },
                richText4: {
                    label: 'Rich Text 4',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText4Label',
                },
                richText5: {
                    label: 'Rich Text 5',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText5Label',
                },
                richText6: {
                    label: 'Rich Text 6',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText6Label',
                },
                richText7: {
                    label: 'Rich Text 7',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText7Label',
                },
                richText8: {
                    label: 'Rich Text 8',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText8Label',
                },
                richText9: {
                    label: 'Rich Text 9',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText9Label',
                },
                richText10: {
                    label: 'Rich Text 10',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText10Label',
                },
                discussionTopic: {
                    label: 'Topic',
                    type: 'reference',
                    references: 'discussionTopic',
                    fieldName: 'discussionTopic',
                    group: 'discussion',
                },
                aiWizardContent: {
                    label: 'AI Wizard Content',
                    placeholder: 'Structured data for interacting with the AI engine.',
                    type: 'json',
                    defaultValue: {
                        ideation: {
                            objective: '',
                        },
                    },
                    subtype: 'aiWizardContent',
                    portalApi: false,
                },
                format: {
                    label: 'Format',
                    placeholder: 'Course Format',
                    type: 'string',
                    enum: ['standard', 'scorm12'],
                    enumLabels: ['Standard', 'SCORM 1.2'],
                    default: 'standard',
                    // readOnly: true,
                    group: 'general',
                    searchable: true,
                },
                doNotIndex: {
                    label: 'Do Not Index',
                    type: 'boolean',
                    default: false,
                    group: 'aiResults',
                    description: 'Do not include the content from this course in responses.',
                    portalApi: false,
                },
                doNotReference: {
                    label: 'Do Not Reference',
                    type: 'boolean',
                    default: false,
                    group: 'aiResults',
                    description: 'Do not display this course in a AI results, but include its contents as source material when generating response.',
                    portalApi: false,
                },
                ogImage: {
                    label: 'OG Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    group: 'advancedOptions',
                },
                enableAutoplay: {
                    label: 'Enable Autoplay',
                    type: 'boolean',
                    default: false,
                    group: 'general',
                    description: 'Enable autoplay for the course.',
                    portalApi: true,
                },
            },
            required: ['id', 'name'],
        },
        courseItem: {
            name: 'CourseItem',
            label: 'Course Item',
            description: 'A piece of learning course that is self contained and can be presented to the customer.',
            type: 'dependant',
            formConfig: [
                {
                    name: 'cta',
                    label: 'Call to Action (CTA)',
                },
                {
                    name: 'certificationConfig',
                    label: 'Certification Configuration',
                },
                {
                    name: 'certificationDisplay',
                    label: 'Certification Display',
                },
                {
                    name: 'certificationMessage',
                    label: 'Certification Message',
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true, // defaults to true
                    primaryKey: true,
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                    default: '',
                    nullable: true,
                },
                getAlerts: {
                    label: 'Get Alerts',
                    type: 'boolean',
                    default: false,
                    description: 'Send a completion alert for this item.',
                },
                headline: {
                    label: 'Headline',
                    type: 'string',
                    minLength: 3,
                    maxLength: 500,
                    default: '',
                },
                url: {
                    label: 'URL',
                    type: 'string',
                    default: '',
                },
                asset: {
                    label: 'Asset',
                    type: 'reference',
                    references: 'asset',
                    fieldName: 'assetID', // overrides the name in the backend datasource
                },
                posterImage: {
                    label: 'Poster Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    fieldName: 'posterImage', // Change this out to be posterImageId?
                },
                type: {
                    label: 'Type',
                    type: 'string',
                    default: 'video',
                    enum: ['block', 'video', 'audio', 'quiz', 'article', 'download', 'knowledgeCheck', 'certification', 'typeform'],
                    enumLabels: ['Block', 'Video', 'Audio', 'Quiz', 'Article', 'Download', 'Knowledge Check', 'Certification', 'Typeform'],
                },
                // introContent: {
                //   label: 'Intro Content',
                //   type: 'richtext',
                //   maxLength: 20000,
                // },
                content: {
                    label: 'Content',
                    type: 'richtext',
                    aiTarget: 'asset',
                    maxLength: 20000,
                },
                required: {
                    label: 'Required Lesson',
                    description: 'Customer must view/read/watch the entire lesson for it to be marked complete. Skipping forward is disabled.',
                    type: 'boolean',
                    group: 'details',
                    default: false,
                },
                instructor: {
                    label: 'Instructor',
                    type: 'reference',
                    references: 'instructor',
                    fieldName: 'instructor', // overrides the name in the backend datasource
                    // "readOnly": false // defaults to true
                },
                level: {
                    label: 'Content Level',
                    type: 'string',
                    default: 'beginner',
                    enum: ['beginner', 'intermediate', 'advanced', 'all'],
                    enumLabels: ['Beginner', 'Intermediate', 'Advanced', 'All Levels'],
                },
                length: {
                    label: 'Content Length',
                    type: 'string',
                    default: '',
                },
                ctaUrl: {
                    label: 'CTA URL',
                    type: 'string',
                    group: 'cta',
                    placeholder: 'Enter full url...',
                    description: 'Include the protocol (http:// or https://) as part of the url entered.',
                },
                ctaLabel: {
                    label: 'CTA Label',
                    type: 'string',
                    group: 'cta',
                    placeholder: 'CTA label...',
                },
                ctaNewWindow: {
                    label: 'CTA New window',
                    type: 'boolean',
                    default: true,
                    group: 'cta',
                    placeholder: 'Open page in new window?',
                },
                kicker: {
                    label: 'Kicker',
                    type: 'reference',
                    references: 'kicker',
                    fieldName: 'portalKicker',
                    group: 'general',
                },
                questions: {
                    label: 'Questions',
                    type: 'json', // could be an array or json - not sure which is best
                    defaultValue: [],
                    subtype: 'quiz', // not sure this is needed
                    aiTarget: 'course',
                },
                instructions: {
                    label: 'Instructions',
                    type: 'richtext',
                    maxLength: 2000,
                },
                postQuizMessage: {
                    label: 'Post Quiz Message',
                    type: 'richtext',
                    maxLength: 2000,
                },
                showScoreOnCompletion: {
                    label: 'Show Score',
                    type: 'boolean',
                    default: true,
                },
                passingPercentage: {
                    label: 'Passing Percentage',
                    type: 'number',
                    default: 30,
                },
                limitRetakes: {
                    label: 'Limit Retakes',
                    type: 'boolean',
                    default: false,
                },
                maximumRetakes: {
                    label: 'Maximum Retakes',
                    type: 'number',
                    default: 0,
                    minValue: 0,
                    maxValue: 100,
                },
                passedMessage: {
                    label: 'Passed Message',
                    type: 'string',
                    default: '',
                    maxLength: 200,
                },
                failedMessage: {
                    label: 'Failed Message',
                    type: 'string',
                    default: '',
                    maxLength: 200,
                },
                timeLimitEnabled: {
                    label: 'Time Limit Enabled',
                    type: 'boolean',
                    default: false,
                },
                timeLimitForQuiz: {
                    label: 'Time Limit For Quiz (in minutes)',
                    type: 'number',
                    default: 0,
                },
                randomizeQuestionOrder: {
                    label: 'Randomize Question Order',
                    type: 'boolean',
                    default: false,
                },
                noOfQuestionsToAsk: {
                    label: 'Number Of Questions To ask',
                    type: 'number',
                    default: 0,
                    description: 'If set to 0 all questions are asked.',
                },
                microcast: {
                    label: 'Microcast',
                    type: 'reference',
                    references: 'microcast',
                    fieldName: 'microcastID',
                },
                certification: {
                    label: 'Certification',
                    type: 'reference',
                    references: 'certification',
                    group: 'certificationConfig',
                    fieldName: 'certificationID',
                },
                completionCriteria: {
                    label: 'Criteria',
                    type: 'json',
                    subtype: 'courseCertificateCriteria',
                    default: {
                        percentage: '100',
                        active: false,
                    },
                    group: 'certificationConfig',
                    placeholder: '100% of a Course complete.',
                },
                quizScoreCriteria: {
                    label: 'Criteria',
                    type: 'json',
                    subtype: 'quizCertificateCriteria',
                    default: {
                        active: false,
                        quizID: 0,
                    },
                    group: 'certificationConfig',
                    placeholder: 'Scores 80% or higher on Final Quiz.',
                },
                bothCriteria: {
                    label: 'Criteria',
                    type: 'boolean',
                    default: false,
                    group: 'certificationConfig',
                    placeholder: 'Must meet BOTH criteria selected to obtain the certificate.',
                },
                emailCertificate: {
                    label: 'Email',
                    type: 'boolean',
                    default: true,
                    group: 'certificationConfig',
                    placeholder: 'Email customer certificate once they qualify.',
                },
                hidden: {
                    label: 'Hide Lesson',
                    type: 'boolean',
                    default: false,
                    group: 'certificationConfig',
                    placeholder: 'If enabled, this lesson will be hidden from the Lesson Bar and Course Outline.',
                },
                certificateImage: {
                    label: 'Certificate Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    fieldName: 'certificateImage',
                    group: 'certificationDisplay',
                },
                certificateDescription: {
                    label: 'Certificate Description',
                    placeholder: 'Add description on certificate',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    searchable: true,
                    group: 'certificationDisplay',
                },
                certificateMessage: {
                    label: 'Certificate Message',
                    placeholder: 'Add message on certificate',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    searchable: true,
                    group: 'certificationMessage',
                },
                showAnswers: {
                    label: 'Show Answers',
                    description: 'Show user correct answer after each question is submitted during quiz.',
                    type: 'boolean',
                    group: 'details',
                    default: true,
                },
                typeformId: {
                    label: 'Typeform ID',
                    description: 'External ID for Typeform Lesson',
                    type: 'string',
                    default: '',
                    maxLength: 200,
                },
                emailTemplate: {
                    label: 'Certificate Email',
                    type: 'reference',
                    references: 'emailTemplate',
                    assetType: 'emailTemplate',
                    fieldName: 'emailTemplateID',
                    group: 'details',
                    portalApi: false,
                },
            },
        },
        courseProgress: {
            name: 'Course Progress',
            label: 'Course Progress',
            pluralTypeName: 'CourseProgresses',
            description: 'A Course Progress tracks the progress of a customer on a course.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                    group: null,
                    subGroup: null,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                    portalApi: false,
                },
                customer: {
                    label: 'Customer',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'customerID',
                    portalApi: false,
                    readOnly: true,
                    primaryKey: true,
                    shortKey: true,
                },
                course: {
                    label: 'Single Course',
                    type: 'reference',
                    references: 'course',
                    fieldName: 'courseID',
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    // readOnly: true,
                },
                progress: {
                    label: 'Progress in %',
                    type: 'number',
                    group: 'null',
                    default: 0,
                    readOnly: true,
                },
                startedDate: {
                    label: 'Course Start Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                completeDate: {
                    label: 'Course Completed Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                lastActiveDate: {
                    label: 'Last active date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                expiryDate: {
                    label: 'Expiry date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                lessonProgress: {
                    label: 'Lesson Progress',
                    type: 'json',
                    defaultValue: {},
                },
                tracking: {
                    label: 'Session Tracking',
                    description: 'Stores time tracking data across multiple course sessions.',
                    type: 'json',
                    readOnly: true,
                    portalApi: false,
                    defaultValue: {
                        totalTime: 0,
                        session: [],
                    },
                },
                quizResults: {
                    label: 'Quiz Results',
                    type: 'json', // could be an array or json - not sure which is best
                    defaultValue: {},
                },
                notes: {
                    label: 'Notes',
                    type: 'json', // could be an array or json - not sure which is best
                    defaultValue: [],
                },
                hasCertification: {
                    label: 'Has Certification',
                    type: 'boolean',
                    default: false,
                    readOnly: true,
                },
                certificateAwarded: {
                    label: 'Certificate Awarded',
                    type: 'number',
                    default: 0,
                    readOnly: true,
                },
                certificates: {
                    label: 'Certificates',
                    type: 'referenceArray',
                    references: 'certificate',
                    fieldName: 'certificates',
                    default: [],
                    readOnly: true,
                },
                certifications: {
                    label: 'Certifications',
                    type: 'referenceArray',
                    references: 'certification',
                    fieldName: 'certifications',
                    default: [],
                    readOnly: true,
                },
                completedCertifications: {
                    label: 'Complted Certifications',
                    type: 'referenceArray',
                    references: 'certification',
                    fieldName: 'completedCertifications',
                    default: [],
                    readOnly: true,
                },
                certificateStatus: {
                    label: 'CertificationStatus',
                    type: 'string',
                    fieldName: 'certificateStatus',
                    default: '',
                    readOnly: true,
                },
                certificateScore: {
                    label: 'Certification Score %',
                    type: 'number',
                    group: 'null',
                    default: 0,
                    fieldName: 'certificateScore',
                    readOnly: true,
                },
                certificateAwardedAt: {
                    label: 'Certification Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                scormData: {
                    label: 'SCORM Data',
                    type: 'json',
                    defaultValue: false,
                },
            },
            required: ['id', 'organizationID', 'customerID', 'courseID'],
        },
        crawlConfig: {
            name: 'crawlConfig',
            label: 'Crawl Configuration',
            description: 'Defines the setting for the web crawler.',
            type: 'dependant',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                    default: '',
                },
                description: {
                    label: 'Description',
                    type: 'string',
                    maxLength: 500,
                },
                // can't directly reference the libraries so just storing these as IDs
                libraries: {
                    label: 'Libraries',
                    type: 'json',
                    fieldName: 'libraries',
                    default: [],
                    references: 'library',
                },
                startingUrl: {
                    label: 'Starting URL',
                    type: 'string',
                    default: '',
                },
                includeRules: {
                    label: 'Include Rules',
                    type: 'string',
                    subtype: 'bulletPoints',
                    default: '',
                    description: `URL pathname regex patterns that include matching URLs in the crawl. 
          Only the paths that match the specified patterns will be included in the response. 
          For example, if you set Include Rules to: blog/.* for the base URL firecrawl.dev, 
          only results matching that pattern will be included, 
          such as https://www.firecrawl.dev/blog/firecrawl-launch-week-1-recap.`,
                },
                excludeRules: {
                    label: 'Exclude Rules',
                    type: 'string',
                    subtype: 'bulletPoints',
                    default: '',
                    descriptions: `URL pathname regex patterns that exclude matching URLs from the crawl. 
          For example, if you set Exclude Rules: blog/.* for the base URL firecrawl.dev, 
          any results matching that pattern will be excluded, 
          such as https://www.firecrawl.dev/blog/firecrawl-launch-week-1-recap.`,
                },
                maxPages: {
                    label: 'Maximum Number of Pages',
                    type: 'number',
                    default: 10,
                },
                status: {
                    label: 'Status',
                    type: 'string',
                    readOnly: true,
                    enum: ['active', 'crawling', 'processing', 'disabled', 'completed'],
                    enumLabels: ['Active', 'Crawling', 'Processing', 'Disabled', 'Completed'],
                },
                jobID: {
                    label: 'Job ID',
                    type: 'string',
                    readOnly: true,
                    default: '',
                },
                pageCount: {
                    label: 'Page Count',
                    type: 'number',
                    readOnly: true,
                    default: 0,
                },
                processedCount: {
                    label: 'Processed Count',
                    type: 'number',
                    readOnly: true,
                    default: 0,
                },
                lastStatus: {
                    label: 'Last Status',
                    type: 'json',
                    readOnly: true,
                    default: {},
                },
                doNotReference: {
                    label: 'Do Not Reference',
                    type: 'boolean',
                    default: false,
                    description: 'When enabled, pages from this crawl will not be referenced in AI responses.',
                },
            },
            required: [],
        },
        customer: {
            name: 'Customer',
            label: 'Customer',
            description: 'The end-users of the application.  Each customer belongs to one organization.',
            type: 'entity',
            formConfig: [
                'email', 'temporaryPassword', 'name', 'phoneNumber', 'hub', 'profileImage',
                'biography', 'background', 'title', 'headline',
                'text1', 'text2', 'text3', 'text4', 'text5',
                'internalNotes', 'stripeCustomerID',
                'activeMembershipCount', 'trialMembershipCount', 'overdueMembershipCount', 'expiredMembershipCount',
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    primaryKey: true,
                    readOnly: true,
                    portalApi: false,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                    portalApi: false,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                email: {
                    label: 'Email',
                    type: 'string',
                    subtype: 'email',
                    portalApi: false,
                    group: 'general',
                    searchable: true,
                },
                temporaryPassword: {
                    label: 'Temporary Password',
                    description: 'Minimum 10 characters and must include one lower case letter, one upper case letter, one number, and one special character',
                    type: 'string',
                    portalApi: false,
                    group: 'general',
                    validation: 'password',
                    minLength: 8,
                    maxLength: 99,
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    group: 'general',
                    minLength: 3,
                    maxLength: 90,
                    searchable: true,
                },
                phoneNumber: {
                    label: 'Phone Number',
                    type: 'string',
                    subtype: 'phoneNumber',
                    group: 'general',
                    searchable: true,
                },
                hub: {
                    label: 'Hub',
                    type: 'reference',
                    references: 'hub',
                    fieldName: 'hubID',
                    group: 'general',
                },
                biography: {
                    label: 'Biography',
                    type: 'string',
                    minLength: 3,
                    maxLength: 5000,
                    rows: 10,
                    keepLineBreaks: true,
                    portalApi: false,
                    group: 'general',
                },
                profileImage: {
                    label: 'Profile Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    group: 'general',
                },
                background: {
                    label: 'Background',
                    type: 'string',
                    group: 'general',
                },
                title: {
                    label: 'Title',
                    type: 'string',
                    group: 'general',
                },
                headline: {
                    label: 'Headline',
                    type: 'string',
                    group: 'general',
                },
                internalNotes: {
                    label: 'Internal Customer Notes',
                    type: 'string',
                    group: 'general',
                    minLength: 3,
                    maxLength: 5000,
                    multiline: true,
                    rows: 10,
                    portalApi: false,
                },
                members: {
                    label: 'Memberships',
                    type: 'join',
                    fieldName: 'members',
                    references: 'member',
                    group: 'memberships',
                },
                stripeCustomerID: {
                    label: 'Stripe Customer ID',
                    type: 'string',
                    portalApi: false,
                    group: 'general',
                },
                lastPaymentAt: {
                    label: 'Last Payment',
                    type: 'date',
                    group: 'general',
                    readOnly: true,
                },
                nextPaymentAt: {
                    label: 'Last Payment',
                    type: 'date',
                    group: 'general',
                    readOnly: true,
                },
                activeMembershipCount: {
                    label: 'Active Memberships',
                    type: 'number',
                    group: 'general',
                    readOnly: true,
                },
                trialMembershipCount: {
                    label: 'Trial Memberships',
                    type: 'number',
                    group: 'general',
                    readOnly: true,
                },
                overdueMembershipCount: {
                    label: 'Overdue Memberships',
                    type: 'number',
                    group: 'general',
                    readOnly: true,
                },
                expiredMembershipCount: {
                    label: 'Expired Memberships',
                    type: 'number',
                    group: 'general',
                    readOnly: true,
                },
                learningBoards: {
                    label: 'Learning Boards',
                    type: 'join',
                    fieldName: 'learningBoards',
                    references: 'learningBoard',
                    group: 'learningBoard',
                },
                text1: {
                    label: 'Text 1',
                    placeholder: 'Add Text',
                    type: 'string',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'customerText1Label',
                },
                text2: {
                    label: 'Text 2',
                    placeholder: 'Add Text',
                    type: 'string',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'customerText2Label',
                },
                text3: {
                    label: 'Text 3',
                    placeholder: 'Add Text',
                    type: 'string',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'customerText3Label',
                },
                text4: {
                    label: 'Text 4',
                    placeholder: 'Add Rich Text',
                    type: 'string',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'customerText4Label',
                },
                text5: {
                    label: 'Text 5',
                    placeholder: 'Add Text',
                    type: 'string',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'customerText5Label',
                },
                status: {
                    label: 'Status',
                    type: 'string',
                    default: 'ACTIVE',
                    enum: ['PENDING', 'ACTIVE', 'SUSPENDED'],
                    enumLabels: ['Pending', 'Active', 'Suspended'],
                    portalApi: false,
                },
            },
            required: ['id'],
        },
        courseSection: {
            name: 'CourseSection',
            label: 'Section',
            description: 'A part of a course that can contain a lesson and/or sub sections for organization.',
            type: 'dependant',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                    default: '',
                },
                getAlerts: {
                    label: 'Get Alerts',
                    type: 'boolean',
                    default: false,
                    description: 'Send an alert when all visible, eligible descendants are complete.',
                },
                description: {
                    label: 'Description',
                    type: 'string',
                    maxLength: 500,
                },
                type: {
                    label: 'Level',
                    type: 'string',
                    default: 'container',
                    enum: ['course', 'microcast', 'block', 'text', 'video', 'audio', 'quiz', 'container', 'knowledgeCheck'],
                    enumLabels: ['course', 'microcast', 'blocks', 'text', 'video', 'audio', 'knowldgeCheck', 'section', 'Knowledge Check'],
                },
                points: {
                    label: 'Points',
                    type: 'json',
                    default: [],
                    portalApi: false,
                },
                script: {
                    label: 'Script',
                    type: 'string',
                    default: '',
                    portalApi: false,
                },
                item: {
                    label: 'Course Item',
                    type: 'dependant',
                    references: 'courseItem',
                },
            },
            required: [],
        },
        // "courseBadge": {
        //   "name": "CourseBadge",
        //   "label": "Badge",
        // eslint-disable-next-line max-len
        //   "description": "A part of a course that can contain badges list about course speciality like bestseller, free, new etc.",
        //   "type": "object",
        //   "properties": {
        //     "id": {
        //       "label": "ID",
        //       "type": "string",
        //       "readOnly": false // defaults to true
        //     },
        //     "Family": {
        //       "label": "Name",
        //       "type": "string",
        //     },
        //     "Text": {
        //       "label": "Text",
        //       "type": "string",
        //     },
        //   },
        //   "required": [],
        // },
        // "courseInstructor": {
        //   "name": "Course Instructor",
        //   "label": "Course Instructor",
        //   "description": "A part of a course which contains information about instructor.",
        //   "type": "object",
        //   "properties": {
        //     "id": {
        //       "label": "ID",
        //       "type": "string",
        //       "readOnly": false // defaults to true
        //     },
        //     "firstName": {
        //       "label": "firstName",
        //       "type": "string",
        //     },
        //     "lastName": {
        //       "label": "lastName",
        //       "type": "string",
        //     },
        //     "asset": {
        //       "label": "Instructor thumbnail",
        //       "type": "reference",
        //       "references": "asset",
        //       "assetType": "image",
        //       "group": null,
        //     }
        //   },
        //   "required": [],
        // },
        discussionPost: {
            name: 'Discussion Post',
            label: 'Discussion Post',
            pluralLabel: 'Discussion Posts',
            description: 'A post (message) by a customer or instructor.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                },
                customer: {
                    label: 'Customer',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'customerID',
                    shortKey: true,
                    portalApi: false,
                },
                user: {
                    label: 'User',
                    type: 'reference',
                    references: 'user',
                    fieldName: 'userID',
                    shortKey: true,
                    portalApi: false,
                },
                approvedBy: {
                    label: 'User',
                    type: 'reference',
                    references: 'user',
                    fieldName: 'approvedByID',
                    shortKey: true,
                    portalApi: false,
                },
                repliesTo: {
                    label: 'repliesTo',
                    type: 'reference',
                    references: 'discussionPost',
                    fieldName: 'repliesTo',
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                topic: {
                    label: 'Topic',
                    type: 'reference',
                    references: 'discussionTopic',
                    fieldName: 'topicID',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 20,
                    maxLength: 50,
                },
                content: {
                    label: 'Content',
                    type: 'string',
                    minLength: 20,
                    maxLength: 500,
                },
                attachment: {
                    label: 'Attachment',
                    type: 'reference',
                    references: 'asset',
                    fieldName: 'attachment',
                },
                status: {
                    label: 'Status',
                    type: 'string',
                    default: 'new',
                    enum: ['new', 'approved', 'rejected'],
                    enumLabels: ['New', 'Approved', 'Rejected'],
                },
            },
            required: ['id', 'name'],
        },
        discussionTopic: {
            name: 'Discussion Topic',
            label: 'Discussion Topic',
            pluralLabel: 'Discussion Topics',
            description: 'A discusssion thread on a particular topic. Can be linked to courses, programs & microcast.',
            type: 'entity',
            formConfig: ['name', 'description', 'approvalRequired' /* , 'oneOnOne' */],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                },
                description: {
                    label: 'Description',
                    type: 'string',
                    minLength: 20,
                    maxLength: 500,
                },
                approvalRequired: {
                    label: 'Approval Required',
                    type: 'boolean',
                    default: false,
                },
                oneOnOne: {
                    label: 'One on One',
                    type: 'boolean',
                    default: false,
                },
            },
            required: ['id', 'name'],
        },
        document: {
            name: 'Document',
            label: 'Document Item',
            description: 'Document (typically PDF, Excel, PowerPoint, etc.) that can be presented to the customer.',
            type: 'entity',
            formConfig: [
                {
                    type: 'tabs',
                    elements: [
                        {
                            name: 'general',
                            type: 'group',
                            label: 'Details',
                            elements: [
                                'name',
                                'publishedAt',
                                'headline',
                                'description',
                                'asset',
                                'image',
                                'content',
                                'tags',
                                'keywords',
                                'instructors',
                                {
                                    name: 'slug',
                                    label: 'Slug',
                                    type: 'subGroup',
                                    elements: [
                                        {
                                            name: 'manualSlug',
                                            type: 'guardedOption',
                                            controlledBy: 'manualSlug',
                                            controlledField: 'slug',
                                            label: 'Override Slug.',
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            name: 'advancedOptions',
                            type: 'group',
                            label: 'Advanced / Commerce',
                            elements: [
                                'libraries',
                                'searchable',
                                {
                                    name: 'cta',
                                    label: 'Call to Action (CTA)',
                                    type: 'subGroup',
                                },
                                {
                                    name: 'advancedDefault',
                                    type: 'group',
                                },
                                'ogImage',
                                {
                                    name: 'aiResults',
                                    label: 'AI Results',
                                    type: 'subGroup',
                                    elements: ['doNotIndex', 'doNotReference', { type: 'component', name: 'IndexedTextPreview' }],
                                },
                            ],
                        },
                        {
                            name: 'access',
                            label: 'Access Rules For Document',
                            type: 'group',
                        },
                        {
                            name: 'discussion',
                            type: 'group',
                            label: 'Discussion',
                        },
                        {
                            name: 'knowledgeBase',
                            label: 'Knowledge Base',
                            type: 'component',
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                    group: null,
                    subGroup: null,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                libraries: {
                    label: 'Libraries',
                    type: 'referenceArray',
                    subtype: 'chips',
                    references: 'library',
                    fieldName: 'libraries',
                    group: 'advancedOptions',
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                name: {
                    label: 'Document Name',
                    placeholder: 'Add document name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                    group: 'general',
                    searchable: true,
                },
                slug: {
                    label: 'Slug',
                    type: 'string',
                    validation: 'slug',
                    group: null,
                    searchable: true,
                },
                manualSlug: {
                    type: 'boolean',
                    group: null,
                    portalApi: false,
                },
                publishedAt: {
                    label: 'Published Date',
                    type: 'date',
                    group: 'general',
                },
                headline: {
                    label: 'Headline',
                    placeholder: 'Add headline',
                    type: 'string',
                    maxLength: 500,
                    wordRequired: true,
                    group: 'general',
                    searchable: true,
                    aiTarget: 'document',
                    promptList: [
                        'Summarize the key points (main points) in one to two paragraphs',
                        'Summarize the key points of this using bullets or a list',
                        'Summarize this document', // created by (insert instructor's names)
                        // 'Summarize the document titled (insert title) created by (insert name)
                    ],
                },
                description: {
                    label: 'Description',
                    placeholder: 'Add description...',
                    type: 'string',
                    aiTarget: 'document',
                    multiline: true,
                    rows: 6,
                    maxLength: 4000,
                    group: 'general',
                    resize: true,
                    searchable: true,
                    promptList: [
                        'Summarize the key points (main points) in one to two paragraphs',
                        'Summarize the key points of this using bullets or a list',
                        'Summarize this document', // created by (insert instructor's names)
                        // 'Summarize the document titled (insert title) created by (insert name)
                    ],
                },
                asset: {
                    label: 'Document Asset',
                    type: 'reference',
                    references: 'asset',
                    fieldName: 'asset',
                    group: 'general',
                },
                image: {
                    label: 'Document Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    fieldName: 'image',
                    group: 'general',
                },
                content: {
                    label: 'Content',
                    type: 'richtext',
                    aiTarget: 'document',
                    maxLength: 20000,
                    group: 'general',
                    promptList: [
                        'Summarize the key points (main points) in one to two paragraphs',
                        'Summarize the key points of this using bullets or a list',
                        'Summarize this document', // created by (insert instructor's names)
                        // 'Summarize the document titled (insert title) created by (insert name)
                    ],
                },
                tags: {
                    label: 'Tags',
                    type: 'string',
                    placeholder: 'Add tags...',
                    subtype: 'chips',
                    maxLength: 500,
                    group: 'general',
                    searchable: true,
                },
                keywords: {
                    label: 'Keywords',
                    type: 'string',
                    placeholder: 'Add keywords...',
                    subtype: 'chips',
                    maxLength: 500,
                    group: 'general',
                    searchable: true,
                },
                // level: {
                //   label: 'Level',
                //   type: 'string',
                //   default: 'beginner',
                //   enum: ['beginner', 'intermediate', 'advanced', 'all'],
                //   enumLabels: ['Beginner', 'Intermediate', 'Advanced', 'All Levels'],
                //   group: 'general',
                //   searchable: true,
                // },
                instructors: {
                    label: 'Instructors',
                    type: 'referenceArray',
                    subtype: 'chips',
                    references: 'instructor',
                    fieldName: 'instructors',
                    group: 'general',
                },
                searchInstructors: {
                    label: 'searchInstructors',
                    type: 'string',
                    searchable: true,
                    group: null,
                    portalApi: false,
                },
                searchable: {
                    label: 'Searchable',
                    type: 'boolean',
                    default: true,
                    group: 'advancedOptions',
                },
                doNotIndex: {
                    label: 'Do Not Index',
                    type: 'boolean',
                    default: false,
                    group: 'aiResults',
                    description: 'Do not include the content from this document in responses.',
                    portalApi: false,
                },
                doNotReference: {
                    label: 'Do Not Reference',
                    type: 'boolean',
                    default: false,
                    group: 'aiResults',
                    description: 'Do not display this document in a AI results, but include its contents as source material when generating response.',
                    portalApi: false,
                },
                active: {
                    label: 'Active',
                    type: 'boolean',
                    default: true,
                    group: 'publishDetails',
                    placeholder: 'Document is enabled and available for access by customers.',
                },
                ctaUrl: {
                    label: 'CTA URL',
                    type: 'string',
                    group: 'cta',
                    placeholder: 'Enter full url...',
                    description: 'Include the protocol (http:// or https://) as part of the url entered.',
                },
                ctaLabel: {
                    label: 'Label',
                    type: 'string',
                    group: 'cta',
                    placeholder: 'Call To Action label...',
                },
                ctaNewWindow: {
                    label: 'New window',
                    type: 'boolean',
                    default: true,
                    group: 'cta',
                    placeholder: 'Open Call To Action in a new window?',
                },
                kicker: {
                    label: 'Kicker',
                    type: 'reference',
                    references: 'kicker',
                    fieldName: 'portalKicker',
                    group: 'advancedDefault',
                },
                accessConfiguration: {
                    label: 'Access Configuration',
                    type: 'string',
                    enum: ['anonymous', 'loggedIn', 'accessGroups'],
                    enumLabels: ['Anonymous', 'Enroll', 'Access Groups'],
                    default: 'accessGroups',
                    group: 'access',
                },
                accessGroups: {
                    label: 'Access Groups',
                    type: 'accessGroups',
                    group: 'access',
                },
                excludeFromAllCourses: {
                    label: 'Exclude from All Courses memberships',
                    type: 'boolean',
                    default: false,
                    group: 'access',
                    description: 'When checked access to this document must be granted via a specific access group.  The All Courses group does not grant access.',
                },
                richText1: {
                    label: 'Rich Text 1',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText1Label',
                },
                richText2: {
                    label: 'Rich Text 2',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText2Label',
                },
                richText3: {
                    label: 'Rich Text 3',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText3Label',
                },
                richText4: {
                    label: 'Rich Text 4',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText4Label',
                },
                richText5: {
                    label: 'Rich Text 5',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText5Label',
                },
                richText6: {
                    label: 'Rich Text 6',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText6Label',
                },
                richText7: {
                    label: 'Rich Text 7',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText7Label',
                },
                richText8: {
                    label: 'Rich Text 8',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText8Label',
                },
                richText9: {
                    label: 'Rich Text 9',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText9Label',
                },
                richText10: {
                    label: 'Rich Text 10',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText10Label',
                },
                discussionTopic: {
                    label: 'Topic',
                    type: 'reference',
                    references: 'discussionTopic',
                    fieldName: 'discussionTopic',
                    group: 'discussion',
                },
                ogImage: {
                    label: 'OG Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    group: 'advancedOptions',
                },
            },
            required: ['id', 'name'],
        },
        documentProgress: {
            name: 'Document Progress',
            pluralTypeName: 'DocumentProgresses',
            label: 'Document Progress',
            description: 'A Document Progress tracks the progress of a customer on a Document.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                    group: null,
                    subGroup: null,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                customer: {
                    label: 'Customer',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'customerID',
                    portalApi: false,
                    readOnly: true,
                    primaryKey: true,
                    shortKey: true,
                },
                document: {
                    label: 'Document',
                    type: 'reference',
                    references: 'document',
                    fieldName: 'documentKey', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                    group: null,
                    subGroup: null,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                progress: {
                    label: 'Progress in %',
                    type: 'json',
                    readOnly: true,
                    group: 'null',
                    default: {},
                },
                startedDate: {
                    label: 'Program Start Date',
                    type: 'timestamp',
                    readOnly: true,
                    group: null,
                    subGroup: null,
                },
                completeDate: {
                    label: 'Program Completed Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                lastActiveDate: {
                    label: 'Last active date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                expiryDate: {
                    label: 'Expiry date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                notes: {
                    label: 'Notes',
                    type: 'json', // could be an array or json - not sure which is best
                    defaultValue: [],
                },
                status: {
                    label: 'Status',
                    type: 'string',
                    default: 'active',
                    enum: ['assigned', 'viewed', 'completed', 'signed', 'archived'],
                    enumLabels: ['Assigned', 'Viewed', 'Completed', 'Signed', 'Archived'],
                },
                includeInMyDocuments: {
                    label: 'Include in My Documents',
                    type: 'boolean',
                    default: false,
                    group: 'details',
                    placeholder: 'Include this document in the customer\'s My Documents list.',
                },
                history: {
                    label: 'Histsory',
                    type: 'json',
                    readOnly: true,
                    portalApi: false,
                    group: 'null',
                    default: [],
                },
                viewCount: {
                    label: 'View Count',
                    type: 'number',
                    default: 0,
                    portalApi: false,
                    readOnly: true,
                },
            },
            required: ['id', 'organizationID', 'customerID', 'documentID'],
        },
        emailTemplate: {
            name: 'EmailTemplate',
            label: 'Email Template',
            description: 'Email Templates used to send emails as a result of engagment flow conditions',
            type: 'entity',
            // formConfig: [
            //   {
            //     type: 'tabs',
            //     elements: [
            //       {
            //         name: 'general',
            //         type: 'group',
            //         label: 'Details',
            //       },
            //     ],
            //   },
            // ],
            formConfig: ['name', 'from', 'bcc', 'subject', 'body'],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                    group: null,
                    subGroup: null,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                name: {
                    label: 'Name',
                    placeholder: 'Name of Email',
                    type: 'string',
                    minLength: 8,
                    maxLength: 80,
                    group: 'general',
                    searchable: true,
                },
                from: {
                    label: 'From',
                    placeholder: 'From Email',
                    type: 'string',
                    subtype: 'email',
                    minLength: 8,
                    maxLength: 80,
                    group: 'general',
                    searchable: true,
                },
                bcc: {
                    label: 'BCC',
                    placeholder: 'BCC Email(s)',
                    type: 'string',
                    subtype: 'email',
                    minLength: 8,
                    maxLength: 200,
                    group: 'general',
                    searchable: true,
                },
                subject: {
                    label: 'Subject',
                    placeholder: 'Subject',
                    type: 'string',
                    minLength: 0,
                    maxLength: 300,
                    group: 'general',
                    searchable: true,
                },
                body: {
                    label: 'Email Body',
                    type: 'richtext',
                    group: 'general',
                    searchable: false,
                },
                tags: {
                    label: 'Tags',
                    type: 'string',
                    placeholder: 'Add tags...',
                    subtype: 'chips',
                    maxLength: 500,
                    group: 'general',
                    searchable: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
            },
            required: ['id', 'name'],
        },
        engagementFlow: {
            name: 'Engagement Flow',
            label: 'Engagement Flow',
            description: 'An engagement flow through which actions can be executed',
            type: 'entity',
            formConfig: [
                {
                    type: 'tabs',
                    elements: [
                        {
                            name: 'general',
                            type: 'group',
                            label: 'Details',
                        },
                        {
                            name: 'rules',
                            type: 'group',
                            label: 'Rules',
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                    group: null,
                    subGroup: null,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                name: {
                    label: 'Name',
                    placeholder: 'Add name',
                    type: 'string',
                    minLength: 8,
                    maxLength: 80,
                    group: 'general',
                    searchable: true,
                },
                description: {
                    label: 'Description',
                    placeholder: 'Add description',
                    type: 'string',
                    minLength: 8,
                    maxLength: 80,
                    group: 'general',
                    searchable: true,
                },
                active: {
                    label: 'Active',
                    type: 'boolean',
                    default: true,
                    group: 'general',
                },
                tags: {
                    label: 'Tags',
                    type: 'string',
                    placeholder: 'Add tags...',
                    subtype: 'chips',
                    maxLength: 500,
                    group: 'general',
                    searchable: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                flow: {
                    label: 'Flow',
                    type: 'json',
                    subtype: 'engagementRules',
                    default: {},
                    group: 'rules',
                },
            },
            required: ['id', 'name'],
        },
        faq: {
            name: 'FAQ',
            label: 'FAQ',
            description: 'A course/program faq contain question and answer on course/program by customer.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                question: {
                    label: 'Question',
                    type: 'string',
                    maxLength: 500,
                },
                // TODO: update it for multiple answers
                answer: {
                    label: 'Answer',
                    type: 'string',
                    maxLength: 2000,
                },
                questioner: {
                    label: 'Questioner',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'customer', // overrides the name in the backend datasource
                },
                // TODO: update it for multiple responders
                responder: {
                    label: 'Responder',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'customer', // overrides the name in the backend datasource
                },
            },
        },
        hub: {
            name: 'Hub',
            label: 'Hub',
            description: 'A grouping of customers.',
            type: 'entity',
            // formConfig: [
            //   {
            //     type: 'tabs',
            //     elements: [
            //       {
            //         name: 'details',
            //         type: 'group',
            //         label: 'Details',
            //         elements: [
            //           'name',
            //           'active',
            //           'description',
            //           'logoImage',
            //         ],
            //       },
            //       {
            //         name: 'categories',
            //         type: 'group',
            //         label: 'Categories & Groups',
            //       },
            //       {
            //         name: 'members',
            //         type: 'group',
            //         label: 'Members',
            //       },
            //     ],
            //   },
            // ],
            formConfig: [
                'name',
                'active',
                'description',
                'logoImage',
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    default: new Date(),
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    default: new Date(),
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 30,
                    default: '',
                    group: 'details',
                },
                active: {
                    label: 'Active',
                    type: 'boolean',
                    default: true,
                    group: 'details',
                    placeholder: 'Hub is enabled and available for access by customers.',
                },
                description: {
                    label: 'Description',
                    type: 'string',
                    maxLength: 5000,
                    default: '',
                    group: 'details',
                },
                logoImage: {
                    label: 'Logo Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    group: 'details',
                },
                categories: {
                    label: 'Categories',
                    type: 'array',
                    references: 'hubCategory',
                    default: [
                        {
                            id: 'e0847e99-0378-42c3-8b3d-d40d3da22c35',
                            description: '',
                            endDate: null,
                            items: [],
                            key: 'e0847e99-0378-42c3-8b3d-d40d3da22c35',
                            name: 'Categories',
                            subCategories: [
                                {
                                    id: 'db05e7cc-feb6-498f-a396-d31ae0962c6b',
                                    description: '',
                                    endDate: null,
                                    items: [],
                                    key: '9a3560a5-d82d-490b-aaf1-67e34ebf739b',
                                    name: 'Dashboard',
                                    type: 'dashboard',
                                    subCategories: [],
                                    syncState: 'stubbed',
                                },
                                {
                                    id: 'ba9a4c2c-d59f-4df9-8ec6-b308e79d4676',
                                    description: '',
                                    endDate: null,
                                    items: [],
                                    key: '6f22356e-88c7-419c-92ce-040b60554fc8',
                                    name: 'Learning',
                                    subCategories: [
                                        {
                                            id: '849778d7-1049-4694-849d-4ba09527dd75',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: '031c4dcf-4357-41b9-b5cd-285d94b771d5',
                                            name: 'Business Strategy',
                                            type: 'learnings',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                        {
                                            id: '28668382-7e3a-405e-8cde-88c6f1356944',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: 'be176f56-2d3d-46cd-a4a6-cfeb91d56ea1',
                                            name: 'Entrepreneurship',
                                            type: 'learnings',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                        {
                                            id: 'ed827c50-a16a-4d90-820e-c4f00e9004e9',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: '345b5649-eb5d-4d0e-b359-ad4f861b2a6a',
                                            name: 'Marketing',
                                            type: 'learnings',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                        {
                                            id: '70265c3f-5c0f-4cbb-a146-411c66298a80',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: 'dd2e6fcd-a4bf-4064-8553-3aefc9470378',
                                            name: 'Sales',
                                            type: 'learnings',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                        {
                                            id: 'dcacbd38-94bc-4025-8093-f7c8faf54644',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: '559f7e63-3c01-40e5-897a-af7367ad1f7e',
                                            name: 'Leadership',
                                            type: 'learnings',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                        {
                                            id: '354a0ed6-83ff-49a8-8ea2-03d1899cf11c',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: '96e9624e-c100-4355-ba43-a6448c6e189a',
                                            name: 'My Learnings',
                                            type: 'learnings',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                    ],
                                    syncState: 'stubbed',
                                },
                                {
                                    id: '28fbab08-6f54-4ee4-abd0-345e7a511811',
                                    description: '',
                                    endDate: null,
                                    items: [],
                                    key: 'd318f1da-3d23-4c85-8e99-291c50e9d4ee',
                                    name: 'Documents',
                                    subCategories: [
                                        {
                                            id: 'cc9471e2-0ae5-469f-bbb3-c7e6ead70d08',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: 'd3c414d4-9e10-4a89-98e0-ebbc2d4fd0f7',
                                            name: 'Sales',
                                            type: 'documents',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                        {
                                            id: 'df613350-35ae-481a-8059-203c4c1a656f',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: '98f6becd-32bc-4fcc-a314-e71eb89f8f80',
                                            name: 'Product',
                                            type: 'documents',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                        {
                                            id: '553606be-3215-4c37-9244-a023c523afaf',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: '6666c6b3-aa6c-493e-9da1-d4890351c42f',
                                            name: 'Contracts',
                                            type: 'documents',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                        {
                                            id: '3ba6fd5f-bb64-4db6-bf2b-d327d2b69466',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: '68615de3-a7df-4173-ac30-ec7471d8d4dd',
                                            name: 'Onboarding',
                                            type: 'documents',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                        {
                                            id: '036d5687-350b-477d-b1ce-7aaa466eae54',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: '326f74e0-fe43-4f87-bb72-fcdc4bb36573',
                                            name: 'Implementation',
                                            type: 'documents',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                    ],
                                    syncState: 'stubbed',
                                },
                                {
                                    id: '76acf897-e71e-4fc7-a9a2-4d35948c82f8',
                                    description: '',
                                    endDate: null,
                                    items: [],
                                    key: '7e703089-22ba-4912-96fb-0347a1dfc0de',
                                    name: 'Meetings',
                                    subCategories: [
                                        {
                                            id: '8f09c8bf-f80b-4e54-861e-c1d9746a79b4',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: '562e0a61-cd18-4b84-b6c5-39e314361e44',
                                            name: 'Upcoming Meetings',
                                            type: 'meetings',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                        {
                                            id: '68190d20-c40c-4fdb-a032-38f1704f8030',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: 'b1c4f6b9-974a-45af-8329-572cb535923d',
                                            name: 'Past Meetings',
                                            type: 'meetings',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                        {
                                            id: '58e131a0-19c8-47f0-bf3f-9921777f1b93',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: '7e7a6af9-c3f7-4ae1-8be1-e65acaa2f194',
                                            name: 'My Meetings',
                                            type: 'meetings',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                    ],
                                    syncState: 'stubbed',
                                },
                                {
                                    id: '48319740-17fe-4db5-a5ba-9c7082114735',
                                    description: '',
                                    endDate: null,
                                    items: [],
                                    key: '28a33e03-a9fc-4886-b7dd-ce3b13e3b4fd',
                                    name: 'Communities',
                                    subCategories: [
                                        {
                                            id: 'fe31e4da-c150-47e9-9e9f-d1af127433f9',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: '4edeed44-97aa-4205-b969-584ec09f4809',
                                            name: 'General Communities',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                        {
                                            id: '58e23803-89b7-40a1-9735-e3867451e72e',
                                            description: '',
                                            endDate: null,
                                            items: [],
                                            key: 'c867ce2e-36bb-4efd-b1b1-12a3379e501d',
                                            name: 'My Communities',
                                            subCategories: [],
                                            syncState: 'stubbed',
                                        },
                                    ],
                                    syncState: 'stubbed',
                                },
                            ],
                            syncState: 'stubbed',
                        },
                        {
                            id: 'd92a6959-6f6c-4a0b-a16c-018419eee2ec',
                            description: '',
                            endDate: null,
                            items: [],
                            key: 'd92a6959-6f6c-4a0b-a16c-018419eee2ec',
                            name: 'Groups',
                            subCategories: [],
                            syncState: 'stubbed',
                        },
                    ],
                },
                learningBoards: {
                    label: 'Learning Boards',
                    type: 'join',
                    fieldName: 'learningBoards',
                    references: 'learningBoard',
                    group: 'learningBoard',
                },
            },
            required: ['id', 'name'],
        },
        hubCategory: {
            name: 'Category',
            label: 'Category',
            pluralTypeName: 'Categories',
            description: 'A list of Categories to organize courses.',
            type: 'entity',
            formConfig: [
                {
                    name: 'tabs',
                    type: 'tabs',
                    elements: [
                        {
                            name: 'details',
                            type: 'group',
                            label: 'Details',
                        },
                        {
                            name: 'items',
                            type: 'group',
                            label: 'Items',
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                    resolver: 'PortalCategoryResolver',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                    group: 'details',
                    // default: '',
                },
                // active: {
                //   label: 'Active',
                //   type: 'boolean',
                //   default: true,
                //   group: 'details',
                // },
                // displayInNavigation: {
                //   label: 'Display In Navigation',
                //   type: 'boolean',
                //   default: true,
                //   group: 'details',
                // },
                description: {
                    label: 'Description',
                    type: 'string',
                    maxLength: 500,
                    group: 'details',
                    // default: '',
                },
                // image: {
                //   label: 'Image',
                //   type: 'reference',
                //   references: 'asset',
                //   assetType: 'image',
                //   fieldName: 'image',
                //   group: 'details',
                // },
                // startDate: {
                //   label: 'Active Dates',
                //   type: 'date',
                //   enum: ['startDate', 'endDate'],
                //   enumLabels: ['Start Date', 'End Date'],
                //   group: 'details',
                // },
                endDate: {
                    label: 'End Date',
                    type: 'date',
                    group: null,
                },
                subCategories: {
                    label: 'Sub Categories',
                    type: 'array',
                    references: 'hubCategory',
                    fieldName: 'categories', // overrides the name in the backend datasource
                    group: null, // should hide it
                },
                items: {
                    label: 'Items',
                    type: 'array',
                    references: 'item',
                    group: 'items',
                    sortable: true,
                },
                type: {
                    label: 'Type',
                    type: 'string',
                    group: 'details',
                    enum: ['dashboard', 'documents', 'learnings', 'meetings'],
                    enumLabels: ['Dashboard', 'Documents', 'Learnings', 'Meetings'],
                },
            },
            required: ['id', 'name'],
        },
        instructor: {
            name: 'Instructor',
            label: 'Instructor',
            description: 'An individual that contributes content to courses and programs.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                slug: {
                    label: 'Slug',
                    type: 'string',
                    validation: 'slug',
                    group: null,
                    searchable: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                email: {
                    label: 'Email',
                    type: 'string',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 30,
                },
                title: {
                    label: 'Title / Credentials',
                    description: 'Professional title, certifications, or credentials used in guide attribution.',
                    type: 'string',
                    maxLength: 120,
                    portalApi: false,
                },
                byline: {
                    label: 'Byline',
                    description: 'Attribution text shown in guides. The expanded Bio is used only on the author page.',
                    type: 'string',
                    maxLength: 5000,
                    multiline: true,
                    rows: 10,
                    portalApi: false,
                },
                aiVisibilityAuthority: {
                    label: 'AI Visibility authority',
                    type: 'boolean',
                    default: false,
                    portalApi: false,
                },
                bylineRole: {
                    label: 'Legacy Byline Role',
                    description: 'Deprecated. Writer or reviewer is selected per guide assignment.',
                    type: 'string',
                    enum: ['author', 'reviewer'],
                    enumLabels: ['Writer', 'Reviewer'],
                    portalApi: false,
                },
                sortOrder: {
                    label: 'Sort Order',
                    type: 'number',
                    default: 0,
                    portalApi: false,
                },
                biography: {
                    label: 'Bio',
                    type: 'string',
                    minLength: 3,
                    maxLength: 5000,
                    multiline: true,
                    rows: 10,
                },
                profileImage: {
                    label: 'Profile Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    fieldName: 'profileImage', // overrides the name in the backend datasource
                },
                linkedInUrl: {
                    label: 'LinkedIn Profile URL',
                    type: 'string',
                    minLength: 8,
                    maxLength: 512,
                },
                twitterUrl: {
                    label: 'X Profile URL',
                    type: 'string',
                    minLength: 8,
                    maxLength: 512,
                },
                facebookUrl: {
                    label: 'Facebook Profile URL',
                    type: 'string',
                    minLength: 8,
                    maxLength: 512,
                },
                instagramUrl: {
                    label: 'Instagram Profile URL',
                    type: 'string',
                    minLength: 8,
                    maxLength: 512,
                },
                youtubeUrl: {
                    label: 'Youtube Profile URL',
                    type: 'string',
                    minLength: 8,
                    maxLength: 512,
                },
                shopifyBioArticleId: {
                    label: 'Shopify Bio Article ID',
                    type: 'string',
                    readOnly: true,
                    portalApi: false,
                },
                shopifyBioUrl: {
                    label: 'Shopify Bio URL',
                    type: 'string',
                    readOnly: true,
                    portalApi: false,
                },
                user: {
                    label: 'User',
                    type: 'reference',
                    references: 'user',
                    fieldName: 'userID',
                    portalApi: false,
                },
            },
            required: ['id'],
        },
        item: {
            name: 'Item',
            label: 'Item',
            description: 'Item could be a course or a program or a membership',
            type: 'dependant',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                type: {
                    label: 'Type',
                    type: 'string',
                },
                asset: {
                    label: 'Document',
                    type: 'reference',
                    references: 'asset',
                    fieldName: 'asset',
                },
                course: {
                    label: 'Course',
                    type: 'reference',
                    references: 'course',
                    fieldName: 'course',
                },
                document: {
                    label: 'Document',
                    type: 'reference',
                    references: 'document',
                    fieldName: 'document',
                },
                meeting: {
                    label: 'Meeting',
                    type: 'reference',
                    references: 'meeting',
                    fieldName: 'meeting',
                },
                microcast: {
                    label: 'Microcast',
                    type: 'reference',
                    references: 'microcast',
                    fieldName: 'microcast',
                },
                program: {
                    label: 'Program',
                    type: 'reference',
                    references: 'program',
                    fieldName: 'program',
                },
            },
        },
        itemProgress: {
            name: 'Progress Item',
            label: 'Progress Item',
            pluralTypeName: 'ItemProgresses',
            description: 'Progress data used in Customer Progress Report.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                    group: null,
                    subGroup: null,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                    portalApi: false,
                },
                customer: {
                    label: 'Customer',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'customerID',
                    portalApi: false,
                    readOnly: true,
                    primaryKey: true,
                },
                course: {
                    label: 'Course',
                    type: 'reference',
                    references: 'course',
                    fieldName: 'courseID',
                    readOnly: true,
                    primaryKey: true,
                },
                document: {
                    label: 'Document',
                    type: 'reference',
                    references: 'document',
                    fieldName: 'documentID',
                    readOnly: true,
                    primaryKey: true,
                },
                microcast: {
                    label: 'Microcast',
                    type: 'reference',
                    references: 'microcast',
                    fieldName: 'microcastID',
                    readOnly: true,
                    primaryKey: true,
                },
                program: {
                    label: 'Program',
                    type: 'reference',
                    references: 'program',
                    fieldName: 'programID',
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    // readOnly: true,
                },
                progress: {
                    label: 'Progress in %',
                    type: 'number',
                    group: 'null',
                    default: 0,
                    readOnly: true,
                },
                tracking: {
                    label: 'Tracking',
                    description: 'Detailed tracking object with session times and total engagement.',
                    type: 'json',
                    readOnly: true,
                    defaultValue: {
                        totalTime: 0,
                        session: [],
                    },
                },
                quizResults: {
                    label: 'Quiz Results',
                    type: 'json', // could be an array or json - not sure which is best
                    defaultValue: [],
                },
                startedDate: {
                    label: 'Start Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                completeDate: {
                    label: 'Completed Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                lastActiveDate: {
                    label: 'Last active date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                certificates: {
                    label: 'Certificates',
                    type: 'referenceArray',
                    references: 'certificate',
                    fieldName: 'certificates',
                    default: [],
                    readOnly: true,
                },
                certifications: {
                    label: 'Certifications',
                    type: 'referenceArray',
                    references: 'certification',
                    fieldName: 'certifications',
                    default: [],
                    readOnly: true,
                },
                completedCertifications: {
                    label: 'Complted Certifications',
                    type: 'referenceArray',
                    references: 'certification',
                    fieldName: 'completedCertifications',
                    default: [],
                    readOnly: true,
                },
                certificateStatus: {
                    label: 'CertificationStatus',
                    type: 'string',
                    fieldName: 'certificateStatus',
                    default: '',
                    readOnly: true,
                },
                certificateScore: {
                    label: 'Certification Score %',
                    type: 'number',
                    group: 'null',
                    default: 0,
                    fieldName: 'certificateScore',
                    readOnly: true,
                },
                certificateAwardedAt: {
                    label: 'Certification Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                // expiryDate: {
                //   label: 'Expiry date',
                //   type: 'timestamp',
                //   group: null,
                //   subGroup: null,
                //   readOnly: true,
                // },
            },
            required: ['id', 'organizationID', 'customerID', 'courseID'],
        },
        itemSection: {
            name: 'ItemSection',
            label: 'Section',
            description: 'A part of a course that can contain a lesson and/or sub sections for organization.',
            type: 'dependant',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                    default: '',
                },
                description: {
                    label: 'Description',
                    type: 'string',
                    maxLength: 500,
                },
                type: {
                    label: 'Level',
                    type: 'string',
                    default: 'container',
                    enum: ['course', 'block', 'text', 'video', 'audio', 'quiz', 'container', 'knowledgeCheck'],
                    enumLabels: ['course', 'blocks', 'text', 'video', 'audio', 'knowldgeCheck', 'section', 'Knowledge Check'],
                },
                item: {
                    label: 'Course Item',
                    type: 'dependant',
                    references: 'courseItem',
                },
            },
            required: [],
        },
        kicker: {
            name: 'Kicker',
            typeName: 'PortalKicker', // this is the GQL type used in queries
            label: 'Kicker',
            description: 'A Kicker is a graphic with a link which directs it to new tab or on the same tab.',
            type: 'entity',
            formConfig: [
                {
                    type: 'tabs',
                    elements: [
                        {
                            name: 'general',
                            type: 'group',
                            label: 'Details',
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                    group: null,
                    subGroup: null,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                name: {
                    label: 'Kicker Name',
                    placeholder: 'Add kicker name',
                    type: 'string',
                    minLength: 8,
                    maxLength: 80,
                    group: 'general',
                    searchable: true,
                },
                asset: {
                    label: 'Kicker Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    fieldName: 'assetID', // Change this out to be courseImageId?
                    group: 'general',
                },
                link: {
                    label: 'Kicker Link',
                    placeholder: 'Add kicker link',
                    type: 'string',
                    minLength: 8,
                    maxLength: 80,
                    group: 'general',
                },
                openInNewTab: {
                    label: 'Opens in new tab',
                    type: 'boolean',
                    default: true,
                    group: 'general',
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                endAt: {
                    label: 'End Date',
                    description: 'End date for when the kicker should be used.',
                    type: 'date',
                    group: 'general',
                },
                tags: {
                    label: 'Tags',
                    type: 'string',
                    placeholder: 'Add tag...',
                    subtype: 'chips',
                    enum: ['tag1', 'tag2', 'tagCategory:tagValue'],
                    enumLabels: ['tag1', 'tag2', 'tagCategory:tagValue'],
                    maxLength: 500,
                    group: 'general',
                    searchable: true,
                },
                text1: {
                    label: 'Text 1',
                    placeholder: 'Add Text',
                    type: 'string',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'kickerText1Label',
                },
                text2: {
                    label: 'Text 2',
                    placeholder: 'Add Text',
                    type: 'string',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'kickerText2Label',
                },
                text3: {
                    label: 'Text 3',
                    placeholder: 'Add Text',
                    type: 'string',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'kickerText3Label',
                },
                text4: {
                    label: 'Text 4',
                    placeholder: 'Add Rich Text',
                    type: 'string',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'kickerText4Label',
                },
                text5: {
                    label: 'Text 5',
                    placeholder: 'Add Text',
                    type: 'string',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'kickerText5Label',
                },
                richText1: {
                    label: 'Rich Text 1',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'kickerRichText1Label',
                },
                richText2: {
                    label: 'Rich Text 2',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'kickerRichText2Label',
                },
                richText3: {
                    label: 'Rich Text 3',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'kickerRichText3Label',
                },
                richText4: {
                    label: 'Rich Text 4',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'kickerRichText4Label',
                },
                richText5: {
                    label: 'Rich Text 5',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'general',
                    searchable: true,
                    tokenKey: 'kickerRichText5Label',
                },
            },
            required: ['id', 'name'],
        },
        lead: {
            name: 'Lead',
            label: 'Lead',
            description: 'The end-users of the application.  Each Lead belongs to one organization.',
            type: 'entity',
            sortBy: { field: 'email', direction: 'asc' },
            formConfig: [
                {
                    type: 'tabs',
                    elements: [
                        {
                            name: 'general',
                            type: 'group',
                            label: 'Details',
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    primaryKey: true,
                    readOnly: true,
                    portalApi: false,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                    portalApi: false,
                },
                email: {
                    label: 'Email',
                    type: 'string',
                    group: 'general',
                },
                firstName: {
                    label: 'First Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 30,
                    group: 'general',
                },
                lastName: {
                    label: 'Last Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 30,
                    group: 'general',
                },
                company: {
                    label: 'Company',
                    type: 'string',
                    group: 'general',
                },
                phoneNumber: {
                    label: 'Phone Number',
                    type: 'string',
                    group: 'general',
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
            },
            required: ['id'],
        },
        profile: {
            name: 'Profile',
            label: 'Profile',
            pluralName: 'profiles',
            description: 'Profile captured from AI conversations. ID = sessionID (conversation cookie).',
            type: 'entity',
            sortBy: { field: 'createdAt', direction: 'desc' },
            formConfig: [
                {
                    type: 'tabs',
                    elements: [
                        {
                            name: 'general',
                            type: 'group',
                            label: 'Details',
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    primaryKey: true,
                    readOnly: true,
                    portalApi: false,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                    portalApi: false,
                },
                portal: {
                    label: 'Portal',
                    type: 'reference',
                    references: 'portal',
                    fieldName: 'portalID',
                    readOnly: true,
                    portalApi: false,
                },
                conversationID: {
                    label: 'Conversation ID',
                    type: 'string',
                    description: 'Session/conversation ID (same as id)',
                    group: 'general',
                },
                userID: {
                    label: 'User ID',
                    type: 'string',
                    description: 'User ID when logged in',
                    group: 'general',
                },
                email: {
                    label: 'Email',
                    type: 'string',
                    group: 'general',
                },
                capturedData: {
                    label: 'Captured Data',
                    type: 'string',
                    description: 'JSON object with captured concepts (name, company, productInterest, intent, etc.)',
                    group: 'general',
                },
                createdAt: {
                    label: 'Created At',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At',
                    type: 'timestamp',
                },
            },
            required: ['id'],
        },
        learningBoard: {
            name: 'LearningBoard',
            label: 'Learning Board',
            description: 'Page created for a specific portal and customer.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                },
                portal: {
                    label: 'Portal',
                    type: 'reference',
                    references: 'portal',
                    fieldName: 'portalID',
                    readOnly: true,
                    primaryKey: true,
                    shortKey: true,
                },
                customer: {
                    label: 'Customer',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'customerID',
                    readOnly: true,
                    primaryKey: true,
                    shortKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                learningBoardViewedAt: {
                    label: 'Learning Board Viewed At Date',
                    type: 'timestamp',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 500,
                },
                sections: {
                    label: 'Sections',
                    type: 'array', // these is a contained array - items are stored directly
                    references: 'learningBoardSection',
                    fieldName: 'sections',
                    group: null,
                    subGroup: null,
                },
            },
            required: ['id', 'name'],
        },
        learningBoardSection: {
            name: 'LearningBoardSection',
            label: 'Learning Board Section',
            description: 'One part of a learning board.',
            type: 'dependant',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                },
                type: {
                    label: 'Type',
                    type: 'string',
                    default: 'video',
                    enum: ['category', 'custom'],
                    enumLabels: ['Portal Category/Group', 'Learning Board Group'],
                },
                category: {
                    label: 'Category',
                    type: 'reference',
                    references: 'category',
                    group: 'access',
                    default: null,
                },
                items: {
                    label: 'Items',
                    type: 'array',
                    references: 'item',
                    fieldName: 'items', // overrides the name in the backend datasource
                    group: 'items',
                    sortable: true,
                },
                sections: {
                    label: 'Sections',
                    type: 'array', // these is a contained array - items are stored directly
                    references: 'learningBoardSection',
                    fieldName: 'sections',
                    group: null,
                    subGroup: null,
                },
            },
            required: ['id', 'name'],
        },
        learningItemCommon: {
            name: 'learningItemCommon',
            label: 'Learning Item',
            description: 'A piece of content that is self contained and can be presented to the customer.',
            type: 'dependant',
            formConfig: [
                {
                    name: 'cta',
                    label: 'Call to Action (CTA)',
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                    default: '',
                    nullable: true,
                },
                headline: {
                    label: 'Headline',
                    type: 'string',
                    minLength: 3,
                    maxLength: 500,
                    default: '',
                },
                url: {
                    label: 'URL',
                    type: 'string',
                    default: '',
                },
                asset: {
                    label: 'Asset',
                    type: 'reference',
                    references: 'asset',
                    fieldName: 'assetID', // overrides the name in the backend datasource
                },
                posterImage: {
                    label: 'Poster Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    fieldName: 'posterImage', // Change this out to be posterImageId?
                },
                type: {
                    label: 'Type',
                    type: 'string',
                    default: 'video',
                    enum: ['block', 'video', 'audio', 'quiz', 'article', 'download', 'knowledgeCheck', 'typeform', 'html', 'scorm'],
                    enumLabels: ['Block', 'Video', 'Audio', 'Quiz', 'Article', 'Download', 'Knowledge Check', 'Typeform', 'HTML', 'SCORM'],
                },
                // introContent: {
                //   label: 'Intro Content',
                //   type: 'richtext',
                //   maxLength: 20000,
                // },
                content: {
                    label: 'Content',
                    type: 'richtext',
                    maxLength: 20000,
                },
                htmlContent: {
                    label: 'HTML Content',
                    type: 'string',
                    multiline: true,
                    rows: 15,
                    default: '',
                    description: 'HTML embed code',
                },
                instructor: {
                    label: 'Instructor',
                    type: 'reference',
                    references: 'instructor',
                    fieldName: 'instructor', // overrides the name in the backend datasource
                    // "readOnly": false // defaults to true
                },
                level: {
                    label: 'Content Level',
                    type: 'string',
                    default: 'beginner',
                    enum: ['beginner', 'intermediate', 'advanced', 'all'],
                    enumLabels: ['Beginner', 'Intermediate', 'Advanced', 'All Levels'],
                },
                length: {
                    label: 'Content Length',
                    type: 'string',
                    default: '',
                },
                ctaUrl: {
                    label: 'CTA URL',
                    type: 'string',
                    group: 'cta',
                    placeholder: 'Enter full url...',
                    description: 'Include the protocol (http:// or https://) as part of the url entered.',
                },
                ctaLabel: {
                    label: 'CTA Label',
                    type: 'string',
                    group: 'cta',
                    placeholder: 'CTA label...',
                },
                ctaNewWindow: {
                    label: 'CTA New window',
                    type: 'boolean',
                    default: true,
                    group: 'cta',
                    placeholder: 'Open page in new window?',
                },
                questions: {
                    label: 'Questions',
                    type: 'json', // could be an array or json - not sure which is best
                    defaultValue: [],
                    subtype: 'quiz', // not sure this is needed
                },
                instructions: {
                    label: 'Instructions',
                    type: 'richtext',
                    maxLength: 2000,
                },
                passingPercentage: {
                    label: 'Passing Percentage',
                    type: 'number',
                    default: 30,
                },
            },
        },
        library: {
            name: 'Library',
            label: 'Library',
            pluralTypeName: 'Libraries',
            description: 'A grouping of courses, programs, documents and microcasts. Items are made availabe to customers in portals by linking the portal to one or more libraries.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                },
                description: {
                    label: 'Description',
                    type: 'string',
                    maxLength: 500,
                    default: '',
                },
                status: {
                    label: 'Status',
                    type: 'string',
                    default: 'active',
                    enum: ['active', 'inactive'],
                    enumLabels: ['Active', 'Inactive'],
                },
            },
            required: ['id', 'name'],
        },
        libraryShare: {
            name: 'LibraryShare',
            label: 'Marketplace Library',
            pluralLabel: 'Marketplace Libraries',
            description: 'Libraries shared from one organization to another.  They can be included as libraries for a portal, and then in categories and search results on that portal.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Source Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                },
                targetOrganization: {
                    label: 'Target Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'targetOrganizationID',
                },
                libraryID: {
                    label: 'Library',
                    type: 'string',
                    fieldName: 'libraryID',
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                },
                status: {
                    label: 'Status',
                    type: 'string',
                    default: 'active',
                    enum: ['active', 'inactive'],
                    enumLabels: ['Active', 'Inactive'],
                },
            },
            required: ['id', 'targetOrganizationID', 'name'],
        },
        meeting: {
            name: 'meeting',
            label: 'Meeting',
            description: 'A Meeting is a live session either to support interactive sessions or webinars.',
            type: 'entity',
            formConfig: [
                {
                    name: 'tabs',
                    type: 'tabs',
                    elements: [
                        {
                            name: 'general',
                            type: 'group',
                            label: 'Details',
                            elements: [
                                'name', 'status', 'headline', 'description', 'image',
                                'recording', 'content', 'instructors', 'createdBy',
                                {
                                    type: 'twoColumn',
                                    elements: ['startsAt', 'plannedDuration'],
                                },
                                'preStartOpenDuration',
                                'meetingType',
                                // 'postStartGraceDuration',
                            ],
                        },
                        {
                            name: 'messaging',
                            type: 'group',
                            label: 'Messaging',
                            elements: ['preMeetingMessage', 'postMeetingMessage', 'kicker'],
                        },
                        {
                            name: 'access',
                            type: 'group',
                            label: 'Access',
                            elements: [
                                {
                                    type: 'guardedOption',
                                    controlledBy: 'registrationRequired',
                                    controlledField: 'registerBy',
                                    label: 'Registration Required',
                                },
                                {
                                    type: 'twoColumn',
                                    elements: ['registrationMax', 'registrationCount'],
                                },
                                // 'registrationRequired', 'registerBy',
                                {
                                    type: 'guardedOption',
                                    controlledBy: 'restrictByAccessGroup',
                                    controlledField: 'accessGroups',
                                    label: 'Restrict by Access Group',
                                },
                                // 'restrictByAccessGroup', 'accessGroups',
                                {
                                    type: 'guardedOption',
                                    controlledBy: 'restrictByCustomer',
                                    controlledField: 'customers',
                                    label: 'Restrict by Customer',
                                },
                                // 'restrictByCustomer', 'customers',
                                'libraries',
                            ],
                        },
                        {
                            name: 'attendees',
                            type: 'group',
                            label: 'Attendees',
                            elements: [
                                {
                                    type: 'component',
                                    name: 'registrations',
                                },
                            ],
                        },
                        {
                            name: 'meeting',
                            type: 'group',
                            label: 'Meeting',
                            elements: [
                                {
                                    type: 'component',
                                    name: 'meeting',
                                },
                            ],
                        },
                        // {
                        //   name: 'commerce',
                        //   type: 'group',
                        //   label: 'Commerce',
                        //   elements: [
                        //     {
                        //       name: 'commerceDefault',
                        //       type: 'subGroup',
                        //     },
                        //     {
                        //       name: 'cta',
                        //       label: 'Call to Action (CTA)',
                        //       type: 'subGroup',
                        //     },
                        //     'kicker',
                        //   ],
                        // },
                        // {
                        //   name: 'publishDetails',
                        //   type: 'group',
                        //   label: 'Publish Details',
                        // },
                        // {
                        //   name: 'attributes',
                        //   type: 'group',
                        //   label: 'Attributes',
                        // },
                        // {
                        //   name: 'discussion',
                        //   type: 'group',
                        //   label: 'Discussion',
                        // },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                slug: {
                    label: 'Slug',
                    type: 'string',
                    validation: 'slug',
                    group: null,
                    searchable: true,
                },
                libraries: {
                    label: 'Libraries',
                    type: 'referenceArray',
                    subtype: 'chips',
                    references: 'library',
                    fieldName: 'libraries',
                },
                portal: {
                    label: 'Portal',
                    type: 'reference',
                    references: 'portal',
                    fieldName: 'portalID',
                    shortKey: true,
                    // readOnly: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                createdBy: {
                    label: 'Created By',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'createdByID',
                    readOnly: true,
                    description: 'Customer who created the meeting.',
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                meetingType: {
                    label: 'Meeting Type',
                    type: 'string',
                    default: 'MEETING',
                    enum: ['MEETING', 'WEBINAR'],
                    enumLabels: ['Meeting', 'Webinar'],
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    searchable: true,
                },
                headline: {
                    label: 'Headline',
                    placeholder: 'Add headline',
                    type: 'string',
                    maxLength: 500,
                    wordRequired: true,
                    searchable: true,
                },
                description: {
                    label: 'Description',
                    placeholder: 'Add description...',
                    type: 'string',
                    multiline: true,
                    rows: 6,
                    maxLength: 4000,
                    resize: true,
                    searchable: true,
                },
                image: {
                    label: 'Select Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    group: 'general',
                },
                recording: {
                    label: 'Select Video',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'video',
                    fieldName: 'recordingID',
                    group: 'general',
                },
                recordingMediaPipelineID: {
                    label: 'Recording Pipeline ID',
                    type: 'string',
                },
                content: {
                    label: 'Content',
                    type: 'richtext',
                    maxLength: 20000,
                },
                instructors: {
                    label: 'Instructors',
                    type: 'referenceArray',
                    subtype: 'chips',
                    references: 'instructor',
                    fieldName: 'instructors',
                },
                searchInstructors: {
                    label: 'Search Instructors',
                    type: 'string',
                    searchable: true,
                    group: null,
                    portalApi: false,
                },
                status: {
                    label: 'Status',
                    type: 'string',
                    default: 'PRE_MEETING',
                    enum: ['PRE_MEETING', 'OPEN', 'ACTIVE', 'POST_MEETING', 'CANCELLED'],
                    enumLabels: ['Pre-Meeting', 'Open', 'Active', 'Post-Meeting', 'Cancelled'],
                },
                preStartOpenDuration: {
                    label: 'Pre-Start Open Duration',
                    type: 'number',
                    description: 'Number of minutes before start time participants can join the meeting.',
                    default: 5,
                },
                startsAt: {
                    label: 'Starts At Date & Time',
                    type: 'timestamp',
                    description: 'Date and time when the meeting is scheduled to start.',
                },
                postStartGraceDuration: {
                    label: 'Post-Start Join Grace Period',
                    type: 'number',
                    description: 'Number of minutes after scheduled start time participants can still join the meeting.',
                    default: 15,
                },
                plannedDuration: {
                    label: 'Planned Duration',
                    type: 'number',
                    description: 'Planned meeting length in minutes.',
                    default: 60,
                },
                actualDuration: {
                    label: 'Actual Duration',
                    type: 'number',
                    description: 'Actual meeting length in minutes.',
                },
                chimeMeeting: {
                    label: 'Chime Meeting',
                    type: 'chimeMeeting',
                    readOnly: true,
                },
                preMeetingMessage: {
                    label: 'Pre Meeting Message',
                    type: 'richtext',
                    description: 'Message to be displayed to participants before the meeting starts.',
                },
                postMeetingMessage: {
                    label: 'Post Meeting Message',
                    type: 'richtext',
                    description: 'Message to be displayed to participants after the meeting has ended.',
                },
                mediaRegion: {
                    label: 'Meeting Region',
                    description: 'Meeting Media Region - Pick location close to host and participants',
                    type: 'string',
                    enum: [
                        'af-south-1',
                        'ap-northeast-1',
                        'ap-northeast-2',
                        'ap-south-1',
                        'ap-southeast-1',
                        'ap-southeast-2',
                        'ca-central-1',
                        'eu-central-1',
                        'eu-north-1',
                        'eu-south-1',
                        'eu-west-1',
                        'eu-west-2',
                        'eu-west-3',
                        'sa-east-1',
                        'us-east-1',
                        'us-east-2',
                        'us-west-1',
                        'us-west-2',
                    ],
                    enumLabels: [
                        'Cape Town',
                        'Tokyo',
                        'Seoul',
                        'Mumbai',
                        'Singapore',
                        'Sydney',
                        'Montreal',
                        'Frankfurt',
                        'Stockholm',
                        'Milan',
                        'Ireland',
                        'London',
                        'Paris',
                        'Sao Paulo',
                        'N. Virginia',
                        'Ohio',
                        'N. California',
                        'Oregon',
                    ],
                    default: 'us-west-1',
                },
                registrationRequired: {
                    label: 'Registration Required',
                    type: 'boolean',
                    default: false,
                },
                registerBy: {
                    label: 'Register By Date',
                    type: 'timestamp',
                    description: 'Date and time when registration for the meeting closes.',
                },
                registrationMax: {
                    label: 'Max Registrations',
                    type: 'number',
                    description: 'Maximum number of participants that can register for the meeting.',
                    default: 100,
                },
                registrationCount: {
                    label: 'Registration Count',
                    type: 'number',
                    readOnly: true,
                    description: 'Number of participants that have registered for the meeting.',
                    default: 0,
                },
                restrictByAccessGroup: {
                    label: 'Restrict by Access Group',
                    type: 'boolean',
                    default: false,
                },
                restrictByCustomer: {
                    label: 'Restrict by Customer List',
                    type: 'boolean',
                    default: false,
                },
                accessGroups: {
                    label: 'Access Groups',
                    type: 'accessGroups',
                },
                customers: {
                    label: 'Customers',
                    type: 'referenceArray',
                    subtype: 'chips',
                    references: 'customer',
                    fieldName: 'customers',
                },
                kicker: {
                    label: 'Kicker',
                    type: 'reference',
                    references: 'kicker',
                    fieldName: 'portalKicker',
                },
                attendees: {
                    label: 'Attendees',
                    type: 'json',
                    default: {},
                },
            },
        },
        meetingRegistration: {
            name: 'meetingRegistration',
            label: 'Meeting Registration',
            description: 'A registration of a customer for a meeting.  Customers can register prior to the meeting and any activity by the customer in regards to the meeting is connected to the registration.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                portal: {
                    label: 'Portal',
                    type: 'reference',
                    references: 'portal',
                    fieldName: 'portalID',
                    readOnly: true,
                    primaryKey: true,
                    shortKey: true,
                },
                meeting: {
                    label: 'Meeting',
                    type: 'reference',
                    references: 'meeting',
                    fieldName: 'meetingID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                    shortKey: true,
                },
                customer: {
                    label: 'Customer',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'customerID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                    portalApi: false,
                    shortKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                },
                status: {
                    label: 'Status',
                    type: 'string',
                    default: 'PENDING',
                    enum: ['PENDING', 'INVITED', 'REGISTERED', 'DECLINED', 'ACTIVE', 'ATTENDED', 'NO_SHOW', 'CANCELLED', 'POST_MEETING_VIEWER'],
                    enumLabels: ['Pending', 'Invited', 'Registered', 'Declined', 'Active', 'Attende', 'No Show', 'Cancelled', 'Viewer'],
                },
                notes: {
                    label: 'Notes',
                    type: 'json', // could be an array or json - not sure which is best
                    defaultValue: [],
                },
                history: {
                    label: 'Events',
                    type: 'json',
                    defaultValue: [],
                },
            },
        },
        member: {
            name: 'member',
            label: 'Member',
            description: 'A Member is the connection between customers and memberships.',
            type: 'entity',
            formConfig: ['name', 'customer', 'membership', 'status', 'expiresOn'],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                    portalApi: false,
                },
                portal: {
                    label: 'Portal',
                    type: 'reference',
                    references: 'portal',
                    fieldName: 'portalID',
                    shortKey: true,
                    // readOnly: true,
                },
                customer: {
                    label: 'Customer',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'customerID',
                    shortKey: true,
                    readOnly: true,
                    portalApi: false,
                },
                membership: {
                    label: 'Membership',
                    type: 'reference',
                    references: 'membership',
                    fieldName: 'membershipID',
                    shortKey: true,
                    readOnly: true,
                },
                expiresOn: {
                    label: 'Expiration Date',
                    type: 'timestamp',
                    readOnly: true,
                    description: 'The date when the membership will expire, calculated based on the type of payment.',
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                name: {
                    label: 'Membership Description',
                    type: 'string',
                },
                status: {
                    label: 'Status',
                    type: 'string',
                    default: 'PENDING',
                    enum: ['PENDING', 'TRIAL', 'ACTIVE', 'OVERDUE', 'EXPIRED'],
                    enumLabels: ['Pending', 'Trial', 'Active', 'Overdue', 'Expired'],
                },
                lastPaymentAt: {
                    label: 'Last Payment Date',
                    type: 'timestamp',
                },
                nextPaymentAt: {
                    label: 'Next Payment Date',
                    type: 'timestamp',
                },
                failedPaymentAttempts: {
                    label: 'Failed Payment Attempts',
                    type: 'number',
                    default: 0,
                },
                stripeSessionID: {
                    label: 'Stripe SessionID for transaction during checkout',
                    type: 'string',
                    portalApi: false,
                },
                stripeExpiresAt: {
                    label: 'Timestamp after which checkout of pending member isnt accepted.',
                    type: 'timestamp',
                    portalApi: false,
                },
                stripePaymentIntentID: {
                    label: 'Stripe Payment Intent',
                    type: 'string',
                    description: 'References payment record in Stripe for one time payments',
                    portalApi: false,
                },
                stripeSubscriptionID: {
                    label: 'Stripe Subscription',
                    type: 'string',
                    description: 'References subscription record in Stripe',
                    portalApi: false,
                },
                stripeInvoiceID: {
                    label: 'Stripe Invoice',
                    type: 'string',
                    description: 'References invoice for subscriptions Stripe',
                    portalApi: false,
                },
                stripeCustomerID: {
                    label: 'Stripe Customer',
                    type: 'string',
                    description: 'References customer for subscriptions Stripe',
                    portalApi: false,
                },
            },
        },
        membership: {
            name: 'Membership',
            label: 'Membership',
            description: 'Defines a set of accesses to resources that can be purchased/granted to customers.',
            type: 'entity',
            formConfig: [
                {
                    name: 'tabs',
                    type: 'tabs',
                    elements: [
                        {
                            name: 'details',
                            type: 'group',
                            label: 'Details',
                        },
                        {
                            name: 'access',
                            type: 'group',
                            label: 'Access',
                        },
                        {
                            name: 'restrictions',
                            type: 'group',
                            label: 'Restrictions',
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                portal: {
                    label: 'Portal',
                    type: 'reference',
                    references: 'portal',
                    fieldName: 'portalID',
                    shortKey: true,
                    // readOnly: true,
                },
                type: {
                    label: 'Membership Type',
                    type: 'string',
                    default: 'membership',
                    enum: ['membership', 'course', 'program'],
                    enumLabels: ['Membership', 'Course', 'Program'],
                    group: 'access',
                    description: 'Whether this is a full membership or a direct purchase of course or program.',
                },
                typeID: {
                    label: 'Course',
                    type: 'reference',
                    references: 'course',
                    fieldName: 'typeID', // overrides the name in the backend datasource
                    group: 'access',
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                    group: 'details',
                },
                description: {
                    label: 'Description',
                    type: 'string',
                    default: '',
                    maxLength: 500,
                    group: 'details',
                },
                active: {
                    label: 'Active',
                    type: 'boolean',
                    default: true,
                    group: 'details',
                },
                onetimeEnableExpiry: {
                    label: 'Enable Expiry for One Time Payment',
                    type: 'boolean',
                    default: false,
                    group: null, // part of custom MembershipTypeField
                    description: 'Enable expiration for one-time payments.',
                },
                onetimeExpireDays: {
                    label: 'One Time Expiry Days',
                    type: 'number',
                    default: 0,
                    group: null, // part of custom MembershipTypeField
                    description: 'Number of days after which the one-time payment membership will expire.',
                },
                installmentEnableExpiry: {
                    label: 'Enable Expiry for Installment Payments',
                    type: 'boolean',
                    default: false,
                    group: null, // part of custom MembershipTypeField
                    description: 'Enable expiration for installment-based memberships.',
                },
                installmentExpireDays: {
                    label: 'Installment Expiry Days',
                    type: 'number',
                    default: 0,
                    group: null, // part of custom MembershipTypeField
                    description: 'Number of days after which the installment-based membership will expire.',
                },
                startDate: {
                    label: 'Active Dates',
                    type: 'date',
                    enum: ['startDate', 'endDate'],
                    enumLabels: ['Start Date', 'End Date'],
                    // group: 'details',
                    group: null,
                },
                endDate: {
                    label: 'End Date',
                    type: 'date',
                    group: null,
                },
                paymentPlan: {
                    label: 'Payment Plan Type',
                    type: 'string',
                    default: 'recurring',
                    enum: ['recurring', 'onetime', 'installments'],
                    enumLabels: ['Recurring', 'One Time', 'Installments'],
                    group: 'details',
                    description: 'Manage how and when the membership is paid for.',
                },
                recurringPrice: {
                    label: 'Price',
                    type: 'currency',
                    default: 0,
                    group: null,
                    description: 'Price billed each period for the membership.',
                },
                recurringTerm: {
                    label: 'Term',
                    type: 'string',
                    default: 'monthly',
                    enum: ['monthly', 'weekly', 'quarterly', 'yearly'],
                    enumLabels: ['Monthly', 'Weekly', 'Quarterly', 'Yearly'],
                    group: null, // part of custom MembershipTypeField
                    description: 'How often the membership is billed.',
                },
                maxMembers: {
                    label: 'Max Members',
                    type: 'number',
                    default: 0,
                    group: null, // part of custom MembershipTypeField
                    description: 'Maximum number of users allowed for this membership. Set to 0 to allow unlimited users.',
                },
                activeMembers: {
                    label: 'Active Members',
                    type: 'number',
                    default: 0,
                    readOnly: true,
                    group: null, // part of custom MembershipTypeField
                    description: 'Current number of active members for this membership.',
                },
                trialEnabled: {
                    label: 'Free Trial',
                    type: 'boolean',
                    default: false,
                    group: null, // part of custom MembershipTypeField
                    description: 'When enabled customer gets an initial free period for the membersip.',
                },
                trialDays: {
                    label: 'Days In Trial',
                    type: 'number',
                    default: 14,
                    group: null, // part of custom MembershipTypeField
                    description: 'The number of days the member gets before the first charge.',
                },
                installmentPrice: {
                    label: 'Price',
                    type: 'currency',
                    default: 0,
                    group: null,
                    description: 'The amount billed for each installment.',
                },
                installmentCount: {
                    label: 'Installments',
                    type: 'number',
                    default: 3,
                    group: null, // part of custom MembershipTypeField
                    description: 'The number of installments that will be billed.',
                },
                installmentRecurrence: {
                    label: 'Installment Recurrence',
                    type: 'string',
                    default: 'weekly',
                    enum: ['monthly', 'weekly', 'quarterly', 'yearly'],
                    enumLabels: ['Monthly', 'Weekly', 'Quarterly', 'Yearly'],
                    group: null, // part of custom MembershipTypeField
                    description: 'How frequently the member is billed for each installment.',
                },
                onetimePrice: {
                    label: 'Price',
                    type: 'currency',
                    default: 0,
                    group: null, // part of custom MembershipTypeField
                    description: 'Amount of one-time billing to become a member.',
                },
                taxExempt: {
                    label: 'Sales Tax Exempt',
                    type: 'boolean',
                    default: false,
                    description: 'If true, tax is never charged on membership.',
                    group: null, // part of custom MembershipTypeField
                },
                accessGroups: {
                    label: 'Access Groups',
                    type: 'accessGroups',
                    group: 'access',
                    default: ['ALL_COURSES'],
                },
                confirmationMessage: {
                    label: 'Confirmation Page Message',
                    type: 'richtext',
                    group: 'details',
                    portalApi: true,
                },
                orderConfirmationEmail: {
                    label: 'Order Confirmation Email',
                    type: 'reference',
                    references: 'emailTemplate',
                    assetType: 'emailTemplate',
                    fieldName: 'orderConfirmationEmailID',
                    group: 'details',
                    portalApi: false,
                },
                upgradeMembership: {
                    label: 'Upgrade Membership',
                    type: 'reference',
                    references: 'membership',
                    fieldName: 'upgradeMembershipID',
                    group: 'details',
                    portalApi: false,
                    shortKey: true,
                },
                downgradeMembership: {
                    label: 'Downgrade Membership',
                    type: 'reference',
                    references: 'membership',
                    fieldName: 'downgradeMembershipID',
                    group: 'details',
                    portalApi: false,
                    shortKey: true,
                },
                emailAllowedDomains: {
                    label: 'Allowed Email Domains',
                    type: 'string',
                    subtype: 'bulletPoints',
                    group: 'restrictions',
                    description: 'List of allowed email domains for membership sign-up (e.g., example.com).',
                    portalApi: false,
                },
                emailDomainMismatchError: {
                    label: 'Email Domain Mismatch Error Message',
                    type: 'richtext',
                    group: 'restrictions',
                    description: 'Custom message displayed when a customer\'s email domain is not allowed.',
                    portalApi: false,
                },
            },
            required: ['id', 'name', 'organizationID', 'portalID', 'type'],
        },
        microcast: {
            name: 'Microcast',
            label: 'Microcast Item',
            description: 'Microcast.',
            type: 'entity',
            formConfig: [
                {
                    type: 'tabs',
                    elements: [
                        {
                            name: 'general',
                            type: 'group',
                            label: 'Details',
                            elements: [
                                'name',
                                'headline',
                                'description',
                                'microcastImage',
                                'format',
                                'microcastAsset',
                                'scormAsset',
                                'useAsPoster',
                                'posterImage',
                                'content',
                                'tags',
                                'keywords',
                                'level',
                                'instructors',
                                'disableSeekAhead',
                                {
                                    name: 'manualSlug',
                                    type: 'guardedOption',
                                    controlledBy: 'manualSlug',
                                    controlledField: 'slug',
                                    label: 'Override Slug.',
                                },
                            ],
                        },
                        {
                            name: 'advancedOptions',
                            type: 'group',
                            label: 'Advanced / Commerce',
                            elements: [
                                'libraries',
                                'searchable',
                                {
                                    name: 'cta',
                                    label: 'Call to Action (CTA)',
                                    type: 'subGroup',
                                },
                                {
                                    name: 'advancedDefault',
                                    type: 'group',
                                },
                                'ogImage',
                                {
                                    name: 'aiResults',
                                    label: 'AI Results',
                                    type: 'subGroup',
                                    elements: ['doNotIndex', 'doNotReference', { type: 'component', name: 'IndexedTextPreview' }],
                                },
                            ],
                        },
                        {
                            name: 'access',
                            label: 'Access Rules For Microcast',
                            type: 'group',
                        },
                        {
                            name: 'discussion',
                            type: 'group',
                            label: 'Discussion',
                        },
                        {
                            name: 'knowledgeBase',
                            label: 'Knowledge Base',
                            type: 'component',
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                    group: null,
                    subGroup: null,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                libraries: {
                    label: 'Libraries',
                    type: 'referenceArray',
                    subtype: 'chips',
                    references: 'library',
                    fieldName: 'libraries',
                    group: 'advancedOptions',
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                name: {
                    label: 'Microcast Name',
                    placeholder: 'Add microcast name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                    group: 'general',
                    searchable: true,
                },
                slug: {
                    label: 'Slug',
                    type: 'string',
                    validation: 'slug',
                    group: null,
                    subGroup: 'slug',
                    searchable: true,
                },
                manualSlug: {
                    type: 'boolean',
                    group: null,
                    subGroup: 'slug',
                    portalApi: false,
                },
                headline: {
                    label: 'Headline',
                    placeholder: 'Add headline',
                    type: 'string',
                    maxLength: 500,
                    wordRequired: true,
                    group: 'general',
                    searchable: true,
                },
                description: {
                    label: 'Description',
                    placeholder: 'Add description...',
                    type: 'string',
                    aiTarget: 'microcast',
                    multiline: true,
                    rows: 6,
                    maxLength: 4000,
                    group: 'general',
                    resize: true,
                    searchable: true,
                },
                microcastImage: {
                    label: 'Microcast Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    fieldName: 'microcastImage', // Change this out to be courseImageId?
                    group: 'general',
                },
                format: {
                    label: 'Format',
                    placeholder: 'Microcast Format',
                    type: 'string',
                    enum: ['standard', 'scorm12'],
                    enumLabels: ['Standard', 'SCORM 1.2'],
                    default: 'standard',
                    // readOnly: true,
                    group: 'general',
                    searchable: true,
                },
                microcastAsset: {
                    label: 'Microcast Video',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'video',
                    fieldName: 'microcastAsset', // overrides the name in the backend datasource
                    group: 'general',
                },
                scormAsset: {
                    label: 'Scorm File',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'zip',
                    fieldName: 'scormAsset', // overrides the name in the backend datasource
                    group: 'general',
                },
                useAsPoster: {
                    label: 'Microcast Image',
                    type: 'boolean',
                    default: true,
                    group: 'general',
                },
                posterImage: {
                    label: 'Select Poster Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    fieldName: 'posterImage', // Change this out to be courseImageId?
                    group: 'general',
                },
                content: {
                    label: 'Content',
                    type: 'richtext',
                    aiTarget: 'microcast',
                    maxLength: 20000,
                    group: 'general',
                },
                tags: {
                    label: 'Microcast Tags',
                    type: 'string',
                    placeholder: 'Add microcast tags...',
                    subtype: 'chips',
                    maxLength: 500,
                    group: 'general',
                    searchable: true,
                },
                keywords: {
                    label: 'Keywords',
                    type: 'string',
                    placeholder: 'Add keywords...',
                    subtype: 'chips',
                    maxLength: 500,
                    group: 'general',
                    searchable: true,
                },
                level: {
                    label: 'Level',
                    type: 'string',
                    default: 'beginner',
                    enum: ['beginner', 'intermediate', 'advanced', 'all'],
                    enumLabels: ['Beginner', 'Intermediate', 'Advanced', 'All Levels'],
                    group: 'general',
                    searchable: true,
                },
                instructors: {
                    label: 'Instructors',
                    type: 'referenceArray',
                    subtype: 'chips',
                    references: 'instructor',
                    fieldName: 'instructors',
                    group: 'general',
                },
                searchInstructors: {
                    label: 'searchInstructors',
                    type: 'string',
                    searchable: true,
                    group: null,
                    portalApi: false,
                },
                searchable: {
                    label: 'Searchable',
                    type: 'boolean',
                    default: true,
                    group: 'advancedOptions',
                },
                active: {
                    label: 'Active',
                    type: 'boolean',
                    default: true,
                    group: 'publishDetails',
                    placeholder: 'Course is enabled and available for access by customers.',
                },
                ctaUrl: {
                    label: 'CTA URL',
                    type: 'string',
                    group: 'cta',
                    placeholder: 'Enter full url...',
                    description: 'Include the protocol (http:// or https://) as part of the url entered.',
                },
                ctaLabel: {
                    label: 'Label',
                    type: 'string',
                    group: 'cta',
                    placeholder: 'Call To Action label...',
                },
                ctaNewWindow: {
                    label: 'New window',
                    type: 'boolean',
                    default: true,
                    group: 'cta',
                    placeholder: 'Open Call To Action in a new window?',
                },
                kicker: {
                    label: 'Kicker',
                    type: 'reference',
                    references: 'kicker',
                    fieldName: 'portalKicker',
                    group: 'advancedDefault',
                },
                accessConfiguration: {
                    label: 'Access Configuration',
                    type: 'string',
                    enum: ['anonymous', 'loggedIn', 'accessGroups'],
                    enumLabels: ['Anonymous', 'Enroll', 'Access Groups'],
                    default: 'accessGroups',
                    group: 'access',
                },
                accessGroups: {
                    label: 'Access Groups',
                    type: 'accessGroups',
                    group: 'access',
                },
                excludeFromAllCourses: {
                    label: 'Exclude from All Courses memberships',
                    type: 'boolean',
                    default: false,
                    group: 'access',
                    description: 'When checked access to this microcast must be granted via a specific access group.  The All Courses group does not grant access.',
                },
                richText1: {
                    label: 'Rich Text 1',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText1Label',
                },
                richText2: {
                    label: 'Rich Text 2',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText2Label',
                },
                richText3: {
                    label: 'Rich Text 3',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText3Label',
                },
                richText4: {
                    label: 'Rich Text 4',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText4Label',
                },
                richText5: {
                    label: 'Rich Text 5',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText5Label',
                },
                richText6: {
                    label: 'Rich Text 6',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText6Label',
                },
                richText7: {
                    label: 'Rich Text 7',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText7Label',
                },
                richText8: {
                    label: 'Rich Text 8',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText8Label',
                },
                richText9: {
                    label: 'Rich Text 9',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText9Label',
                },
                richText10: {
                    label: 'Rich Text 10',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText10Label',
                },
                discussionTopic: {
                    label: 'Topic',
                    type: 'reference',
                    references: 'discussionTopic',
                    fieldName: 'discussionTopic',
                    group: 'discussion',
                },
                disableSeekAhead: {
                    label: 'Disable Seek Ahead',
                    type: 'boolean',
                    default: false,
                    description: 'When checked, the user will not be able to skip ahead in the video.',
                    group: 'general',
                },
                doNotIndex: {
                    label: 'Do Not Index',
                    type: 'boolean',
                    default: false,
                    group: 'aiResults',
                    description: 'Do not include the content from this course in responses.',
                    portalApi: false,
                },
                doNotReference: {
                    label: 'Do Not Reference',
                    type: 'boolean',
                    default: false,
                    group: 'aiResults',
                    description: 'Do not display this course in a AI results, but include its contents as source material when generating response.',
                    portalApi: false,
                },
                ogImage: {
                    label: 'OG Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    group: 'advancedOptions',
                },
            },
            required: ['id', 'name'],
        },
        microcastProgress: {
            name: 'Microcast Progress',
            label: 'Microcast Progress',
            pluralTypeName: 'MicrocastProgresses',
            description: 'A Microcast Progress tracks the progress of a customer on a Microcast.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                    group: null,
                    subGroup: null,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                    portalApi: false,
                },
                customer: {
                    label: 'Customer',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'customerID',
                    portalApi: false,
                    readOnly: true,
                    primaryKey: true,
                    shortKey: true,
                },
                microcast: {
                    label: 'Microcast',
                    type: 'reference',
                    references: 'microcast',
                    fieldName: 'microcastID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                    group: null,
                    subGroup: null,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                progress: {
                    label: 'Progress in %',
                    type: 'json',
                    readOnly: true,
                    group: 'null',
                    default: {},
                },
                lastPosition: {
                    label: 'Last Position',
                    type: 'number',
                    readOnly: true,
                    group: 'null',
                    default: 0,
                },
                maxPosition: {
                    label: 'Max Position',
                    type: 'number',
                    readOnly: true,
                    group: 'null',
                    default: 0,
                },
                tracking: {
                    label: 'Session Tracking',
                    description: 'Stores time tracking data across multiple microcast sessions.',
                    type: 'json',
                    readOnly: true,
                    portalApi: false,
                    defaultValue: {
                        totalTime: 0,
                        session: [],
                    },
                },
                startedDate: {
                    label: 'Program Start Date',
                    type: 'timestamp',
                    readOnly: true,
                    group: null,
                    subGroup: null,
                },
                completeDate: {
                    label: 'Program Completed Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                lastActiveDate: {
                    label: 'Last active date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                expiryDate: {
                    label: 'Expiry date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                notes: {
                    label: 'Notes',
                    type: 'json', // could be an array or json - not sure which is best
                    defaultValue: [],
                },
                scormData: {
                    label: 'SCORM Data',
                    type: 'json',
                    defaultValue: {},
                },
            },
            required: ['id', 'organizationID', 'customerID', 'microcastID'],
        },
        organization: {
            name: 'Organization',
            label: 'Organization',
            description: 'The group that owns the account in Microcasting and under which all content is organized.',
            type: 'entity',
            formConfig: [
                {
                    type: 'tabs',
                    elements: [
                        {
                            name: 'details',
                            type: 'group',
                            label: 'Details',
                            elements: [
                                'name',
                                // 'stripePublicKey',
                                'enableDiscussionGroups',
                                'enableAI',
                                'enableMeetings',
                                'enableHubs',
                                'enableGeo',
                                'enableCompetitiveContentScore',
                                'defaultRoles',
                                'signUpFields',
                                'signUpRequiredFields',
                            ],
                        },
                        {
                            name: 'ai',
                            type: 'group',
                            label: 'AI Configuration',
                            elements: [
                                'clientAIStatus',
                                'clientAILanguageModel',
                                'contentGenerationModel',
                                'searchGuidance',
                                'usePostHnswFilter',
                                'exportFullConversations',
                                'internalIpRanges',
                            ],
                        },
                        {
                            name: 'syncConfigs',
                            type: 'group',
                            label: 'Sync Configs',
                            elements: [
                                {
                                    type: 'component',
                                    name: 'organizationSyncConfigs',
                                },
                            ],
                        },
                        {
                            name: 'deployment',
                            type: 'group',
                            label: 'Deployment Info',
                            elements: [
                                'deployStatus',
                                'deployedAt',
                                'appsyncGraphqlID',
                                'appsyncGraphqlKey',
                                'appsyncGraphqlUrl',
                                'cognitoUserPoolID',
                                'cognitoIdentityPoolID',
                                'cognitoWebClientID',
                            ],
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 30,
                },
                clientAIStatus: {
                    label: 'Client AI Status',
                    type: 'string',
                    description: 'Indicates the AI service status: Active, Disabled, or Rate Limited.',
                    enum: ['Active', 'Disabled', 'Rate Limited'],
                    enumLabels: ['Active', 'Disabled', 'Rate Limited'],
                    default: 'Active',
                    group: 'ai',
                },
                stripePublicKey: {
                    label: 'Stripe Public Key',
                    type: 'string',
                    default: '',
                    // field is not included or accessible via the portalApi
                    portalApi: false,
                },
                stripeSecretKey: {
                    label: 'Stripe Secret Key',
                    type: 'string',
                    default: '',
                    // field is not included or accessible via the portalApi
                    portalApi: false,
                },
                googleApiConfig: {
                    label: 'Google API Configuration',
                    type: 'json',
                    portalApi: false,
                    default: [],
                },
                googleApiSyncInfo: {
                    label: 'Google Sync Info',
                    type: 'json',
                    portalApi: false,
                    default: {},
                },
                cognitoUserPoolID: {
                    label: 'Cognito User Pool ID',
                    type: 'string',
                    default: '',
                    portalApi: false,
                    readOnly: true,
                },
                cognitoWebClientID: {
                    label: 'Cognito Web Client ID',
                    type: 'string',
                    default: '',
                    portalApi: false,
                    readOnly: true,
                },
                cognitoIdentityPoolID: {
                    label: 'Cognito Identity Pool ID',
                    type: 'string',
                    default: '',
                    portalApi: false,
                    readOnly: true,
                },
                appsyncGraphqlKey: {
                    label: 'GraphQL Endpoint Key',
                    type: 'string',
                    default: '',
                    portalApi: false,
                    readOnly: true,
                },
                appsyncGraphqlID: {
                    label: 'GraphQL API ID',
                    type: 'string',
                    default: '',
                    portalApi: false,
                    readOnly: true,
                },
                appsyncGraphqlUrl: {
                    label: 'GraphQL Endpoint URL',
                    type: 'string',
                    default: '',
                    portalApi: false,
                    readOnly: true,
                },
                accessGroups: {
                    label: 'Access Groups',
                    type: 'array',
                    references: 'accessGroupDefinition',
                    fieldName: 'accessGroups',
                    portalApi: false,
                    default: [
                        {
                            id: 'ALL_COURSES',
                            key: 'ALL_COURSES',
                            active: true,
                            canDelete: false,
                            code: 'ALL_COURSES',
                            description: 'Provides access to all courses unless specifically excluded.',
                            name: 'All Courses',
                            // syncState: 'stubbed'
                        },
                    ],
                },
                deployedAt: {
                    label: 'Deployed Date',
                    type: 'timestamp',
                    default: new Date(),
                    portalApi: false,
                    readOnly: true,
                    group: 'deployment',
                },
                deployStatus: {
                    label: 'Deployment Status',
                    type: 'string',
                    default: 'pending',
                    enum: ['pending', 'inprogress', 'ready', 'error'],
                    enumLabels: ['Pending', 'In Progress', 'Ready', 'Error'],
                    portalApi: false,
                    readOnly: true,
                    group: 'deployment',
                },
                enableDiscussionGroups: {
                    label: 'Enable Discussion Groups',
                    type: 'boolean',
                    default: false,
                    group: 'details',
                    placeholder: 'If enabled, discussion groups can be configured for portals in this organization.',
                },
                enableAI: {
                    label: 'Enable AI Content Generation',
                    type: 'boolean',
                    default: false,
                    group: 'details',
                    placeholder: 'If enabled, content can be generated via helpers wizards.',
                    portalApi: false,
                },
                enableMeetings: {
                    label: 'Enable Meetings',
                    type: 'boolean',
                    default: false,
                    group: 'details',
                    placeholder: 'If enabled, organization can use meeting (Live) features.',
                    portalApi: true,
                },
                enableHubs: {
                    label: 'Enable Hubs',
                    type: 'boolean',
                    default: false,
                    group: 'details',
                    placeholder: 'If enabled, organization can use Hub features.',
                    portalApi: false,
                },
                enableGeo: {
                    label: 'Enable GEO / AI Visibility',
                    type: 'boolean',
                    default: false,
                    group: 'details',
                    placeholder: 'If enabled, the AI Visibility (GEO) section appears in the admin menu.',
                    portalApi: false,
                },
                enableCompetitiveContentScore: {
                    label: 'Enable Competitive Content Score (RankBee)',
                    type: 'boolean',
                    default: false,
                    group: 'details',
                    placeholder: 'If enabled, guide editors show the Competitive Content Score panel. Each run uses paid third-party credit.',
                    portalApi: false,
                },
                defaultRoles: {
                    label: 'Default Roles',
                    description: 'Default roles assigned to new users if not explicitly set.',
                    type: 'options',
                    singleSelect: false,
                    enum: ['FULLADMIN', 'AIADMIN'],
                    enumLabels: ['Full Admin', 'AI Admin'],
                    default: ['FULLADMIN'],
                    group: 'details',
                    portalApi: false,
                },
                signUpFields: {
                    label: 'Sign-Up Fields',
                    type: 'options',
                    singleSelect: false,
                    enum: ['name', 'phoneNumber', 'text1', 'text2', 'text3', 'text4', 'text5'],
                    enumLabels: ['Name', 'Phone Number', 'Customer Text 1', 'Customer Text 2', 'Customer Text 3', 'Customer Text 4', 'Customer Text 5'],
                    default: [],
                    group: 'details',
                    description: 'Controls what extra fields are included when a customer creates an account or updates their profile. Email and password are always included.',
                },
                signUpRequiredFields: {
                    label: 'Sign-Up Required Fields',
                    type: 'options',
                    singleSelect: false,
                    enum: ['name', 'phoneNumber', 'text1', 'text2', 'text3', 'text4', 'text5'],
                    enumLabels: ['Name', 'Phone Number', 'Customer Text 1', 'Customer Text 2', 'Customer Text 3', 'Customer Text 4', 'Customer Text 5'],
                    default: [],
                    group: 'details',
                    description: 'Controls which sign-up fields are required. Email and password will always be required. Field must also be included in the Sign-Up Fields list.',
                },
                clientAILanguageModel: {
                    label: 'Language Model Override',
                    description: 'ID of the language model to use in generating responses.',
                    type: 'string',
                    portalApi: false,
                    default: 'anthropic/claude-haiku-4-5-20251001',
                    enum: ['default', 'us.anthropic.claude-3-5-haiku-20241022-v1:0', 'us.anthropic.claude-3-5-sonnet-20241022-v2:0', 'us.anthropic.claude-3-7-sonnet-20250219-v1:0', 'global.anthropic.claude-haiku-4-5-20251001-v1:0', 'anthropic/claude-3-5-sonnet-20241022', 'anthropic/claude-3-5-haiku-20241022', 'anthropic/claude-3-opus-20240229', 'anthropic/claude-3-7-sonnet-20250219', 'anthropic/claude-haiku-4-5-20251001', 'openai.gpt-4o-mini', 'openai.gpt-4.1-2025-04-14', 'openai.gpt-5-mini', 'openai.gpt-5-nano', 'google.gemini-2.5-flash', 'google.gemini-2.5-flash-lite', 'google.gemini-2.5-pro', 'google.gemini-3-flash', 'google.gemini-3-pro', 'moonshotai.kimi-k2.5', 'mistral.voxtral-small-24b-2507'],
                    enumLabels: ['Haiku 4.5 Direct (System Default)', 'Anthropic Claude Haiku 3.5 (Bedrock)', 'Anthropic Claude Sonnet 3.5 (Bedrock)', 'Anthropic Claude Sonnet 3.7 (Bedrock)', 'Anthropic Claude Haiku 4.5 (Bedrock)', 'Anthropic Claude Sonnet 3.5 (Direct)', 'Anthropic Claude Haiku 3.5 (Direct)', 'Anthropic Claude Opus 3 (Direct)', 'Anthropic Claude Sonnet 3.7 (Direct)', 'Anthropic Claude Haiku 4.5 (Direct)', 'OpenAI GPT-4o Mini', 'OpenAI GPT-4.1', 'OpenAI GPT-5 Mini', 'OpenAI GPT-5 Nano', 'Google Gemini 2.5 Flash', 'Google Gemini 2.5 Flash Lite', 'Google Gemini 2.5 Pro', 'Google Gemini 3 Flash', 'Google Gemini 3 Pro', 'Moonshot Kimi 2.5 (Bedrock)', 'Mistral Voxtral Small 1.0 (Bedrock)'],
                    group: 'ai',
                },
                contentGenerationModel: {
                    label: 'Page content generation model',
                    description: 'LLM for product/category page content (suggested prompts, smart summary, SEO). Organization-level only. When System Default, uses environment BedrockConfig.contentGenerationModel, then BedrockConfig.clientAILanguageModel.',
                    type: 'string',
                    portalApi: false,
                    default: 'default',
                    enum: ['default', 'us.anthropic.claude-3-5-haiku-20241022-v1:0', 'us.anthropic.claude-3-5-sonnet-20241022-v2:0', 'us.anthropic.claude-3-7-sonnet-20250219-v1:0', 'global.anthropic.claude-haiku-4-5-20251001-v1:0', 'anthropic/claude-3-5-sonnet-20241022', 'anthropic/claude-3-5-haiku-20241022', 'anthropic/claude-3-opus-20240229', 'anthropic/claude-3-7-sonnet-20250219', 'anthropic/claude-haiku-4-5-20251001', 'openai.gpt-4o-mini', 'openai.gpt-4.1-2025-04-14', 'openai.gpt-5-mini', 'openai.gpt-5-nano', 'google.gemini-2.5-flash', 'google.gemini-2.5-flash-lite', 'google.gemini-2.5-pro', 'google.gemini-3-flash', 'google.gemini-3-pro', 'moonshotai.kimi-k2.5', 'mistral.voxtral-small-24b-2507'],
                    enumLabels: ['System Default', 'Anthropic Claude Haiku 3.5 (Bedrock)', 'Anthropic Claude Sonnet 3.5 (Bedrock)', 'Anthropic Claude Sonnet 3.7 (Bedrock)', 'Anthropic Claude Haiku 4.5 (Bedrock)', 'Anthropic Claude Sonnet 3.5 (Direct)', 'Anthropic Claude Haiku 3.5 (Direct)', 'Anthropic Claude Opus 3 (Direct)', 'Anthropic Claude Sonnet 3.7 (Direct)', 'Anthropic Claude Haiku 4.5 (Direct)', 'OpenAI GPT-4o Mini', 'OpenAI GPT-4.1', 'OpenAI GPT-5 Mini', 'OpenAI GPT-5 Nano', 'Google Gemini 2.5 Flash', 'Google Gemini 2.5 Flash Lite', 'Google Gemini 2.5 Pro', 'Google Gemini 3 Flash', 'Google Gemini 3 Pro', 'Moonshot Kimi 2.5 (Bedrock)', 'Mistral Voxtral Small 1.0 (Bedrock)'],
                    group: 'ai',
                },
                crawlConfigs: {
                    label: 'Web Crawl Configuration',
                    type: 'array', // these is a contained array - items are stored directly
                    references: 'crawlConfig',
                    fieldName: 'crawlConfigs',
                    default: [],
                    description: 'Defines web crawl configurations.',
                    portalApi: false,
                },
                internalIpRanges: {
                    label: 'Internal IP Ranges',
                    type: 'array',
                    references: 'string',
                    fieldName: 'internalIpRanges',
                    default: [],
                    description: 'List of internal IP addresses/ranges in CIDR format for identifying internal requests.',
                    portalApi: false,
                    group: 'ai',
                },
                syncConfigs: {
                    label: 'Sync Configs',
                    type: 'json',
                    default: [],
                    portalApi: false,
                    description: 'Organization-level product feed configs (json, xml, csv, kibo, bigcommerce, essense).',
                },
                usePostHnswFilter: {
                    label: 'Use Post HNSW Filter',
                    type: 'boolean',
                    fieldName: 'usePostHnswFilter',
                    description: 'When enabled, commerce search via the assistant uses pgvector post-filter HNSW (faster approximate recall on large catalogs). Applies to Bedrock direct vector search for this organization.',
                    default: false,
                    portalApi: false,
                    group: 'ai',
                },
                exportFullConversations: {
                    label: 'Export Full Conversations',
                    type: 'boolean',
                    description: 'When true, the Conversations report export includes a Full Conversation transcript column (USER/AGENT/END format). When false, the column is omitted.',
                    default: false,
                    portalApi: false,
                    group: 'ai',
                },
                searchGuidance: {
                    label: 'Search Guidance',
                    type: 'string',
                    multiline: true,
                    rows: 8,
                    portalApi: false,
                    description: 'Instructions for the knowledge base / LLM covering product types, product attributes, and SKU options. Derived from Product table; updated by product sync.',
                    group: 'ai',
                },
                status: {
                    label: 'Status',
                    type: 'string',
                    readOnly: true,
                    portalApi: false,
                    default: 'ACTIVE',
                },
                deletedAt: {
                    label: 'Deleted At',
                    type: 'timestamp',
                    readOnly: true,
                    portalApi: false,
                },
                deletedBy: {
                    label: 'Deleted By',
                    type: 'string',
                    readOnly: true,
                    portalApi: false,
                },
                purgeScheduledAt: {
                    label: 'Purge Scheduled At',
                    type: 'timestamp',
                    readOnly: true,
                    portalApi: false,
                },
                purgeStatus: {
                    label: 'Purge Status',
                    type: 'string',
                    readOnly: true,
                    portalApi: false,
                },
                purgeSteps: {
                    label: 'Purge Steps',
                    type: 'json',
                    readOnly: true,
                    portalApi: false,
                },
                purgeError: {
                    label: 'Purge Error',
                    type: 'string',
                    readOnly: true,
                    portalApi: false,
                },
            },
            required: ['id'],
        },
        payment: {
            name: 'payment',
            label: 'Payment',
            description: 'Payments done on the portal',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                customer: {
                    label: 'Customer',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'customerID', // overrides the name in the backend datasource
                    shortKey: true,
                    portalApi: false,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true, // defaults to true
                    primaryKey: true,
                    portalApi: false,
                },
                membership: {
                    label: 'Membership',
                    type: 'reference',
                    references: 'membership',
                    fieldName: 'membershipID', // overrides the name in the backend datasource
                    readOnly: true, // defaults to true
                    shortKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                referenceID: {
                    label: 'Reference ID',
                    type: 'string',
                },
                sessionID: {
                    label: 'Session ID',
                    type: 'string',
                },
                status: {
                    label: 'Status',
                    type: 'string',
                },
            },
        },
        portal: {
            name: 'Portal',
            label: 'Portal',
            description: 'A site that publishes elearning content to target audiences.',
            type: 'entity',
            formConfig: [
                {
                    type: 'tabs',
                    elements: [
                        {
                            name: 'details',
                            type: 'group',
                            label: 'Details',
                            elements: [
                                'name',
                                'active',
                                'showCertification',
                                'showQuizResultsTab',
                                'publicName',
                                'publicDomain',
                                'sslDomain',
                                'description',
                                'logoImage',
                                'faviconImage',
                                'ogImage',
                                'noAccessImage',
                                {
                                    name: 'configureCourseCard',
                                    type: 'subGroup',
                                    label: 'Course Card Configuration',
                                    elements: [
                                        'courseCardShowImage',
                                        'courseCardShowTitle',
                                        'courseCardShowInstructor',
                                        'courseCardShowDesc',
                                        'courseCardShowInfoBar',
                                        'courseCardShowActionButton',
                                        'courseCardShowLessonCount',
                                    ],
                                },
                                {
                                    name: 'configureProgramLandingPage',
                                    type: 'subGroup',
                                    label: 'Program Landing Page Configuration',
                                    elements: [
                                        'programLandingWithAccessCourseAction',
                                    ],
                                },
                                {
                                    name: 'configureLearningBoard',
                                    type: 'subGroup',
                                    label: 'Learning Board Configuration',
                                    elements: [
                                        'showLearningBoard',
                                        'showUserNameInLearningBoard',
                                        'learningBoardConfig.noDataMessage',
                                    ],
                                },
                                'defaultCheckoutConfirmationMessage',
                                'orderConfirmationEmail',
                                'newCustomerMembership',
                                'googleAnalyticsTagID',
                                'trackDeskTenantId',
                            ],
                        },
                        {
                            name: 'portalTypes',
                            type: 'group',
                            label: 'Portal Type',
                            elements: [
                                'type',
                            ],
                        },
                        {
                            name: 'libraries',
                            type: 'group',
                            label: 'Libraries',
                            elements: [
                                'defaultLibrary',
                                'libraries',
                                'libraryShares',
                            ],
                        },
                        {
                            name: 'learningGroups',
                            type: 'group',
                            label: 'Learning Groups',
                            elements: [
                                'learningBoardGroupNames',
                            ],
                        },
                        {
                            name: 'portal-tokens',
                            type: 'group',
                            label: 'Portal Tokens',
                            elements: [
                                'tokens',
                            ],
                        },
                        {
                            name: 'deployment',
                            type: 'group',
                            label: 'Deployment',
                        },
                        {
                            name: 'tracking',
                            type: 'group',
                            label: 'Tracking Scripts',
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                },
                defaultLibrary: {
                    label: 'Default Library',
                    type: 'reference',
                    references: 'library',
                    fieldName: 'defaultLibraryID',
                    description: 'Default library for this portal.  Items in this library are available in the portal and new items are added to this library when they are created when this is the selected library in the Admin.',
                },
                libraries: {
                    label: 'Additional Libraries',
                    type: 'referenceArray',
                    subtype: 'chips',
                    references: 'library',
                    fieldName: 'libraries',
                    description: 'Additional libraries that are connected to the portal.  Items in these libraries are available in the portal.',
                    group: 'general',
                },
                libraryShares: {
                    label: 'Marketplace Libraries',
                    type: 'referenceArray',
                    subtype: 'chips',
                    references: 'libraryShare',
                    fieldName: 'libraryShares',
                    description: 'Marketplace libraries that are connected to the portal.  Items in these libraries are available in the portal.',
                    group: 'general',
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    default: new Date(),
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    default: new Date(),
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 30,
                    default: '',
                    group: 'details',
                },
                active: {
                    label: 'Active',
                    type: 'boolean',
                    default: true,
                    group: 'details',
                    placeholder: 'Site is enabled and available for access by customers.',
                },
                publicName: {
                    label: 'Customer-Facing Name',
                    type: 'string',
                    // minLength: 3,
                    // maxLength: 30,
                    default: '',
                    group: 'details',
                },
                noAccessImage: {
                    label: 'No Access Image',
                    description: 'Image to show when user attempts to take a course for which they do not have access.',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    group: 'details',
                },
                publicDomain: {
                    label: 'Public Domain Name for Portal',
                    type: 'string',
                    // minLength: 5,
                    // maxLength: 250,
                    default: '',
                    group: 'details',
                },
                sslDomain: {
                    label: 'SSL Certification Domain',
                    description: 'Domain of the SSL certificate matching the Public Domain.  It can either be the publicDomain or a wild-card domain that includes the Public Domain.',
                    type: 'string',
                    // minLength: 5,
                    // maxLength: 250,
                    default: '',
                    group: 'details',
                    portalApi: false,
                },
                description: {
                    label: 'Description',
                    type: 'string',
                    maxLength: 5000,
                    default: '',
                    group: 'details',
                },
                logoImage: {
                    label: 'Logo Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    group: 'details',
                    defaultPublicAccess: true,
                },
                faviconImage: {
                    label: 'Favicon Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    group: 'details',
                    description: 'Icon that appears in the browser tab. Use a square image (JPEG, PNG, SVG, or WebP) that scales well between 192px x 192px and 16px by 16px.',
                    defaultTags: 'favicon',
                    defaultPublicAccess: true,
                },
                ogImage: {
                    label: 'OG Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    group: 'details',
                    description: 'Image that appears in OG tags, like on Facebook and Twitter when the home page is included as a link. Preferred size is 1200 X 630.',
                    defaultPublicAccess: true,
                    portalApi: false,
                },
                type: {
                    label: 'Portal Type',
                    type: 'string',
                    default: 'public',
                    enum: ['public', 'membership', 'commerce'],
                    enumLabels: ['Public', 'Membership', 'Commerce'],
                    group: 'details',
                },
                allowSearch: {
                    label: 'Allow Search',
                    type: 'boolean',
                    default: true,
                    group: 'details',
                    placeholder: 'Site includes tagging to facilitate inclusion in organic search.',
                },
                enableInstructors: {
                    label: 'Enable Instructors',
                    type: 'boolean',
                    default: true,
                    group: 'details',
                    placeholder: 'Instructor information is included as port of the portal pages.',
                },
                requireSignature: {
                    label: 'Require Signature',
                    type: 'boolean',
                    default: true,
                    group: 'details',
                    placeholder: 'Instructor signature is displayed on course certificate.',
                },
                // "defaultInstructorImage": {
                //   "label": "Default Instructor Image",
                //   "type": "reference",
                //   "references": "asset",
                //   "assetType": "image",
                //   "fieldName": "defaultInstructorImage",
                //   "group": "commerce",
                //   "subGroup": null,
                // },
                categories: {
                    label: 'Categories',
                    type: 'array',
                    references: 'category',
                    // "readOnly": false // defaults to true
                    default: [
                        {
                            id: v4(),
                            key: v4(),
                            name: 'Categories',
                        },
                        {
                            id: v4(),
                            key: v4(),
                            name: 'Groups',
                        },
                    ],
                },
                pageDefaults: {
                    label: 'Page Defaults',
                    type: 'json',
                    default: {
                        account: 'base.accountPage',
                        asset: 'base.assetPage',
                        category: 'base.categoryPage',
                        class: 'base.classPage',
                        checkout: 'base.checkoutPage',
                        confirm: 'base.confirmPage',
                        course: 'base.coursePage',
                        document: 'base.documentPage',
                        home: 'base.homePage',
                        learningBoard: 'base.learningBoardPage',
                        meeting: 'base.meetingPage',
                        member: 'base.memberPage',
                        membership: 'base.membershipPage',
                        memberships: 'base.membershipLandingPage',
                        myLearnings: 'base.myLearningsPage',
                        myMeetings: 'base.myMeetingsPage',
                        program: 'base.programPage',
                        search: 'base.searchPage',
                        microcast: 'base.microcastPage',
                        instructor: 'base.instructorPage',
                    },
                },
                pageRules: {
                    label: 'Page Rules',
                    type: 'json',
                    default: {},
                },
                configs: {
                    label: 'Component Configs',
                    type: 'json',
                    fieldName: 'componentConfigs',
                    default: {
                        'ml.appbar': {
                            type: 'component',
                            component: 'AppBar',
                            props: {
                                limitToPageWidth: true,
                                searchAction: '@searchAction',
                                rootCategory: '@rootCategory',
                                showSubCategoryDropdowns: true,
                                showLogo: true,
                                showCustomBtn: false,
                            },
                        },
                        'ml.catnav': {
                            type: 'component',
                            component: 'CategoryNav',
                            props: {
                                limitToPageWidth: true,
                                rootCategory: '@rootCategory',
                                showSubCategoryDropdowns: true,
                            },
                        },
                        'ml.footerColumns': {
                            component: 'ColumnLayout',
                            props: {
                                columns: [
                                    {
                                        id: 'col1',
                                        config: 'ml.footer.col1',
                                        md: 3,
                                        xs: 12,
                                    },
                                    {
                                        id: 'col2',
                                        config: 'ml.footer.col2',
                                        md: 3,
                                        xs: 12,
                                    },
                                    {
                                        id: 'col3',
                                        config: 'ml.footer.col3',
                                        md: 3,
                                        xs: 12,
                                    },
                                    {
                                        id: 'col4',
                                        config: 'ml.footer.col4',
                                        md: 3,
                                        xs: 12,
                                    },
                                ],
                                layout: {
                                    padding: {
                                        top: '050',
                                    },
                                },
                                limitToPageWidth: true,
                                spacing: 2,
                            },
                            type: 'component',
                        },
                        'ml.footer.col1': {
                            component: 'LinksWidget',
                            props: {
                                link: {
                                    links: [
                                        {
                                            id: 'test-link',
                                            course: '',
                                            label: 'Example Link',
                                            type: 'url',
                                            url: 'https://microcasting.com',
                                        },
                                    ],
                                    title: 'Link 1',
                                },
                            },
                            type: 'component',
                        },
                        'ml.footer.col2': {
                            component: 'LinksWidget',
                            props: {
                                link: {
                                    links: [
                                        {
                                            id: 'test-link',
                                            course: '',
                                            label: 'Example Link',
                                            type: 'url',
                                            url: 'https://microcasting.com',
                                        },
                                    ],
                                    title: 'Link 2',
                                },
                            },
                            type: 'component',
                        },
                        'ml.footer.col3': {
                            component: 'LinksWidget',
                            props: {
                                link: {
                                    links: [
                                        {
                                            id: 'test-link',
                                            course: '',
                                            label: 'Example Link',
                                            type: 'url',
                                            url: 'https://microcasting.com',
                                        },
                                    ],
                                    title: 'Link 3',
                                },
                            },
                            type: 'component',
                        },
                        'ml.footer.col4': {
                            component: 'SocialLinksWidget',
                            props: {
                                link: {
                                    links: [
                                        {
                                            id: 'link.facebook',
                                            platform: 'Facebook',
                                            url: 'https://facebook.com',
                                        },
                                        {
                                            id: 'link.instagram',
                                            platform: 'Instagram',
                                            url: 'https://instagram',
                                        },
                                        {
                                            id: 'link.x',
                                            platform: 'X',
                                            url: 'https://X.com',
                                        },
                                        {
                                            id: 'link.linkedin',
                                            platform: 'LinkedIn',
                                            url: 'https://linkedin.com',
                                        },
                                        {
                                            id: 'link.youtube',
                                            platform: 'Youtube',
                                            url: 'https://youtube.com',
                                        },
                                    ],
                                    title: 'Connect With Us',
                                },
                            },
                            type: 'component',
                        },
                        'template.ml.mainContent': {
                            type: 'component',
                            component: 'HtmlWidget',
                            props: {
                                text: '<h2>Main Content Slot</h2>',
                            },
                        },
                        'template.mainLayout': {
                            component: 'RowLayout',
                            name: 'Main Layout',
                            props: {
                                items: [
                                    'ml.appbar',
                                    'ml.catnav',
                                    'template.ml.mainContent',
                                    'ml.footerColumns',
                                ],
                            },
                            type: 'template',
                        },
                        'template.hp.mc.1': {
                            component: 'HeaderWidget',
                            props: {
                                backgroundImage: {
                                    assetId: undefined,
                                },
                                horizontalAlignment: 'center',
                                limitToPageWidth: false,
                                margin: {
                                    bottom: 0,
                                    enabled: true,
                                    left: 0,
                                    right: 0,
                                    top: 0,
                                },
                                opacity: '1',
                                padding: {
                                    bottom: 0,
                                    enabled: true,
                                    left: 0,
                                    right: 0,
                                    top: 0,
                                },
                                content: {
                                    html: null,
                                    slate: [
                                        {
                                            children: [
                                                {
                                                    bold: true,
                                                    fontSize: 30,
                                                    text: 'Enter Headline/Image Here',
                                                },
                                            ],
                                            type: 'p',
                                        },
                                    ],
                                },
                                verticalAlignment: 'middle',
                            },
                            type: 'component',
                        },
                        'template.hp.mc.2.1': {
                            component: 'RichTextWidget',
                            props: {
                                content: {
                                    html: null,
                                    slate: [
                                        {
                                            id: '03gr9',
                                            align: 'center',
                                            children: [
                                                {
                                                    bold: true,
                                                    text: 'Enter Opening Statement Here',
                                                },
                                            ],
                                            type: 'p',
                                        },
                                    ],
                                },
                                layout: {
                                    padding: {
                                        bottom: '30',
                                        right: '',
                                        top: '30',
                                    },
                                },
                            },
                            type: 'component',
                        },
                        'template.hp.mc.2.2': {
                            component: 'FeaturedCard',
                            props: {
                                item: {},
                                layout: {
                                    padding: {
                                        bottom: '20',
                                        top: '20',
                                    },
                                },
                                settings: {
                                    showCourseHeadline: true,
                                    showCourseInfo: true,
                                    showInstructor: true,
                                    showOfferedBy: true,
                                },
                            },
                            type: 'component',
                        },
                        'template.hp.mc.2.3': {
                            component: 'ItemCarousel',
                            props: {
                                category: '@categoryId',
                                layout: {
                                    padding: {
                                        bottom: '020',
                                        top: '20',
                                    },
                                },
                                maxItemCount: 20,
                                showTitle: true,
                                text: {
                                    html: '<div>&nbsp;</div>',
                                },
                            },
                            type: 'component',
                        },
                        'template.hp.mc.2.4': {
                            component: 'ItemCarousel',
                            props: {
                                category: '@categoryId',
                                layout: {
                                    padding: {
                                        bottom: '20',
                                        top: '20',
                                    },
                                },
                                maxItemCount: 20,
                                showTitle: true,
                                text: {
                                    html: '<div>&nbsp;</div>',
                                },
                            },
                            type: 'component',
                        },
                        'template.hp.mc.2.5': {
                            component: 'ItemCarousel',
                            props: {
                                category: '@categoryId',
                                layout: {
                                    padding: {
                                        bottom: '20',
                                        top: '20',
                                    },
                                },
                                maxItemCount: 20,
                                showTitle: true,
                                text: {
                                    html: '<div>&nbsp;</div>',
                                },
                            },
                            type: 'component',
                        },
                        'template.hp.mc.2': {
                            component: 'RowLayout',
                            editProps: {
                                canAdd: true,
                                canRemove: true,
                            },
                            props: {
                                backgroundColor: '#00ff00',
                                horizontalPadding: 1,
                                items: [
                                    'template.hp.mc.2.1',
                                    'template.hp.mc.2.2',
                                    'template.hp.mc.2.3',
                                    'template.hp.mc.2.4',
                                    'template.hp.mc.2.5',
                                ],
                                limitToPageWidth: true,
                                verticalPadding: 1,
                            },
                            type: 'component',
                        },
                        'template.hp.mc': {
                            type: 'component',
                            component: 'RowLayout',
                            props: {
                                backgroundColor: '#000000',
                                horizontalPadding: 1,
                                items: [
                                    'template.hp.mc.1',
                                    'template.hp.mc.2',
                                ],
                                limitToPageWidth: false,
                                verticalPadding: 1,
                            },
                        },
                        'template.homePage': {
                            type: 'template',
                            extends: 'template.mainLayout',
                            component: '',
                            name: 'Home Page Template',
                            subType: 'template',
                            overrides: {
                                'template.ml.mainContent': 'template.hp.mc',
                            },
                        },
                        'template.cp.mc.1': {
                            component: 'HeaderWidget',
                            props: {
                                backgroundImage: {},
                                backgroundColor: '#000000',
                                horizontalAlignment: 'center',
                                limitToPageWidth: false,
                                margin: {
                                    bottom: 0,
                                    enabled: true,
                                    left: 0,
                                    right: 0,
                                    top: 0,
                                },
                                opacity: '0.7',
                                padding: {
                                    bottom: 0,
                                    enabled: true,
                                    left: 0,
                                    right: 0,
                                    top: 0,
                                },
                                content: {
                                    html: null,
                                    slate: [
                                        {
                                            children: [
                                                {
                                                    bold: true,
                                                    fontSize: 36,
                                                    color: 'white',
                                                    text: 'Enter Category Name ',
                                                },
                                            ],
                                            type: 'p',
                                        },
                                        {
                                            id: 'wv9ws',
                                            children: [
                                                {
                                                    fontSize: 26,
                                                    color: 'white',
                                                    text: 'Compelling Message',
                                                },
                                            ],
                                            type: 'p',
                                        },
                                    ],
                                },
                                verticalAlignment: 'middle',
                            },
                            type: 'component',
                        },
                        'template.cp.mc.2': {
                            type: 'component',
                            component: 'RowLayout',
                            props: {
                                horizontalPadding: 1,
                                items: [
                                    'template.cp.mc.2.1',
                                    'template.cp.mc.2.2',
                                ],
                                limitToPageWidth: true,
                                verticalPadding: 1,
                                layout: {
                                    padding: {
                                        top: '50',
                                    },
                                },
                            },
                        },
                        'template.cp.mc.2.1': {
                            component: 'FeaturedCard',
                            props: {
                                item: {},
                                layout: {
                                    padding: {
                                        bottom: '20',
                                        top: '20',
                                    },
                                },
                                settings: {
                                    showCourseHeadline: true,
                                    showCourseInfo: true,
                                    showInstructor: true,
                                    showOfferedBy: true,
                                },
                            },
                            type: 'component',
                        },
                        'template.cp.mc.2.2': {
                            component: 'ItemList',
                            props: {
                                categoryId: '@categoryId',
                                layout: {
                                    padding: {
                                        bottom: '20',
                                        top: '20',
                                    },
                                },
                                settings: {
                                    defaultItemView: 'list',
                                    ItemCount: 40,
                                    showTitle: true,
                                    showToggleViewBtns: true,
                                },
                                text: {
                                    html: null,
                                    slate: [
                                        {
                                            children: [
                                                {
                                                    text: 'Enter Listing Name',
                                                },
                                            ],
                                            type: 'p',
                                        },
                                    ],
                                },
                                viewAction: '@viewItemAction',
                            },
                            type: 'component',
                        },
                        'template.cp.mc': {
                            component: 'RowLayout',
                            editProps: {
                                canAdd: true,
                                canRemove: true,
                            },
                            props: {
                                backgroundColor: '#00ff00',
                                horizontalPadding: 1,
                                items: [
                                    'template.cp.mc.1',
                                    'template.cp.mc.2',
                                ],
                                limitToPageWidth: false,
                                verticalPadding: 1,
                            },
                            type: 'component',
                        },
                        'template.categoryPage': {
                            type: 'template',
                            component: '',
                            name: 'Category Page Template',
                            extends: 'template.mainLayout',
                            subType: 'template',
                            overrides: {
                                'template.ml.mainContent': 'template.cp.mc',
                            },
                        },
                        'base.homePage': {
                            type: 'template',
                            extends: 'template.homePage',
                            component: '',
                            name: 'Home Page',
                            subType: 'home',
                            overrides: {},
                        },
                        'base.categoryPage': {
                            type: 'template',
                            component: '',
                            name: 'Category Page',
                            extends: 'template.categoryPage',
                            subType: 'categoryGroup',
                            overrides: {},
                        },
                        'base.coursePage': {
                            type: 'template',
                            name: 'Course Landing Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'courseLanding',
                            overrides: {
                                'template.ml.mainContent': 'crp.mc',
                            },
                        },
                        'base.programPage': {
                            type: 'template',
                            name: 'Program Landing Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'programLanding',
                            overrides: {
                                'template.ml.mainContent': 'prp.mc',
                            },
                        },
                        'base.searchPage': {
                            type: 'template',
                            name: 'Search Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'searchResults',
                            overrides: {
                                'template.ml.mainContent': 'sp.mc',
                            },
                        },
                        'base.instructorPage': {
                            type: 'template',
                            name: 'Instructors Delivery Page',
                            extends: 'template.mainLayout',
                            subType: 'intructor',
                            component: '',
                            overrides: {
                                'template.ml.mainContent': 'ip.mc',
                            },
                        },
                        'base.classPage': {
                            type: 'template',
                            name: 'Course Delivery Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'courseDelivery',
                            overrides: {
                                'template.ml.mainContent': 'clp.mc',
                                'ml.footer': 'clp.copyrightFooter',
                            },
                        },
                        'base.microcastPage': {
                            type: 'template',
                            name: 'Microcast Delivery Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'microcastDelivery',
                            overrides: {
                                'template.ml.mainContent': 'mdp.mc',
                                'ml.footer': 'clp.copyrightFooter',
                            },
                        },
                        'base.accountPage': {
                            type: 'template',
                            name: 'Account Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'account',
                            overrides: {
                                'template.ml.mainContent': 'account.mc',
                            },
                        },
                        'base.myLearningsPage': {
                            type: 'template',
                            name: 'My Learnings Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'myLearnings',
                            overrides: {
                                'template.ml.mainContent': 'myLearnings.mc',
                            },
                        },
                        'base.myMeetingsPage': {
                            type: 'template',
                            name: 'My Meetings Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'myMeetings',
                            overrides: {
                                'template.ml.mainContent': 'myMeetings.mc',
                            },
                        },
                        'base.learningBoardPage': {
                            type: 'template',
                            name: 'Learning Board Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'learningBoard',
                            overrides: {
                                'template.ml.mainContent': 'learningBoard.mc',
                            },
                        },
                        'base.membershipLandingPage': {
                            type: 'template',
                            name: 'Membership Landing Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'memberships',
                            overrides: {
                                'template.ml.mainContent': 'membershipLanding.mc',
                            },
                        },
                        'base.checkoutPage': {
                            type: 'template',
                            name: 'Checkout Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'checkout',
                            overrides: {
                                'template.ml.mainContent': 'checkout.mc',
                            },
                        },
                        'base.confirmPage': {
                            type: 'template',
                            name: 'Confirmation Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'checkout',
                            overrides: {
                                'template.ml.mainContent': 'confirm.mc',
                            },
                        },
                        'base.meetingPage': {
                            type: 'template',
                            name: 'Meeting Delivery Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'meeting',
                            overrides: {
                                'template.ml.mainContent': 'meeting.mc',
                            },
                        },
                        'base.assetPage': {
                            type: 'template',
                            name: 'Asset Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'asset',
                            overrides: {
                                'template.ml.mainContent': 'asset.mc',
                            },
                        },
                        'base.documentPage': {
                            type: 'template',
                            name: 'Document Page',
                            extends: 'template.mainLayout',
                            component: '',
                            subType: 'document',
                            overrides: {
                                'template.ml.mainContent': 'document.mc',
                            },
                        },
                    },
                },
                // termsOfService: {
                //   label: 'Terms Of Service',
                //   placeholder: 'Add Terms Of Service...',
                //   type: 'string',
                //   multiline: true,
                //   rows: 6,
                //   maxLength: 2000,
                //   group: 'general',
                //   showTermsOfService: false,
                // },
                publicCardShowActionButton: {
                    label: 'Show Action Button',
                    type: 'boolean',
                    default: true,
                    group: 'salesCardConfig',
                },
                publicCardButtonLabel: {
                    label: 'Button Label Override',
                    description: 'If blank button label defaults to \'Start Course\'',
                    placeholder: 'Start Course',
                    type: 'string',
                    maxLength: 64,
                    default: '',
                    group: 'salesCardConfig',
                },
                publicCardAction: {
                    label: 'Card Action',
                    description: 'Action to take when user clicks on the sales card.',
                    type: 'string',
                    default: 'landing',
                    enum: ['landing', 'delivery'],
                    enumLabels: ['Landing Page', 'Delivery Page'],
                    group: 'salesCardConfig',
                },
                publicLandingPageButtonLabel: {
                    label: 'Button Label Override',
                    description: 'If blank button label defaults to \'Start Course\'',
                    type: 'string',
                    default: '',
                    placeholder: 'Start Course',
                    group: 'landingPageConfig',
                },
                membershipCardShowActionButton: {
                    label: 'Show Action Button',
                    type: 'boolean',
                    default: true,
                    group: 'salesCardConfig',
                },
                membershipCardNoAccessButtonLabel: {
                    label: 'Button Label',
                    description: 'Label when user does not have access to the course.',
                    type: 'string',
                    maxLength: 64,
                    default: 'Learn More',
                    group: 'salesCardConfig',
                },
                membershipCardNoAccessAction: {
                    label: 'Card Action',
                    description: 'Action to take when user clicks the sales card when the user does not have access to the course.',
                    type: 'string',
                    default: 'landing',
                    enum: ['landing', 'offerPage', 'checkout'],
                    enumLabels: ['Landing Page', 'Membership Offer Page', 'Checkout'],
                    group: 'salesCardConfig',
                },
                membershipCardWithAccessButtonLabel: {
                    label: 'Button Label Override',
                    description: 'Label when user has access to the course. If blank defaults to \'Start Course\'',
                    type: 'string',
                    maxLength: 64,
                    default: '',
                    placeholder: 'Start Course',
                    group: 'salesCardConfig',
                },
                membershipCardWithAccessAction: {
                    label: 'Card Action',
                    description: 'Action to take when user clicks the sales card when the user has access to the course.',
                    type: 'string',
                    default: 'delivery',
                    enum: ['landing', 'delivery'],
                    enumLabels: ['Landing Page', 'Delivery Page'],
                    group: 'salesCardConfig',
                },
                membershipLandingPageWithAccessButtonLabel: {
                    label: 'Button Label Override',
                    description: 'Label when user has access to the course.  If blank defaults to \'Start Course\'',
                    placeholder: 'Start Course',
                    type: 'string',
                    default: '',
                    group: 'landingPageConfig',
                },
                membershipLandingPageNoAccessButtonLabel: {
                    label: 'Button Label',
                    description: 'Label when user does not have access to the course.',
                    type: 'string',
                    default: 'Learn More',
                    group: 'landingPageConfig',
                },
                membershipLandingPageNoAccessMembershipAction: {
                    label: 'Button Action',
                    type: 'string',
                    default: 'offerPage',
                    enum: ['checkout', 'offerPage'],
                    enumLabels: ['Membership Checkout', 'Membership Offer Page'],
                    group: 'landingPageConfig',
                },
                commerceCardShowActionButton: {
                    label: 'Show Action Button',
                    type: 'boolean',
                    default: true,
                    group: 'salesCardConfig',
                },
                commerceCardNoAccessButtonLabel: {
                    label: 'Button Label',
                    description: 'Label when user does not have access to the course.',
                    type: 'string',
                    maxLength: 64,
                    default: '',
                    group: 'salesCardConfig',
                },
                commerceCardNoAccessAction: {
                    label: 'Card Action',
                    type: 'string',
                    default: 'landing',
                    enum: ['landing', 'checkout'],
                    enumLabels: ['Landing Page', 'Checkout'],
                    group: 'salesCardConfig',
                    description: 'Action to take when user clicks the sales card when the user does not have access to the course.',
                },
                commerceCardWithAccessButtonLabel: {
                    label: 'Button Label Override',
                    description: 'Label when user has access to the course. If blank defaults to \'Start Course\'',
                    placeholder: 'Start Course',
                    type: 'string',
                    maxLength: 64,
                    default: '',
                    group: 'salesCardConfig',
                },
                commerceCardWithAccessAction: {
                    label: 'Card Action',
                    type: 'string',
                    default: 'delivery',
                    enum: ['landing', 'delivery'],
                    enumLabels: ['Landing Page', 'Delivery Page'],
                    group: 'salesCardConfig',
                },
                commerceLandingPageWithAccessButtonLabel: {
                    label: 'Button Label Override',
                    description: 'Label when user has access to the course. If blank defaults to \'Start Course\'',
                    type: 'string',
                    placeholder: 'Start Course',
                    default: null,
                    group: 'landingPageConfig',
                },
                commerceLandingPageNoAccessButtonLabel: {
                    label: 'Button Label',
                    description: 'Label when user does not have access to the course.',
                    type: 'string',
                    default: '',
                    group: 'landingPageConfig',
                },
                commerceLandingPageNoAccessAction: {
                    label: 'Card Action',
                    type: 'string',
                    default: 'checkout',
                    enum: ['checkout'],
                    enumLabels: ['Checkout'],
                    group: 'landingPageConfig',
                    description: 'Action to take when user clicks the button when they do not have access to the course.',
                },
                cardShowPrice: {
                    label: 'Show Price',
                    type: 'boolean',
                    default: true,
                    group: 'salesCardConfig',
                },
                primaryMembership: {
                    label: 'Membership For Checkout',
                    type: 'reference',
                    references: 'membership',
                    fieldName: 'primaryMembershipID',
                    group: 'actionConfig',
                    shortKey: true,
                },
                learningBoardGroupNames: {
                    label: 'Learning Board Group Names',
                    placeholder: 'Add Group Name...',
                    type: 'string',
                    subtype: 'bulletPoints',
                    group: 'general',
                    portalApi: false,
                    default: 'Just For You',
                },
                stylesheet: {
                    label: 'Portal Stylesheet',
                    description: 'Specifies fonts, colors and other styles for the portal.',
                    type: 'json',
                    subtype: 'stylesheet',
                    portalApi: true,
                    default: {},
                },
                tokens: {
                    label: 'Tokens',
                    description: 'Text tokens to customize words and phrases within portal.',
                    type: 'json',
                    subtype: 'tokens',
                    group: 'tokens',
                },
                supportedLocales: {
                    label: 'Supported Locales',
                    description: 'List of supported locales for multi-language token support (e.g., ["en", "es", "en-GB"]).',
                    type: 'array',
                    references: 'string',
                    default: ['en', 'es'],
                    group: 'tokens',
                },
                defaultCheckoutConfirmationMessage: {
                    label: 'Default Checkout Confirmation Message',
                    description: 'Message that is used on order confirmation page if the membershi, course or program doesn\'t define one',
                    type: 'richtext',
                    group: 'details',
                    default: {
                        html: null,
                        slate: [
                            {
                                children: [
                                    {
                                        bold: true,
                                        fontSize: 44,
                                        text: "\n\nThank you for registering\n\n Let's get started\n\n\n",
                                    },
                                ],
                                type: 'p',
                                align: 'center',
                            },
                        ],
                    },
                    portalApi: true,
                },
                courseCardShowImage: {
                    label: 'Show Image',
                    type: 'boolean',
                    default: true,
                    group: 'configureCourseCard',
                    portalApi: true,
                    placeholder: 'Course Image is enabled.',
                },
                programLandingWithAccessCourseAction: {
                    label: 'Courses Go To Delivery Page when customer has access.',
                    type: 'string',
                    default: 'landing',
                    enum: ['landing', 'delivery'],
                    enumLabels: ['Landing Page', 'Delivery Page'],
                    group: 'configureProgramLandingPage',
                    portalApi: true,
                    placeholder: 'Courses Go To Delivery Page when customer has access.',
                },
                showLearningBoard: {
                    label: 'Show Learning Board',
                    type: 'boolean',
                    default: true,
                    group: 'configureLearningBoard',
                    portalApi: true,
                    placeholder: 'Learning Board is Enabled',
                },
                showUserNameInLearningBoard: {
                    label: 'Show User Name in Learning Board',
                    type: 'boolean',
                    default: true,
                    group: 'configureLearningBoard',
                    portalApi: true,
                    placeholder: 'User name is Enabled',
                },
                learningBoardConfig: {
                    label: 'Learning Board Configuration',
                    type: 'dependant',
                    references: 'learningBoardConfiguration',
                    portalApi: true,
                },
                courseCardShowTitle: {
                    label: 'Show Title',
                    type: 'boolean',
                    default: true,
                    group: 'configureCourseCard',
                    portalApi: true,
                    placeholder: 'Course Title is enabled.',
                },
                courseCardShowInstructor: {
                    label: 'Show Instructor',
                    type: 'boolean',
                    default: true,
                    group: 'configureCourseCard',
                    portalApi: true,
                    placeholder: 'Course Instructor Info is enabled.',
                },
                courseCardShowDesc: {
                    label: 'Show Desc',
                    type: 'boolean',
                    default: true,
                    group: 'configureCourseCard',
                    portalApi: true,
                    placeholder: 'Course Description is enabled.',
                },
                courseCardShowInfoBar: {
                    label: 'Show InfoBar',
                    type: 'boolean',
                    default: true,
                    group: 'configureCourseCard',
                    portalApi: true,
                    placeholder: 'Course Information bar is enabled.',
                },
                courseCardShowActionButton: {
                    label: 'Show Action Button',
                    type: 'boolean',
                    default: true,
                    group: 'configureCourseCard',
                    portalApi: true,
                    placeholder: 'Course Action button is enabled.',
                },
                courseCardShowLessonCount: {
                    label: 'Show Lesson Count',
                    type: 'boolean',
                    default: true,
                    group: 'configureCourseCard',
                    portalApi: true,
                    placeholder: 'Lesson count is enabled.',
                },
                showCertification: {
                    label: 'Show My Certifications Tab',
                    type: 'boolean',
                    default: false,
                    group: 'details',
                    placeholder: 'Show My Certifications Tab on My Learnings page.',
                },
                showQuizResultsTab: {
                    label: 'Show Quiz Results Tab',
                    type: 'boolean',
                    default: false,
                    group: 'details',
                    placeholder: 'Show Quiz Results Tab on My Learnings page.',
                },
                orderConfirmationEmail: {
                    label: 'Order Confirmation Email',
                    type: 'reference',
                    references: 'emailTemplate',
                    assetType: 'emailTemplate',
                    fieldName: 'orderConfirmationEmailID',
                    group: 'details',
                    portalApi: false,
                },
                newCustomerMembership: {
                    label: 'New Customer Membership',
                    description: 'New customers are assigned to this membership upon signing-up.',
                    type: 'reference',
                    references: 'membership',
                    assetType: 'membership',
                    fieldName: 'newCustomerMembershipID',
                    group: 'details',
                    portalApi: false,
                    shortKey: true,
                },
                trackDeskTenantId: {
                    label: 'TrackDesk Tenant ID',
                    description: 'Unique Identifier for your account in TrackDesk.  If empty TrackDesk integration is disabled.',
                    type: 'string',
                    default: '',
                    group: 'details',
                },
                cloudfrontDistributionID: {
                    label: 'Cloudfront Distribution ID',
                    type: 'string',
                    default: '',
                    portalApi: false,
                    readOnly: true,
                    group: 'deployment',
                },
                cloudfrontDistributionDomain: {
                    label: 'Cloudfront Distribution Domain',
                    type: 'string',
                    default: '',
                    portalApi: false,
                    readOnly: true,
                    group: 'deployment',
                },
                deployedAt: {
                    label: 'Deployed Date',
                    type: 'timestamp',
                    default: new Date(),
                    portalApi: false,
                    readOnly: true,
                    group: 'deployment',
                },
                deployStatus: {
                    label: 'Deployment Status',
                    type: 'string',
                    default: 'pending',
                    enum: ['pending', 'inprogress', 'ready', 'error'],
                    enumLabels: ['Pending', 'In Progress', 'Ready', 'Error'],
                    portalApi: false,
                    readOnly: true,
                    group: 'deployment  ',
                },
                googleAnalyticsTagID: {
                    label: 'Google Analytics Tracking ID',
                    type: 'string',
                    default: '',
                    group: 'details',
                },
                footerScripts: {
                    label: 'FooterScripts',
                    description: 'Block of content (usually tracking scripts) added to the bottom of the portal page.',
                    type: 'string',
                    multiline: true,
                    rows: 20,
                    default: '',
                    group: 'tracking',
                },
                repositoryOriginAccessIdentityID: {
                    label: 'Repository Origin Access Identity ID',
                    type: 'string',
                    portalApi: false,
                    readOnly: true,
                    group: 'deployment',
                    default: '',
                    description: 'Used to grant permission to repository resources in the bucket.',
                },
                clientAIBaseUrl: {
                    label: 'Base URL',
                    type: 'string',
                    portalApi: false,
                    default: '',
                },
                clientAIAnswerLength: {
                    label: 'Answer Length',
                    type: 'number',
                    default: 800,
                    portalApi: false,
                },
                clientAIAdditionalInstructions: {
                    label: 'Additional Instructions',
                    placeholder: 'Add instructions for agent...',
                    type: 'string',
                    multiline: true,
                    rows: 4,
                    portalApi: false,
                    default: '',
                    description: 'Additional system prompt instructions.  These instructions are included in all conversation types.',
                },
                clientAIFeedbackButtons: {
                    label: 'Feedback Buttons',
                    type: 'boolean',
                    default: false,
                    portalApi: true,
                },
                clientAIPromptPlaceholder: {
                    // TODO: remove this MCN-2144 (move to tokens)
                    label: 'Prompt Placeholder',
                    placeholder: 'How can I help?',
                    type: 'string',
                    portalApi: true,
                    default: '',
                },
                clientAIStartingMessage: {
                    // TODO: remove this MCN-2144 (move to tokens)
                    label: 'Starting Message',
                    description: 'Message to display when the client AI is first loaded.',
                    type: 'string',
                    portalApi: true,
                    default: '',
                },
                clientAISuggestedPrompts: {
                    // TODO: remove this MCN-2144 (move to tokens)
                    label: 'Suggested Prompts',
                    placeholder: 'Add prompt...',
                    type: 'string',
                    subtype: 'bulletPoints',
                    portalApi: true,
                    default: '',
                    description: 'Show in the main content area until the user submits first question.',
                },
                clientAIPersistentPrompts: {
                    // TODO: remove this MCN-2144 (move to tokens)
                    label: 'Persistent Prompts',
                    placeholder: 'Add persistent prompt...',
                    type: 'string',
                    subtype: 'bulletPoints',
                    portalApi: true,
                    default: '',
                    description: 'Always show at the bottom of the last response, as well as after the Suggested Prompts.',
                },
                clientAIPromptTemplate: {
                    label: 'Client AI Prompt Template',
                    description: 'System prompt template for the shopping assistant. Supports lodash template placeholders.',
                    type: 'string',
                    portalApi: false,
                    default: '',
                },
                clientAIInlineReferences: {
                    label: 'Show References Inline With Response',
                    type: 'boolean',
                    group: 'general',
                    portalApi: false,
                    default: false,
                },
                clientAISuggestRefinements: {
                    label: 'Suggest Refinements',
                    type: 'boolean',
                    group: 'general',
                    portalApi: false,
                    default: false,
                },
                clientAIConfig: {
                    label: 'Client AI Configuration',
                    type: 'dependant',
                    references: 'aiConfiguration',
                    portalApi: true,
                    default: {},
                },
            },
            required: ['id', 'name'],
        },
        portalTemplate: {
            name: 'Portal Template',
            label: 'Portal Template',
            description: 'A template used to build pages for the portal.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    default: new Date(),
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    default: new Date(),
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 30,
                    default: '',
                },
                config: {
                    label: 'Configuration',
                    type: 'string',
                },
            },
        },
        prerequisite: {
            name: 'Prerequisite',
            label: 'Prerequisite',
            description: 'Defines the required prerequisites for accessing a course, such as certifications or microcasts.',
            type: 'dependant',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                prerequisiteType: {
                    label: 'Type',
                    type: 'string',
                    enum: ['course', 'microcast'],
                    enumLabels: ['Course', 'Microcast'],
                    description: 'Determines whether the prerequisite is a course or a microcast.',
                },
                course: {
                    label: 'Course',
                    type: 'reference',
                    references: 'course',
                    description: 'Reference to the course required as a prerequisite.',
                },
                microcast: {
                    label: 'Microcast',
                    type: 'reference',
                    references: 'microcast',
                    description: 'Reference to the microcast required as a prerequisite.',
                },
                certificationID: {
                    label: 'Certification',
                    type: 'string',
                    description: 'Reference to the certification tied to the prerequisite course.',
                },
            },
        },
        program: {
            name: 'Program',
            label: 'Program',
            description: 'A program is a collection of courses that are marketed and sold together.',
            type: 'entity',
            formConfig: [
                {
                    type: 'tabs',
                    elements: [
                        {
                            name: 'general',
                            type: 'group',
                            label: 'Details',
                            elements: [
                                'name',
                                'headline',
                                'description',
                                'objectives',
                                'programImage',
                                'tags',
                                'keywords',
                                'level',
                                'length',
                                'instructors',
                                'companyName',
                                {
                                    name: 'slug',
                                    label: 'Slug',
                                    type: 'subGroup',
                                    elements: [
                                        {
                                            name: 'manualSlug',
                                            type: 'guardedOption',
                                            controlledBy: 'manualSlug',
                                            controlledField: 'slug',
                                            label: 'Override Slug.',
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            name: 'advancedOptions',
                            type: 'group',
                            label: 'Advanced Options',
                            elements: [
                                {
                                    name: 'aiResults',
                                    label: 'AI Results',
                                    type: 'subGroup',
                                    elements: [{ type: 'component', name: 'IndexedTextPreview' }],
                                },
                            ],
                        },
                        {
                            name: 'commerce',
                            type: 'group',
                            label: 'Commerce',
                            elements: [
                                {
                                    name: 'commerceDefault',
                                    type: 'subGroup',
                                },
                                // {
                                //   name: 'cta',
                                //   label: 'Call to Action (CTA)',
                                //   type: 'subGroup',
                                // },
                                {
                                    name: 'access',
                                    label: 'Access Rules For Course',
                                    type: 'subGroup',
                                },
                            ],
                        },
                        {
                            name: 'publishDetails',
                            type: 'group',
                            label: 'Publish Details',
                        },
                        {
                            name: 'attributes',
                            type: 'group',
                            label: 'Attributes',
                        },
                        {
                            name: 'discussion',
                            type: 'group',
                            label: 'Discussion',
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                slug: {
                    label: 'Slug',
                    type: 'string',
                    validation: 'slug',
                    group: null,
                    searchable: true,
                },
                manualSlug: {
                    type: 'boolean',
                    group: null,
                    portalApi: false,
                },
                libraries: {
                    label: 'Libraries',
                    type: 'referenceArray',
                    subtype: 'chips',
                    references: 'library',
                    fieldName: 'libraries',
                    group: 'advancedOptions',
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                externalID: {
                    label: 'External ID',
                    type: 'string',
                    description: 'A reference to this assets ID in an external system such as CMS.  Typically used in import processes.',
                    default: '',
                },
                name: {
                    label: 'Program Name',
                    placeholder: 'Add program name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                    group: 'general',
                    searchable: true,
                },
                headline: {
                    label: 'Headline',
                    placeholder: 'Add headline...',
                    type: 'string',
                    maxLength: 500,
                    wordRequired: true,
                    group: 'general',
                    searchable: true,
                },
                description: {
                    label: 'Description',
                    placeholder: 'Add description...',
                    type: 'string',
                    multiline: true,
                    rows: 6,
                    maxLength: 2000,
                    group: 'general',
                    searchable: true,
                },
                objectives: {
                    label: 'Learning Objectives',
                    placeholder: 'Add Learning Objective...',
                    type: 'string',
                    subtype: 'bulletPoints',
                    group: 'general',
                },
                programImage: {
                    label: 'Program Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    fieldName: 'programImage', // Change this out to be courseImageId?
                    group: 'general',
                },
                tags: {
                    label: 'Program Tags',
                    type: 'string',
                    placeholder: 'Add program tags...',
                    subtype: 'chips',
                    enum: ['programTag1', 'programTag2', 'programTag3'],
                    enumLabels: ['Program Tag 1', 'Program Tag 2', 'Program Tag 3'],
                    maxLength: 500,
                    group: 'general',
                    searchable: true,
                },
                keywords: {
                    label: 'Keywords',
                    type: 'string',
                    maxLength: 500,
                    group: 'general',
                    searchable: true,
                },
                level: {
                    label: 'Level',
                    type: 'string',
                    default: 'beginner',
                    enum: ['beginner', 'intermediate', 'advanced', 'all'],
                    enumLabels: ['Beginner', 'Intermediate', 'Advanced', 'All Levels'],
                    group: 'general',
                    searchable: true,
                },
                length: {
                    label: 'Course Length',
                    type: 'string',
                    subtype: 'duration',
                    group: 'general',
                },
                // sharePassword: {
                //   label: 'Share Password',
                //   type: 'string',
                //   maxLength: 500,
                //   group: 'general',
                // },
                audience: {
                    label: 'Who Is This Program For',
                    type: 'string',
                    maxLength: 500,
                    group: 'commerce',
                    subGroup: null,
                },
                searchable: {
                    label: 'Searchable',
                    type: 'boolean',
                    default: true,
                    group: 'advancedOptions',
                    subGroup: 'options',
                },
                gated: {
                    label: 'Gated',
                    type: 'boolean',
                    default: false,
                    description: 'Gated courses require lessons be completed in order.',
                    enum: [
                        true,
                        false,
                    ],
                    enumLabels: [
                        'Gated (Learning content must be completed in sequence',
                        'Ungated (Customer may complete learning content in any order',
                    ],
                    group: 'advancedOptions',
                    subGroup: 'options',
                },
                requiredSkills: {
                    label: 'Skills needed',
                    type: 'string',
                    placeholder: 'Add required skills...',
                    subtype: 'chips',
                    enum: ['researcher', 'technicalSEO', 'contentWriting'],
                    enumLabels: ['Researcher', 'Technical SEO', 'Content Writing'],
                    maxLength: 500,
                    group: 'advancedOptions',
                    subGroup: 'options',
                    searchable: true,
                },
                startDate: {
                    label: 'Active Dates',
                    type: 'date',
                    enum: ['startDate', 'expiryDate'],
                    enumLabels: ['start Date', 'Expiry Date'],
                    // Hide for now
                    // group: 'advancedOptions',
                    group: null,
                },
                expiryDate: {
                    label: 'Expiry Date',
                    type: 'date',
                    group: null,
                },
                welcomeMessage: {
                    label: 'Welcome Message',
                    type: 'richtext',
                    maxLength: 2000,
                    group: 'advancedOptions',
                    subGroup: 'options',
                },
                congratulatoryMessage: {
                    label: 'Congratulatory Message',
                    type: 'richtext',
                    maxLength: 2000,
                    group: 'advancedOptions',
                    subGroup: 'options',
                },
                // "certification": {
                //   "label": "Certification & awards",
                //   "type": "reference",
                //   "references": 'programCertification',
                // },
                // Start- these are combined values for all courses
                // (so I think we will generate these values on api call
                // OR increment these fields as well whenever new value updated in a course)
                enrollmentCount: {
                    label: 'Students Enrolled',
                    type: 'number',
                    default: 0,
                },
                price: {
                    label: 'Price',
                    type: 'currency',
                    default: 0,
                    group: 'commerceDefault',
                },
                sellPrice: {
                    label: 'Sell Price',
                    type: 'currency',
                    default: '0',
                    group: 'commerceDefault',
                },
                // Not needed
                // // both rating and reviewsCount are average of courses
                // "rating": {
                //   "label": "Average Ratings",
                //   "type": "number",
                //   "default": 0,
                // },
                // resources: {
                //   label: 'Resources',
                //   type: 'array',
                //   references: 'resource',
                //   fieldName: 'resources',
                // },
                instructors: {
                    label: 'Instructors',
                    type: 'referenceArray',
                    subtype: 'chips',
                    references: 'instructor',
                    fieldName: 'instructors',
                    group: 'general',
                },
                searchInstructors: {
                    label: 'searchInstructors',
                    type: 'string',
                    searchable: true,
                    group: null,
                    subGroup: null,
                    portalApi: false,
                },
                reviews: {
                    label: 'Reviews',
                    type: 'array', // these is a contained array - items are stored directly
                    references: 'review',
                    fieldName: 'reviews',
                },
                faqs: {
                    label: 'FAQs',
                    type: 'array', // these is a contained array - items are stored directly
                    references: 'faq',
                    fieldName: 'faqs',
                },
                // -end-
                currency: {
                    label: 'Currency',
                    type: 'string',
                    default: 'USD',
                    enum: ['USD', 'EUR', 'JPY', 'GBP', 'CAD'],
                    enumLabels: ['USD', 'EUR', 'JPY', 'GBP', 'CAD'],
                    group: 'commerce',
                },
                discountPercent: {
                    label: 'Discount',
                    type: 'number',
                    default: 0,
                    group: 'commerce',
                },
                companyName: {
                    label: 'Company Name',
                    placeholder: 'Add company name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                    group: 'general',
                    searchable: true,
                },
                certification: {
                    label: 'Program Certification',
                    type: 'reference',
                    references: 'certification',
                    group: 'advancedOptions',
                },
                active: {
                    label: 'Active',
                    type: 'boolean',
                    default: true,
                    group: 'publishDetails',
                    subGroup: 'default',
                    placeholder: 'Program is enabled and available for access by customers.',
                },
                ctaUrl: {
                    label: 'CTA URL',
                    type: 'string',
                    group: 'cta',
                    placeholder: 'Enter full url...',
                    description: 'Include the protocol (http:// or https://) as part of the url entered.',
                },
                ctaLabel: {
                    label: 'CTA Label',
                    type: 'string',
                    group: 'cta',
                    placeholder: 'CTA label...',
                },
                ctaNewWindow: {
                    label: 'CTA New window',
                    type: 'boolean',
                    default: true,
                    group: 'cta',
                    placeholder: 'Open page in new window?',
                },
                sections: {
                    label: 'Sections',
                    type: 'array', // these is a contained array - items are stored directly
                    references: 'programSection',
                    fieldName: 'sections',
                    group: null,
                    subGroup: null,
                },
                courseCount: {
                    label: 'Program Course Count',
                    type: 'number',
                    default: 0,
                },
                excludeFromAllCourses: {
                    label: 'Exclude from All Courses memberships',
                    type: 'boolean',
                    default: false,
                    group: 'access',
                    description: 'When checked access to this program must be granted via a specific access group.  The All Courses group does not grant access.',
                },
                accessGroups: {
                    label: 'Access Groups',
                    type: 'accessGroups',
                    group: 'access',
                    portalApi: true,
                },
                courseItems: {
                    label: 'Course Items',
                    type: 'string',
                    group: null,
                    subGroup: null,
                    portalApi: false,
                },
                microcastItems: {
                    label: 'Microcast Items',
                    type: 'string',
                    group: null,
                    subGroup: null,
                    portalApi: false,
                },
                richText1: {
                    label: 'Rich Text 1',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText1Label',
                },
                richText2: {
                    label: 'Rich Text 2',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText2Label',
                },
                richText3: {
                    label: 'Rich Text 3',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText3Label',
                },
                richText4: {
                    label: 'Rich Text 4',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText4Label',
                },
                richText5: {
                    label: 'Rich Text 5',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText5Label',
                },
                richText6: {
                    label: 'Rich Text 6',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText6Label',
                },
                richText7: {
                    label: 'Rich Text 7',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText7Label',
                },
                richText8: {
                    label: 'Rich Text 8',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText8Label',
                },
                richText9: {
                    label: 'Rich Text 9',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText9Label',
                },
                richText10: {
                    label: 'Rich Text 10',
                    placeholder: 'Add Rich Text',
                    type: 'richtext',
                    group: 'attributes',
                    searchable: true,
                    tokenKey: 'richText10Label',
                },
                discussionTopic: {
                    label: 'Topic',
                    type: 'reference',
                    references: 'discussionTopic',
                    fieldName: 'discussionTopic',
                    group: 'discussion',
                },
                ogImage: {
                    label: 'OG Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    group: 'advancedOptions',
                },
            },
        },
        programItem: {
            name: 'programItem',
            label: 'Program Item',
            description: 'A piece of going through program that is self contained and can be presented to the customer.',
            type: 'dependant',
            formConfig: [
                {
                    name: 'cta',
                    label: 'Call to Action (CTA)',
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                    default: '',
                    nullable: true,
                },
                headline: {
                    label: 'Headline',
                    type: 'string',
                    minLength: 3,
                    maxLength: 500,
                    default: '',
                },
                url: {
                    label: 'URL',
                    type: 'string',
                    default: '',
                },
                asset: {
                    label: 'Asset',
                    type: 'reference',
                    references: 'asset',
                    fieldName: 'assetID', // overrides the name in the backend datasource
                },
                posterImage: {
                    label: 'Poster Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    fieldName: 'posterImage', // Change this out to be posterImageId?
                },
                type: {
                    label: 'Type',
                    type: 'string',
                    default: 'video',
                    enum: ['course', 'block', 'video', 'audio', 'quiz', 'article', 'download', 'knowledgeCheck', 'microcast', 'certification', 'typeform'],
                    enumLabels: ['Course', 'Block', 'Video', 'Audio', 'Quiz', 'Article', 'Download', 'Knowledge Check', 'Microcast', 'Certification', 'Typeform'],
                },
                // introContent: {
                //   label: 'Intro Content',
                //   type: 'richtext',
                //   maxLength: 20000,
                // },
                content: {
                    label: 'Content',
                    type: 'richtext',
                    maxLength: 20000,
                },
                instructor: {
                    label: 'Instructor',
                    type: 'reference',
                    references: 'instructor',
                    fieldName: 'instructor', // overrides the name in the backend datasource
                    // "readOnly": false // defaults to true
                },
                level: {
                    label: 'Content Level',
                    type: 'string',
                    default: 'beginner',
                    enum: ['beginner', 'intermediate', 'advanced', 'all'],
                    enumLabels: ['Beginner', 'Intermediate', 'Advanced', 'All Levels'],
                },
                length: {
                    label: 'Content Length',
                    type: 'string',
                    default: '',
                },
                ctaUrl: {
                    label: 'CTA URL',
                    type: 'string',
                    group: 'cta',
                    placeholder: 'Enter full url...',
                    description: 'Include the protocol (http:// or https://) as part of the url entered.',
                },
                ctaLabel: {
                    label: 'CTA Label',
                    type: 'string',
                    group: 'cta',
                    placeholder: 'CTA label...',
                },
                ctaNewWindow: {
                    label: 'CTA New window',
                    type: 'boolean',
                    default: true,
                    group: 'cta',
                    placeholder: 'Open page in new window?',
                },
                questions: {
                    label: 'Questions',
                    type: 'json', // could be an array or json - not sure which is best
                    defaultValue: [],
                    subtype: 'quiz', // not sure this is needed
                },
                instructions: {
                    label: 'Instructions',
                    type: 'richtext',
                    maxLength: 2000,
                },
                passingPercentage: {
                    label: 'Passing Percentage',
                    type: 'number',
                    default: 30,
                },
                limitRetakes: {
                    label: 'Limit Retakes',
                    type: 'boolean',
                    default: false,
                },
                maximumRetakes: {
                    label: 'Maximum Retakes',
                    type: 'number',
                    default: 0,
                    minValue: 0,
                    maxValue: 100,
                },
                passedMessage: {
                    label: 'Passed Message',
                    type: 'string',
                    default: '',
                    maxLength: 200,
                },
                failedMessage: {
                    label: 'Failed Message',
                    type: 'string',
                    default: '',
                    maxLength: 200,
                },
                timeLimitEnabled: {
                    label: 'Time Limit Enabled',
                    type: 'boolean',
                    default: false,
                },
                timeLimitForQuiz: {
                    label: 'Time Limit For Quiz (in minutes)',
                    type: 'number',
                    default: 0,
                },
                course: {
                    label: 'Course',
                    type: 'reference',
                    references: 'course',
                    fieldName: 'courseID',
                },
                microcast: {
                    label: 'Microcast',
                    type: 'reference',
                    references: 'microcast',
                    fieldName: 'microcastID',
                },
                certification: {
                    label: 'Certification',
                    type: 'reference',
                    references: 'certification',
                    group: 'certificationConfig',
                    fieldName: 'certificationID',
                },
                completionCriteria: {
                    label: 'Criteria',
                    type: 'json',
                    subtype: 'courseCertificateCriteria',
                    default: {
                        percentage: '100',
                        active: false,
                    },
                    group: 'certificationConfig',
                    placeholder: '100% of a Course complete.',
                },
                quizScoreCriteria: {
                    label: 'Criteria',
                    type: 'json',
                    subtype: 'quizCertificateCriteria',
                    default: {
                        active: false,
                        quizID: 0,
                    },
                    group: 'certificationConfig',
                    placeholder: 'Scores 80% or higher on Final Quiz.',
                },
                bothCriteria: {
                    label: 'Criteria',
                    type: 'boolean',
                    default: false,
                    group: 'certificationConfig',
                    placeholder: 'Must meet BOTH criteria selected to obtain the certificate.',
                },
                emailCertificate: {
                    label: 'Email',
                    type: 'boolean',
                    default: true,
                    group: 'certificationConfig',
                    placeholder: 'Email customer certificate once they qualify.',
                },
                certificateImage: {
                    label: 'Certificate Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    fieldName: 'certificateImage',
                    group: 'certificationDisplay',
                },
                certificateDescription: {
                    label: 'Certificate Description',
                    placeholder: 'Add description on certificate',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    searchable: true,
                    group: 'certificationDisplay',
                },
                certificateMessage: {
                    label: 'Certificate Message',
                    placeholder: 'Add message on certificate',
                    type: 'richtext',
                    minLength: 8,
                    maxLength: 80,
                    searchable: true,
                    group: 'certificationMessage',
                },
                showAnswers: {
                    label: 'Show Answers',
                    description: 'Show user correct answer after each question is submitted during quiz.',
                    type: 'boolean',
                    group: 'details',
                    default: true,
                },
                required: {
                    label: 'Required Lesson',
                    description: 'Customer must view/read/watch the entire lesson for it to be marked complete. Skipping forward is disabled.',
                    type: 'boolean',
                    group: 'details',
                    default: false,
                },
            },
        },
        programProgress: {
            name: 'Program Progress',
            label: 'Program Progress',
            pluralTypeName: 'ProgramProgresses',
            description: 'A Program Progress tracks the progress of a customer on a Program.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                    group: null,
                    subGroup: null,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                    portalApi: false,
                },
                customer: {
                    label: 'Customer',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'customerID',
                    portalApi: false,
                    readOnly: true,
                    primaryKey: true,
                    shortKey: true,
                },
                program: {
                    label: 'Single Course',
                    type: 'reference',
                    references: 'program',
                    fieldName: 'programID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                },
                progress: {
                    label: 'Progress in %',
                    type: 'number',
                    group: 'null',
                    default: 0,
                    readOnly: true,
                },
                startedDate: {
                    label: 'Program Start Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                completeDate: {
                    label: 'Program Completed Date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                lastActiveDate: {
                    label: 'Last active date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                expiryDate: {
                    label: 'Expiry date',
                    type: 'timestamp',
                    group: null,
                    subGroup: null,
                    readOnly: true,
                },
                sectionProgress: {
                    label: 'Program Progress',
                    type: 'json',
                    defaultValue: {},
                },
                quizResults: {
                    label: 'Quiz Results',
                    type: 'json', // could be an array or json - not sure which is best
                    defaultValue: [],
                },
                notes: {
                    label: 'Notes',
                    type: 'json', // could be an array or json - not sure which is best
                    defaultValue: [],
                },
                hasCertification: {
                    label: 'Has Certification',
                    type: 'boolean',
                    default: false,
                    readOnly: true,
                },
                certificateAwarded: {
                    label: 'Certificate Awarded',
                    type: 'number',
                    default: 0,
                    readOnly: true,
                },
                certificates: {
                    label: 'Certificates',
                    type: 'referenceArray',
                    references: 'certificate',
                    fieldName: 'certificates',
                    default: [],
                    readOnly: true,
                },
                certifications: {
                    label: 'Certifications',
                    type: 'referenceArray',
                    references: 'certification',
                    fieldName: 'certifications',
                    default: [],
                    readOnly: true,
                },
                completedCertifications: {
                    label: 'Complted Certifications',
                    type: 'referenceArray',
                    references: 'certification',
                    fieldName: 'completedCertifications',
                    default: [],
                    readOnly: true,
                },
                certificateStatus: {
                    label: 'CertificationStatus',
                    type: 'string',
                    fieldName: 'certificateStatus',
                    default: '',
                    readOnly: true,
                },
                certificateScore: {
                    label: 'Certification Score %',
                    type: 'number',
                    group: 'null',
                    default: 0,
                    fieldName: 'certificateScore',
                    readOnly: true,
                },
                certificateAwardedAt: {
                    label: 'Certification Date',
                    type: 'timestamp',
                    readOnly: true,
                },
            },
            required: ['id', 'organizationID', 'customerID', 'programID'],
        },
        programSection: {
            name: 'ProgramSection',
            label: 'Section',
            description: 'A part of a program that contains courses',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                },
                type: {
                    label: 'Type',
                    type: 'string',
                    default: 'container',
                    enum: ['course', 'block', 'text', 'video', 'audio', 'knowledgeCheck', 'container', 'microcast'],
                    enumLabels: ['Course', 'Block', 'Text', 'Video', 'Audio', 'Knowledge Check', 'Section', 'Microcast'],
                },
                description: {
                    label: 'Description',
                    placeholder: 'Add description...',
                    type: 'string',
                    multiline: true,
                    rows: 6,
                    maxLength: 2000,
                },
                item: {
                    label: 'Program Item',
                    type: 'object',
                    references: 'programItem',
                },
                sections: {
                    label: 'Sections',
                    type: 'array', // these is a contained array - items are stored directly
                    references: 'programSection',
                    fieldName: 'sections',
                    group: null,
                    subGroup: null,
                },
            },
        },
        question: {
            name: 'Question',
            label: 'Question',
            description: 'A question that is presented to a customer in a questionnaire.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    default: new Date(),
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    default: new Date(),
                },
                name: {
                    label: 'Question Text',
                    type: 'string',
                    minLength: 3,
                    maxLength: 300,
                    default: '',
                    group: 'details',
                },
                type: {
                    label: 'Type',
                    type: 'string',
                    default: 'multipleChoice',
                    enum: ['fillInTheBlank', 'multipleChoice', 'multipleResponse', 'yesno', 'scale'],
                    enumLabels: ['Fill In The Blank', 'Multiple Choice', 'Multiple Response', 'Yes/No', 'Scale'],
                },
                availableAnswers: {
                    label: 'Available Answers',
                    type: 'json',
                    default: [],
                },
            },
            required: ['id', 'name'],
        },
        questionnaire: {
            name: 'Questionnaire',
            label: 'Questionnaire',
            description: 'A set of questions that are prepared by an administrator and answered by a customer.',
            type: 'entity',
            formConfig: [
                {
                    type: 'tabs',
                    elements: [
                        {
                            name: 'details',
                            type: 'group',
                            label: 'Details',
                            elements: [
                                'name',
                                'email',
                                'status',
                                'questions',
                            ],
                        },
                        {
                            name: 'messaging',
                            type: 'group',
                            label: 'Messsaging',
                            elements: [
                                'welcomeMessage',
                                'completionMessage',
                            ],
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                },
                portal: {
                    label: 'Portal',
                    type: 'reference',
                    references: 'portal',
                    fieldName: 'portalID',
                    readOnly: true,
                    primaryKey: true,
                    shortKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    default: new Date(),
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                    default: new Date(),
                },
                startedAt: {
                    label: 'Started At Date',
                    type: 'timestamp',
                    default: new Date(),
                },
                completedAt: {
                    label: 'Completed Date',
                    type: 'timestamp',
                    default: new Date(),
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 30,
                    default: '',
                    group: 'details',
                },
                email: {
                    label: 'Customer Email',
                    type: 'string',
                    default: 'customer.email@microcasting.com',
                },
                welcomeMessage: {
                    label: 'Welcome Message',
                    type: 'richtext',
                    maxLength: 2000,
                    group: 'messaging',
                },
                completionMessage: {
                    label: 'Completion Message',
                    type: 'richtext',
                    maxLength: 2000,
                    group: 'messaging',
                },
                status: {
                    label: 'Status',
                    type: 'string',
                    default: 'development',
                    enum: ['development', 'pending', 'started', 'complete'],
                    enumLabels: ['In Development', 'Pending', 'Started', 'Completed'],
                },
                questions: {
                    label: 'Questions',
                    // type: 'array', // these is a contained array - items are stored directly
                    // references: 'question',
                    // fieldName: 'questions',
                    type: 'json',
                    default: [],
                    subtype: 'quiz',
                },
            },
            required: ['id', 'name'],
        },
        repository: {
            name: 'Repository',
            label: 'Repository',
            pluralTypeName: 'Repositories',
            description: 'A storage location for elearning content. Repositories contain published content that is available to customers. Each organization has one or more repositories.',
            type: 'entity',
            formConfig: [
                {
                    type: 'tabs',
                    elements: [
                        {
                            name: 'details',
                            type: 'group',
                            label: 'Details',
                            elements: [
                                'name',
                                'description',
                                'bucket',
                                'status',
                            ],
                        },
                        {
                            name: 'deployment',
                            type: 'group',
                            label: 'Deployment',
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                },
                description: {
                    label: 'Description',
                    type: 'string',
                    maxLength: 500,
                    default: '',
                },
                // cdnPath: {
                //   label: 'CDN Path',
                //   type: 'string',
                // },
                bucket: {
                    label: 'Bucket Name',
                    type: 'string',
                    // minLength: 6,
                    // maxLength: 40,
                    // regex: '^[a-z0-9-]+$',
                    readOnly: true,
                    default: '',
                    // regexFailMessage: 'Must only contain lower case letters, numbers and dashes (-)',
                },
                status: {
                    label: 'Status',
                    type: 'string',
                    default: 'new',
                    enum: ['new', 'online', 'offline', 'deploying', 'deleting', 'deleted'],
                    enumLabels: ['New', 'Online', 'Offline', 'Deploying', 'Deleting', 'Deleted'],
                    readOnly: true,
                },
                cloudfrontDistributionID: {
                    label: 'Cloudfront Distribution ID',
                    type: 'string',
                    default: '',
                    portalApi: false,
                    readOnly: true,
                    group: 'deployment',
                },
                cloudfrontDistributionDomain: {
                    label: 'Cloudfront Distribution Domain',
                    type: 'string',
                    default: '',
                    portalApi: true,
                    readOnly: true,
                    group: 'deployment',
                },
                cloudfrontOriginAccessIdentity: {
                    label: 'Cloudfront Origin Access Identity',
                    type: 'string',
                    default: '',
                    portalApi: false,
                    readOnly: true,
                    group: 'deployment',
                },
                deployedAt: {
                    label: 'Deployed Date',
                    type: 'timestamp',
                    default: new Date(),
                    portalApi: false,
                    readOnly: true,
                    group: 'deployment',
                },
                deployStatus: {
                    label: 'Deployment Status',
                    type: 'string',
                    default: 'pending',
                    enum: ['pending', 'inprogress', 'ready', 'error'],
                    enumLabels: ['Pending', 'In Progress', 'Ready', 'Error'],
                    portalApi: false,
                    readOnly: true,
                    group: 'deployment',
                },
                signingKeyID: {
                    label: 'Active Key To Use in Signing',
                    type: 'string',
                    portalApi: false,
                    group: 'deployment',
                    description: 'Needs to match the KeyPairId in the Cloudfront distribution. Private key is stored in secrets manager.',
                },
            },
            required: ['id', 'name'],
        },
        review: {
            name: 'review',
            label: 'Review',
            description: 'A review given on course/program by customer.',
            type: 'object',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                rating: {
                    label: 'Rating',
                    type: 'number',
                    default: 0,
                },
                text: {
                    label: 'Review',
                    type: 'string',
                    maxLength: 2000,
                },
                customer: {
                    label: 'Customer',
                    type: 'reference',
                    references: 'customer',
                    fieldName: 'customerID', // overrides the name in the backend datasource
                    // "readOnly": false // defaults to true
                },
            },
        },
        shopifyConfig: {
            name: 'Shopify Converse Config',
            label: 'Shopify Converse Config',
            description: 'A Shopify Converse Configuration connects a Shopify store to a Converse AI configuration.',
            type: 'entity',
            formConfig: [
                {
                    name: 'tabs',
                    type: 'tabs',
                    elements: [
                        {
                            name: 'general',
                            type: 'group',
                            label: 'General',
                            elements: [
                                'name',
                                'description',
                                'shop',
                                'storefrontDomain',
                                'status',
                                'clientAi',
                            ],
                        },
                        {
                            name: 'productFeed',
                            type: 'group',
                            label: 'Product Feed',
                            elements: [
                                'productFeedEnabled',
                                'indexCategories',
                                'ignoreInventory',
                                'ignoreOnlineStoreUrl',
                                'productFeedStatus',
                                'metafieldsConfig',
                                'additionalProductDataCSV',
                                'additionalProductDataConfig',
                                'titleTemplate',
                                'parentTitleTemplate',
                                'productActiveTemplate',
                                'productTemplate',
                                'customizableTag',
                            ],
                        },
                        {
                            name: 'productDescriptionOptimization',
                            type: 'group',
                            label: 'Product Description Optimization',
                            elements: [
                                'productDescriptionOptimizationInstructions',
                                'productDescriptionOptimizationMaxWords',
                            ],
                        },
                        {
                            name: 'billing',
                            type: 'group',
                            label: 'Billing',
                            elements: [
                                'billingPlanType',
                                'targetBillingTier',
                                'customBillingAmount',
                                'billingMessage',
                                'billingTier',
                                'merchantApprovedAmount',
                                'installationDate',
                                'subscriptionStart',
                                'freePeriodExpiry',
                                'lastPaymentDate',
                            ],
                        },
                        {
                            name: 'contact',
                            type: 'group',
                            label: 'Contact Information',
                            elements: [
                                'contactName',
                                'contactEmail',
                                'contactPhone',
                            ],
                        },
                        {
                            name: 'status',
                            type: 'group',
                            label: 'Status',
                            elements: [
                                {
                                    type: 'component',
                                    name: 'productFeedStatus',
                                },
                                {
                                    type: 'component',
                                    name: 'webPixelStatus',
                                },
                                {
                                    type: 'component',
                                    name: 'subscriptionStatus',
                                },
                                {
                                    type: 'component',
                                    name: 'grantedScopes',
                                },
                            ],
                        },
                    ],
                },
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 80,
                },
                description: {
                    label: 'Description',
                    type: 'string',
                    maxLength: 500,
                    default: '',
                },
                shop: {
                    label: 'Shop',
                    type: 'string',
                    readOnly: false,
                    default: 'example.myshopify.com',
                },
                storefrontDomain: {
                    label: 'Storefront Domain',
                    description: 'Public domain customers browse (e.g. https://www.example.com). '
                        + 'Captured from Shopify at install. Leave blank to use the myshopify.com '
                        + 'domain above. Set this only if published content is served here — a test '
                        + 'store should stay blank.',
                    type: 'string',
                    readOnly: false,
                    default: '',
                },
                status: {
                    label: 'Status',
                    type: 'string',
                    default: 'inactive',
                    enum: ['active', 'inactive'],
                    enumLabels: ['Active', 'Inactive'],
                    readOnly: true,
                },
                clientAi: {
                    label: 'Client AI Configuration',
                    type: 'reference',
                    references: 'clientAi',
                    fieldName: 'clientAiKey',
                },
                productFeedEnabled: {
                    label: 'Product Feed Enabled',
                    type: 'boolean',
                    default: true,
                },
                indexCategories: {
                    label: 'Index Categories',
                    description: 'Index categories/collections to the knowledge base.',
                    type: 'boolean',
                    default: false,
                },
                ignoreInventory: {
                    label: 'Ignore Inventory',
                    type: 'boolean',
                    default: false,
                },
                ignoreOnlineStoreUrl: {
                    label: 'Ignore Online Store URL',
                    description: 'onlineStoreUrl is the URL of the product on the Shopify store. This is null of the product is not published to the online store.',
                    type: 'boolean',
                    default: false,
                },
                productFeedStatus: {
                    label: 'Product Feed Status',
                    type: 'json',
                    default: {
                        status: 'notStarted',
                        message: '',
                        products: 0,
                        lastSync: null,
                    },
                    readOnly: true,
                },
                metafieldsConfig: {
                    label: 'Metafields Configuration',
                    description: 'Configuration for which product metafields to load from Shopify. Leave empty to skip metafields.',
                    type: 'json',
                    default: null,
                    example: {
                        namespaces: [
                            {
                                namespace: 'custom',
                                keys: ['brand', 'material', 'dimensions'],
                            },
                            {
                                namespace: 'global',
                                keys: ['seo_title', 'seo_description'],
                            },
                        ],
                    },
                },
                productTemplate: {
                    label: 'Product Template',
                    // eslint-disable-next-line no-template-curly-in-string
                    description: 'Custom template for generating product sheets. Uses ${VariableName} syntax for variable substitution. Leave empty to use default template.',
                    type: 'string',
                    default: null,
                    multiline: true,
                    rows: 15,
                    // eslint-disable-next-line no-template-curly-in-string
                    example: '# ${Title}\n\n${Description}\n\n## Product Details\n- **Type:** ${Type}\n- **Category:** ${ProductCategory}\n- **Price:** ${Price}\n- **Vendor:** ${Vendor}\n- **Inventory:** ${TotalInventory}\n\n## Collections\n${Collections}\n\n## Variants\n${Variants}\n\n## Metafields\n${Metafields}',
                },
                titleTemplate: {
                    label: 'Title Template',
                    description: 'Lodash template to generate the product title. Use <%= %> and the mapped Shopify fields like Title, Vendor, Type, Tags, etc.',
                    type: 'string',
                    default: null,
                    multiline: true,
                    rows: 3,
                    example: "<%= Vendor ? Vendor + ' - ' : '' %><%= Title %>",
                },
                parentTitleTemplate: {
                    label: 'Parent Title Template',
                    description: 'Lodash template to generate a parent/collection title grouping. Use <%= %> with fields like Type, Collections, Categories.',
                    type: 'string',
                    default: null,
                    multiline: true,
                    rows: 3,
                    example: "<%= Type ? Type + ' Collection' : 'Products' %>",
                },
                productActiveTemplate: {
                    label: 'Product Active Template',
                    description: 'Lodash template evaluated to determine if a product is active. Should return a boolean or a truthy/falsey string. Example: <%= Array.isArray(Tags) && Tags.includes(\'in-stock\') %>',
                    type: 'string',
                    default: null,
                    multiline: true,
                    rows: 2,
                },
                additionalProductDataCSV: {
                    label: 'Additional Product Data CSV',
                    description: 'CSV file with additional product data to merge with the product feed.',
                    type: 'reference',
                    references: 'asset',
                    shortKey: false,
                    default: null,
                },
                catalogTaxonomyAssetID: {
                    label: 'Catalog Taxonomy Spreadsheet',
                    description: 'CSV or Excel taxonomy file used by the AI Catalog Categorization Assistant.',
                    type: 'reference',
                    references: 'asset',
                    shortKey: false,
                    default: null,
                },
                productDescriptionOptimizationInstructions: {
                    label: 'Product description optimization (GEO) instructions',
                    description: 'Store-specific voice, style, and GEO guidance. Grounding, HTML safety, tenant checks, and the word cap remain enforced by the backend.',
                    type: 'string',
                    default: '',
                    multiline: true,
                    rows: 8,
                    maxLength: 4000,
                },
                productDescriptionOptimizationMaxWords: {
                    label: 'Maximum product description words',
                    description: 'Maximum visible words in a generated description. Allowed range: 1–500.',
                    type: 'number',
                    default: 250,
                    min: 1,
                    max: 500,
                    step: 1,
                },
                additionalProductDataConfig: {
                    label: 'Additional Product Data Config',
                    description: 'Configuration for mapping CSV columns to product data. Define keyColumn (e.g. Handle) and dataColumns with column path, dataName, and optional processor (none | splitToArray). Use splitToArray to split on commas and store as array of lowercased strings.',
                    type: 'json',
                    default: null,
                    example: {
                        keyColumn: 'Handle',
                        dataColumns: [
                            { column: 'custom_fields["bullet_text"]:string', dataName: 'bulletText', processor: 'none' },
                            { column: 'custom_fields["custom_shipping_message"]:string', dataName: 'customShippingMethod', processor: 'none' },
                        ],
                    },
                },
                billingPlanType: {
                    label: 'Billing Plan Type',
                    description: 'The type of billing plan',
                    type: 'string',
                    default: 'monthly',
                    enumLabels: ['Monthly', 'Free'],
                    enum: ['monthly', 'free'],
                },
                billingTier: {
                    label: 'Billing Tier',
                    description: 'The revenue tier for billing purposes',
                    type: 'string',
                    default: 'not_set',
                    enum: ['not_set', 'starter', 'accelerate', 'growth', 'enterprise'],
                    enumLabels: ['Not Set', 'Starter', 'Accelerate', 'Growth', 'Enterprise'],
                    readOnly: true,
                },
                targetBillingTier: {
                    label: 'Target Billing Tier',
                    description: 'The billing tier selected but not yet approved by the merchant',
                    type: 'string',
                    default: 'not_set',
                    enum: ['not_set', 'starter', 'accelerate', 'growth', 'enterprise'],
                    enumLabels: ['Not Set', 'Starter', 'Accelerate', 'Growth', 'Enterprise'],
                },
                merchantApprovedAmount: {
                    label: 'Merchant Approved Amount',
                    description: 'The amount approved by the merchant for billing. Should match the subscription amount from shopiy.',
                    type: 'number',
                    default: 0,
                    minimum: 0,
                    readOnly: true,
                },
                customBillingAmount: {
                    label: 'Custom Billing Amount',
                    description: 'Custom billing amount for Enterprise plan. When set, the Enterprise plan will show this price instead of Contact Us.',
                    type: 'number',
                    default: 0,
                    minimum: 0,
                },
                billingMessage: {
                    label: 'Billing Message',
                    description: 'The message to display to the merchant on the billing page',
                    type: 'string',
                    default: '',
                    multiline: true,
                    rows: 10,
                },
                installationDate: {
                    label: 'Installation Date',
                    description: 'When the app was first installed',
                    type: 'timestamp',
                },
                subscriptionStart: {
                    label: 'Subscription Start',
                    description: 'When the subscription started',
                    type: 'timestamp',
                    readOnly: true,
                },
                lastPaymentDate: {
                    label: 'Last Payment Date',
                    description: 'Date of the last payment',
                    type: 'timestamp',
                    readOnly: true,
                },
                freePeriodExpiry: {
                    label: 'Free Period Expiry',
                    description: 'When the free trial period expires',
                    type: 'timestamp',
                },
                contactName: {
                    label: 'Contact Name',
                    description: 'Contact name for the Shopify configuration',
                    type: 'string',
                    maxLength: 100,
                },
                contactEmail: {
                    label: 'Contact Email',
                    description: 'Contact email for the Shopify configuration',
                    type: 'string',
                    format: 'email',
                    maxLength: 255,
                },
                contactPhone: {
                    label: 'Contact Phone',
                    description: 'Contact phone for the Shopify configuration',
                    type: 'string',
                    maxLength: 50,
                },
                customizableTag: {
                    label: 'Customizable Product Tag',
                    description: 'Shopify tag used to identify products that support personalization (e.g., "customizable", "personalize")',
                    type: 'string',
                    maxLength: 50,
                    placeholder: 'customizable',
                },
            },
        },
        stylesheetStyle: {
            name: 'Portal Stylesheet Items',
            label: 'Portal Stylesheet Items',
            description: 'The stylesheet which defines the look and feel of the portal.',
            type: 'entity',
            properties: {},
        },
        user: {
            name: 'User',
            label: 'User',
            description: 'A user is someone that logs in to the administration system.  It does not include customers (end-users).',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                // "cognitoID": {
                //   "label": "Cognito ID",
                //   "type": "string",
                //   "readOnly": false // defaults to true
                // },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                email: {
                    label: 'Email',
                    type: 'string',
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 30,
                },
                biography: {
                    label: 'Biography',
                    type: 'string',
                    minLength: 3,
                    maxLength: 5000,
                    rows: 10,
                    keepLineBreaks: true,
                },
                profileImage: {
                    label: 'Profile Image',
                    type: 'reference',
                    references: 'asset',
                    assetType: 'image',
                    fieldName: 'profileImage', // overrides the name in the backend datasource
                },
                background: {
                    label: 'Background',
                    type: 'string',
                },
                headline: {
                    label: 'Headline',
                    type: 'string',
                },
                title: {
                    label: 'Title',
                    type: 'string',
                },
                enableAdminAgent: {
                    label: 'Enable admin assistant',
                    description: 'Show the admin assistant icon in the app bar for this user (still requires super user for the current organization).',
                    type: 'boolean',
                    default: false,
                },
                //   "organizationLinks": {
                //     "label": "Organizations",
                //     "type": "join",  // these is a contained array - items are stored directly
                //     "references": "UserOrganization",
                //     "fieldName": "Organizations",
                //  },
            },
            required: ['id'],
        },
        userOrganization: {
            name: 'UserOrganization',
            label: 'User Organization Link',
            description: 'The connection between Users and Organizations.',
            type: 'entity',
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                user: {
                    label: 'User',
                    type: 'reference',
                    references: 'user',
                    fieldName: 'userID', // overrides the name in the backend datasource
                    // "readOnly": false // defaults to true
                    readOnly: true,
                    primaryKey: true,
                    shortKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID', // overrides the name in the backend datasource
                    readOnly: true,
                    primaryKey: true,
                },
                name: {
                    label: 'Name',
                    type: 'string',
                    minLength: 3,
                    maxLength: 30,
                },
                createdAt: {
                    label: 'Created At Date',
                    type: 'timestamp',
                    readOnly: true,
                },
                updatedAt: {
                    label: 'Updated At Date',
                    type: 'timestamp',
                },
                superUser: {
                    label: 'Enables system level administration functions.',
                    type: 'boolean',
                    default: false,
                },
                mcpAccess: {
                    label: 'MCP access',
                    description: 'Lets this member use the MCP connector for the organization, limited by their roles.',
                    type: 'boolean',
                    default: false,
                },
                roles: {
                    label: 'User Role',
                    description: 'Defines the role of the user within the organization (e.g., Full Admin, AI Admin)',
                    type: 'options',
                    singleSelect: false,
                    enum: ['FULLADMIN', 'AIADMIN'],
                    enumLabels: ['Full Admin', 'AI Admin'],
                    default: ['FULLADMIN'],
                },
            },
            required: [],
        },
        product: {
            name: 'Product',
            label: 'Product',
            description: 'Product from e-commerce sync (Shopify, BigCommerce, etc.).',
            type: 'entity',
            sortBy: { field: 'name', direction: 'asc' },
            formConfig: [
                'handle',
                'name',
                'bodyHtml',
                'vendor',
                'productType',
                'optionTypes',
                'attributes',
                'imageSrc',
                'url',
                'tags',
                'status',
                'externalIDs',
                'productData',
                'createdAt',
                'updatedAt',
            ],
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                },
                syncConfigID: { label: 'Sync Config ID', type: 'string' },
                handle: { label: 'Handle', type: 'string', searchable: true },
                name: { label: 'Name', type: 'string', searchable: true },
                bodyHtml: { label: 'Body HTML', type: 'string' },
                vendor: { label: 'Vendor', type: 'string', searchable: true },
                productType: { label: 'Product Type', type: 'string', searchable: true },
                optionTypes: {
                    label: 'Option Types',
                    description: 'Variant option names from the feed (e.g. Size, Color).',
                    type: 'array',
                    references: 'string',
                    readOnly: true,
                },
                imageSrc: { label: 'Image Src', type: 'string', subtype: 'imageUrl' },
                url: { label: 'URL', type: 'string' },
                tags: {
                    label: 'Tags',
                    type: 'string',
                    subtype: 'chips',
                    placeholder: 'Add tags...',
                    searchable: true,
                },
                status: { label: 'Status', type: 'string', searchable: true },
                externalIDs: {
                    label: 'External IDs',
                    description: 'Normalized identifiers used for exact search (e.g. SKU, MPN). Populated by product sync; you can add or adjust values here.',
                    type: 'array',
                    references: 'string',
                },
                productData: { label: 'Product Data', type: 'json' },
                catalogCategorization: {
                    label: 'Last Published Catalog Categorization',
                    description: 'Latest versioned catalog categorization confirmed in Shopify.',
                    type: 'json',
                    portalApi: false,
                    itemLoad: false,
                    genericUpdate: false,
                },
                attributes: {
                    label: 'Attributes',
                    description: 'Custom fields from Shopify (metafields) and other backends; key-value map for filtering/display.',
                    type: 'json',
                },
                productSheet: { label: 'Product Sheet', description: 'Markdown content fed to the knowledge base indexer.', type: 'string' },
                variants: {
                    label: 'Variants',
                    description: 'Variant/SKU list for this product (from feed). Maintained on product; no separate Sku table.',
                    type: 'json',
                },
                relatedGuideIds: {
                    label: 'Related guides',
                    description: 'Ordered projection of the canonical Guide.suggestedProductHandles membership. Writes must use the membership contract.',
                    type: 'array',
                    references: 'string',
                    genericUpdate: false,
                },
                createdAt: { label: 'Created At', type: 'timestamp', readOnly: true },
                updatedAt: { label: 'Updated At', type: 'timestamp' },
            },
            required: ['id', 'organizationID'],
        },
        ecommerceCategory: {
            name: 'Ecommerce Category',
            label: 'Ecommerce Category',
            pluralLabel: 'Ecommerce Categories',
            typeName: 'Category',
            pluralTypeName: 'Categories',
            description: 'Category/Collection from e-commerce sync.',
            type: 'entity',
            sortBy: { field: 'name', direction: 'asc' },
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                },
                syncConfigID: { label: 'Sync Config ID', type: 'string' },
                handle: { label: 'Handle', type: 'string' },
                name: { label: 'Name', type: 'string' },
                descriptionHtml: { label: 'Description HTML', type: 'string' },
                imageUrl: { label: 'Image URL', type: 'string' },
                productCount: { label: 'Product Count', type: 'number' },
                relatedGuideIds: {
                    label: 'Related guides',
                    description: 'Guide IDs linked from this Category; Category.relatedGuideIds is the canonical relationship.',
                    type: 'array',
                    references: 'string',
                    // Category guide assignments must go through the shared preview/confirm
                    // transport (cap, eligibility, proposal hash and CAS). Do not let the
                    // legacy generic store.update path create a second writer.
                    genericUpdate: false,
                },
                categoryData: { label: 'Category Data', type: 'json' },
                createdAt: { label: 'Created At', type: 'timestamp', readOnly: true },
                updatedAt: { label: 'Updated At', type: 'timestamp' },
            },
            required: ['id', 'organizationID'],
        },
        webPage: {
            name: 'Web Page',
            label: 'Web Page',
            pluralLabel: 'Web Pages',
            typeName: 'WebPage',
            pluralTypeName: 'WebPages',
            description: 'Web page from Firecrawl crawl.',
            type: 'entity',
            sortBy: { field: 'name', direction: 'asc' },
            properties: {
                id: {
                    label: 'ID',
                    type: 'string',
                    readOnly: true,
                    primaryKey: true,
                },
                organization: {
                    label: 'Organization',
                    type: 'reference',
                    references: 'organization',
                    fieldName: 'organizationID',
                    readOnly: true,
                    primaryKey: true,
                },
                crawlID: { label: 'Crawl ID', type: 'string' },
                url: { label: 'URL', type: 'string' },
                name: { label: 'Name', type: 'string' },
                description: { label: 'Description', type: 'string' },
                pageMarkdown: { label: 'Page Markdown', description: 'Markdown content fed to the knowledge base.', type: 'string' },
                screenshotKey: { label: 'Screenshot Key', type: 'string' },
                pageData: { label: 'Page Data', type: 'json' },
                createdAt: { label: 'Created At', type: 'timestamp', readOnly: true },
                updatedAt: { label: 'Updated At', type: 'timestamp' },
            },
            required: ['id', 'organizationID'],
        },
    },
};
//# sourceMappingURL=microcasting.schema.js.map